import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { forkJoin, merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  take,
  takeUntil,
  tap
} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpService } from '@app/@core/services/http.service';
import { AlertService } from '@app/@core/services/alert.service';
import { environment } from '@env/environment';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';
import { SsnMaskDirective } from '@app/shared/directives/ssn-mask.directive';
import { ToggleMaskDirective } from '@app/shared/directives/toggle-mask.directive';
import { NumbersOnlyDirective } from '@app/shared/directives/numberOnly.directive';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO, FIXED_EST_TZ } from '@app/shared/custom-component/data-grid/data-grid.model';
import { comparePaymentBreakdownRows } from '@app/shared/utils/payment-breakdown-sort.util';
import {
  formatPaymentBreakdownAmount,
  formatPaymentBreakdownPrice
} from '@app/shared/utils/payment-breakdown-format.util';
import {
  ColumnConfig,
  GridAction,
  GridActionsComponent
} from '@app/shared/custom-component/grid-actions/grid-actions.component';
import {
  columnsForExcelExport,
  headersToColumnConfigs,
  mergeVisibleColumnDefaultsWithChooserLock,
  withColumnChooserLock
} from '../../lookup-grid-columns.util';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import { ModalConfig, modalButtonTitles, modalContent, modalHeading } from '@app/shared/constants/modal.model';
import { getCancelFileUploadModalConfig } from '@app/shared/constants/cancel-file-upload-modal.util';
import { StaffDocumentUploadNoticeModalService } from '@app/shared/services/staff-document-upload-notice-modal.service';
import { EducatorDuplicateDocumentGuardService } from '@app/shared/services/educator-duplicate-document-guard.service';
import {
  legalNameFirstLastPattern,
  legalNameMiddleOptionalPattern,
  patternMatch
} from '@app/shared/constants/validators.model';
import { NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ManageProfileService } from '@app/pages/manage-profile/manage-profile.service';
import { RegistrationService } from '@app/registration/registration.service';
import {
  EducatorDashboardAddressInfo,
  EducatorDashboardDataHttpResponse,
  EducatorDashboardDataRow,
  EducatorDashboardPersonInfo
} from '../../educator-dashboard-data.model';
import { EducatorDocumentPreviewModalService } from '../../educator-document-preview-modal.service';
import { resolveEducatorDocumentFileUrl } from '../../educator-document-download.util';
import { profileUploadDocumentsFromDashboardRow } from '../../educator-profile-documents.util';
import {
  ProfileDocumentLabelContext,
  resolveProfileDocumentCategoryLabel,
  resolveProfileDocumentTypeLabel,
  uniqueSystemACategoryKeysFromDocuments
} from '../../educator-profile-document-labels.util';
import {
  appendProfileUploadToDashboardRow,
  buildProfileUploadRowForLocalCache,
  toDuplicateLookupOptions
} from '../../educator-duplicate-document.util';
import {
  formatAlternatePhoneFromInput,
  fromAlternatePhoneDb,
  toAlternatePhoneDb
} from '../../lookup-user-alternate-phone.util';
import { CommonService } from '@app/shared/services/common.service';
import { ROLE_ID } from '@app/shared/constants/rolesConstant.model';
import { LocalService } from '@app/@core/services/local.service';
import {
  ApplicationOpenFromGridService,
  ApplicationOpenGridRow,
  lookupUserReturnTargets,
  normalizeNumericApplicationId
} from '@app/shared/services/application-open-from-grid.service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { ROMAN_SUFFIX_MAP } from '@app/shared/constants/registration-constants';
import { CommonNotificationService } from '@app/@core/services/common-notification.service';
import { sortCountryListWithUnitedStatesFirst } from './lookup-user-personal-countries.util';
import { educatorDocumentUploadWithClientBlobPut } from '../../educator-blob-upload.util';
import { LOOKUP_USER_SECTION_FORM_IDS } from '../../lookup-user-form-ids.constants';
import { SupportingDocumentRuleConfigService } from '@app/pages/page-designer/supporting-document-rule-config/supporting-document-rule-config.service';
import { SupportingDocumentGridOption } from '@app/pages/page-designer/supporting-document-grid/supporting-document-grid-config.model';

import * as FileSaver from 'file-saver';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { flipLookupUploadDropdownAbove } from '../../lookup-upload-dropdown.util';

/** Last dashboard/API mailing fields; restored when user unchecks "Mailing address same as above". */
interface ApiMailingAddressSnapshot {
  mailingCountry: string;
  mailingStreet: string;
  mailingLine2: string;
  mailingCity: string;
  mailingCounty: string;
  mailingState: string;
  mailingZip: string;
}

@Component({
  selector: 'lookup-user-personal-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialUIModule,
    SsnMaskDirective,
    ToggleMaskDirective,
    NumbersOnlyDirective,
    DataGridComponent,
    GridActionsComponent,
    DropdownModule
  ],
  templateUrl: './lookup-user-personal-tab.component.html',
  styleUrls: ['./lookup-user-personal-tab.component.scss']
})
export class LookupUserPersonalTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input() personId: string | null = null;
  /** Parent refetches dashboard so profile card and [row] stay in sync after name / prior name updates. */
  @Output() requestDashboardRefresh = new EventEmitter<void>();
  /** @deprecated Kept for parent wiring; Application ID now opens dynamic routing like My App. */
  @Output() openApplicationLicenseTab = new EventEmitter<{
    internalApplicationId: string;
    displayApplicationId: string;
  }>();
  @ViewChild('priorNamesGrid') priorNamesGridRef?: DataGridComponent;
  @ViewChild('transactionHistoryGrid') transactionHistoryGridRef?: DataGridComponent;
  @ViewChild('supportingDocumentsGrid') supportingDocumentsGridRef?: DataGridComponent;
  @ViewChild('identityPhotoSupportingDocumentsGrid') identityPhotoSupportingDocumentsGridRef?: DataGridComponent;
  @ViewChild('paymentBreakdownGrid') paymentBreakdownGridRef?: DataGridComponent;
  @ViewChild('staffUploadFilesTpl') staffUploadFilesTpl?: TemplateRef<unknown>;
  @ViewChild('editPriorNameModal') editPriorNameModalTpl?: TemplateRef<unknown>;

  readonly staffProfileFileAccept = '.pdf,.jpg,.jpeg,.png';
  private readonly staffProfileMaxBytes = 15 * 1024 * 1024;
  private readonly staffProfileAllowedExt = ['.pdf', '.jpg', '.jpeg', '.png'];
  staffProfileUploadFile: File | null = null;
  staffProfileUploadNotes = '';
  staffProfileUploadError = '';
  staffProfileUploading = false;
  staffProfileDropzoneActive = false;
  /** Upload modal: category label (display) and key (cd value) for the currently selected category. */
  nameChangeUploadCategoryLabel = '';
  nameChangeUploadCategoryKey = '';
  nameChangeUploadTypeOptions: SupportingDocumentGridOption[] = [];
  nameChangeUploadTypeKey = '';
  /** Categories available in the currently-open upload modal (varies by context). */
  activeUploadCategoryOptions: SupportingDocumentGridOption[] = [];
  /** True when section has a single category — dropdown pre-selected and disabled. */
  staffUploadCategoryDisabled = false;
  /** Initial category key set when modal opens (to detect user changes). */
  private initialUploadCategoryKey = '';
  /** Initial type key set when modal opens (to detect user changes). */
  private initialUploadTypeKey = '';
  /**
   * Shared staff upload modal: which section opened it (category/type init + `profile_tab_section` on save).
   */
  staffProfileUploadContext: 'nameChange' | 'identityPhoto' = 'nameChange';
  private allSupportingDocCategories: SupportingDocumentGridOption[] = [];
  private supportingDocTypesCache: Record<string, SupportingDocumentGridOption[]> = {};
  /** Preserve upload-selected type/category until dashboard refresh returns matching row. */
  private pendingUploadTypeOverrides = new Map<
    string,
    {
      cd_document_category: string;
      cd_document_type: string;
      document_category?: string;
      document_type?: string;
    }
  >();
  private staffUploadFilesModalRef: NgbModalRef | null = null;
  private editPriorNameModalRef: NgbModalRef | null = null;
  /** Form values when Edit Record modal opened (Reset restores these). */
  private priorNameEditFormSnapshot: {
    priorFirst: string;
    priorMiddle: string;
    priorLast: string;
    priorSuffix: string | null;
  } | null = null;

  /** When parent already loaded educator dashboard (profile card), pass row to avoid duplicate HTTP. */
  @Input() set row(value: EducatorDashboardDataRow | null) {
    this._rowInput = value;
    if (value) {
      this.educatorDashboardRow = value;
      this.mergePendingUploadTypesIntoRow(this.educatorDashboardRow);
      this.patchPersonalFormFromRow();
      this.patchContactFormFromRow();
      this.patchAddressFormFromRow();
      this.patchNameChangeFormFromRow();
      this.refreshDocumentGridsWithLabelPreload();
    }
  }
  private _rowInput: EducatorDashboardDataRow | null = null;

  educatorDashboardRow: EducatorDashboardDataRow | null = null;

  showIdentityPanel = false
  personalForm!: FormGroup;
  private personalFormSnapshot: Record<string, unknown> = {};

  ssnExists = false;
  itinExists = false;
  isSSNFocused = false;
  isITINFocused = false;
  originalSsn = '';
  /** ITIN digits as loaded from the server; skip duplicate API when unchanged after focus/blur. */
  originalItin = '';
  private destroy$ = new Subject<void>();

  showContactPanel = false
  showAddressPanel = false
  contactForm!: FormGroup;
  addressForm!: FormGroup;
  private contactFormSnapshot: Record<string, unknown> = {};
  private addressFormSnapshot: Record<string, unknown> = {};
  /** From dashboard p_address_info[0].profile_address_id for educator-update-address. */
  private currentProfileAddressId: number | null = null;
  /** Refreshed in patchAddressFormFromRow from API m_* (toggle off checkbox restores these values). */
  private apiMailingSnapshot: ApiMailingAddressSnapshot = {
    mailingCountry: '',
    mailingStreet: '',
    mailingLine2: '',
    mailingCity: '',
    mailingCounty: '',
    mailingState: '',
    mailingZip: ''
  };
  emailExists = false;
  recoveryEmailExists = false;
  phoneExists = false;
  alternatePhoneExists = false;

  /** Debounced triggers for duplicate checks (blur no longer used for these APIs). */
  private readonly ssnDupDebounced$ = new Subject<void>();
  private readonly itinDupDebounced$ = new Subject<void>();
  private readonly primaryPhoneDupDebounced$ = new Subject<void>();
  private readonly altPhoneDupDebounced$ = new Subject<void>();

  showNameChangePanel = false;
  /** Transaction History section collapsed by default (functional spec). */
  showTransactionHistoryPanel = false;
  showPaymentBreakdownPanel = false;
  nameChangeForm!: FormGroup;
  private nameChangeFormSnapshot: Record<string, unknown> = {};
  addPriorNameForm!: FormGroup;
  showAddPriorForm = false;
  /** Original row (suffix `s` is lookup key) for edit/update and duplicate checks. */
  editingPriorNameOriginal: { f: string; m: string; l: string; s: string } | null = null;
  showPriorNamesColumnChooser = false;
  /** Column visibility for Prior Names grid (column chooser). */
  priorNamesColumnVisibility: Record<string, boolean> = {
    first_name: true,
    middle_name: true,
    last_name: true,
    suffix: true,
    updated_by: true
  };
  suffixList: { key: string; value: string }[] = [];
  informationMessage: Record<string, string> = {};
  placeholderMessage: Record<string, string> = {};
  supportingDocumentsGridConfig: DataGridVO = new DataGridVO();
  identityPhotoSupportingDocumentsGridConfig: DataGridVO = new DataGridVO();
  priorNamesGridConfig: DataGridVO = new DataGridVO();
  transactionHistoryGridConfig: DataGridVO = new DataGridVO();
  paymentBreakdownGridConfig: DataGridVO = new DataGridVO();

  /** Transaction History toolbar */
  showTransactionHistoryColumnChooser = false;
  transactionHistoryColumnVisibility: Record<string, boolean> = {
    paymentTransactionId: true,
    transactionType: true,
    application_id: true,
    application_type: true,
    amount: true,
    transactionDate: true,
    transactionStatus: true
  };

  readonly lookupPersonalExportColumnChooserActions: GridAction[] = [
    { type: 'export', id: 'lookup_personal_export', title: 'Export', tooltip: 'Export' },
    {
      type: 'columnChooser',
      id: 'lookup_personal_column_chooser',
      title: 'Column Chooser',
      tooltip: 'Column Chooser'
    }
  ];

  showSupportingDocumentsColumnChooser = false;
  supportingDocumentsColumnVisibility: Record<string, boolean> = {};
  supportingDocumentsAllColumns: ColumnConfig[] = [];

  showIdentityPhotoSupportingDocumentsColumnChooser = false;
  identityPhotoSupportingDocumentsColumnVisibility: Record<string, boolean> = {};
  identityPhotoSupportingDocumentsAllColumns: ColumnConfig[] = [];

  showPaymentBreakdownColumnChooser = false;
  paymentBreakdownColumnVisibility: Record<string, boolean> = {};
  paymentBreakdownAllColumns: ColumnConfig[] = [];

  /** Application ID click: Supporting Documents, Transaction History, Payment Breakdown → same flow as My App grid. */
  readonly onLookupApplicationIdClick = (applicationInternalId: string, event?: Event): void => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const idStr =
      applicationInternalId != null && String(applicationInternalId).trim() !== ''
        ? String(applicationInternalId).trim()
        : '';
    if (!idStr) {
      this.alertService.warn('No application is linked to this row.');
      return;
    }
    const fromTxn = (this._lastTransactionGridRows || []).find(
      r => String(r.id ?? '').trim() === idStr
    );
    const fromDoc =
      (this._lastSupportingDocumentsRows || []).find(r => String(r.id ?? '').trim() === idStr) ||
      (this._lastIdentityPhotoSupportingRows || []).find(r => String(r.id ?? '').trim() === idStr);
    const fromPayment = (this._lastPaymentBreakdownRows || []).find(
      r => String(r.id ?? '').trim() === idStr
    );
    const gridRow = fromTxn || fromDoc || fromPayment;
    const selectedApp = this.buildSelectedAppForOpenFromLookup(idStr, gridRow);
    this.applicationOpenFromGrid.openApplicationLikeDataGrid(selectedApp, {
      returnTargets: lookupUserReturnTargets(this.personId),
      sourceTab: 'personal',
      sourcePersonId: this.personId ?? undefined
    });
  };

  showAddTransactionForm = false;
  /**
   * Add Transaction: REVENUE_CODE_DESC lookup (`optionaldata.amount`); dropdown filtered to keys present on
   * `p_payment_breakdown` as `revenue_code` or `revenue_code_desc` for the selected application (full list if no app).
   * Refund cap = Application Fee row in `p_transaction_dtls`; enforced on Amount only (not on revenue multiselect).
   */
  addTransactionForm!: FormGroup;
  /** Last built transaction rows (for Application ID click display resolution). */
  private _lastTransactionGridRows: any[] = [];
  /** Last built supporting document rows (internal `id` + display `application_id`). */
  private _lastSupportingDocumentsRows: any[] = [];
  private _lastIdentityPhotoSupportingRows: any[] = [];
  /** Last built Payment Breakdown rows (internal `id` + display `application_id`). */
  private _lastPaymentBreakdownRows: any[] = [];
  /** paymentTransactionTypeLookup: keys 3 / 4 for Add Transaction (labels from API). */
  addTransactionTypeLookupOptions: { key: string; value: string }[] = [];
  /** Refund / Credit keys in `application_payment_transaction.cd_transaction_type` (dashboard join uses these). */
  readonly addTxnCreditLookupKey = '3';
  readonly addTxnRefundLookupKey = '4';

  /** Full REVENUE_CODE_DESC lookup (loaded once). */
  private addTransactionRevenueLookupAll: { key: string; label: string; sumAmount: number }[] = [];
  /** Refund: `key` = `payment_history_id`. Credit: lookup key. `sumAmount` = refundable (refund) or lookup amount. */
  addTransactionRevenueOptions: { key: string; label: string; sumAmount: number; disabled?: boolean }[] = [];
  /** All applications from transactions (Credit uses full list). */
  private addTransactionApplicationOptionsAll: {
    key: string;
    displayId: string;
    internalId: number;
    typeCode: string;
  }[] = [];
  /** Filtered list shown in the Application dropdown (Refund = apps with Application Fee row only). */
  addTransactionApplicationOptions: {
    key: string;
    displayId: string;
    internalId: number;
    typeCode: string;
  }[] = [];
  /** Refund ceiling from `p_transaction_dtls` Application Fee row for the selected application. */
  private addTransactionApplicationFeeCap = 0;
  private addTransactionMaxAmount = 0;
  private readonly applicationFeeTypeNormalized = 'application fee';

  private readonly transactionHistoryExcelColumns = [
    { label: 'Transaction ID', key: 'paymentTransactionId', dataType: 'string' as const },
    { label: 'Transaction Type', key: 'transactionType', dataType: 'string' as const },
    { label: 'Application ID', key: 'application_id', dataType: 'string' as const },
    { label: 'Application Type', key: 'application_type', dataType: 'string' as const },
    { label: 'Amount', key: 'amount', dataType: 'string' as const },
    { label: 'Date', key: 'transactionDate', dataType: 'string' as const },
    { label: 'Status', key: 'transactionStatus', dataType: 'string' as const }
  ];

  private readonly searchHeader = (
    caption: string,
    dataField: string,
    options?: {
      minWidth?: number;
      allowFiltering?: boolean;
      fixed?: boolean;
      fixedPosition?: 'left' | 'right';
    }
  ) => ({
    dataField,
    caption,
    allowSearch: { isSearchEnable: true, type: 'string' as const },
    ...(options?.minWidth != null ? { minWidth: options.minWidth } : {}),
    ...(options?.allowFiltering !== undefined ? { allowFiltering: options.allowFiltering } : {}),
    ...(options?.fixed === true
      ? { fixed: true, fixedPosition: options.fixedPosition ?? 'left' }
      : {})
  });

  private readonly priorNamesExcelColumns = [
    { label: 'First Name', key: 'first_name', dataType: 'string' as const },
    { label: 'Middle Name', key: 'middle_name', dataType: 'string' as const },
    { label: 'Last Name', key: 'last_name', dataType: 'string' as const },
    { label: 'Suffix', key: 'suffix', dataType: 'string' as const },
    { label: 'Updated By', key: 'updated_by', dataType: 'string' as const }
  ];

  readonly priorNamesAllColumns: ColumnConfig[] = withColumnChooserLock(
    this.priorNamesExcelColumns.map(c => ({
      key: c.key,
      label: c.label
    }))
  );

  readonly transactionHistoryAllColumns: ColumnConfig[] = withColumnChooserLock(
    this.transactionHistoryExcelColumns.map(c => ({
      key: c.key,
      label: c.label
    }))
  );

  genderList: { key: string; value: string }[] = [];
  ethinicityList: { key: string; value: string }[] = [];
  raceList: { key: string; value: string }[] = [];
  countryList: { key: string; value: string }[] = [];

  errorMessage: Record<string, string> = {};
  toastMessage: Record<string, string> = {};

  currentDate: string = new Date().toISOString().split('T')[0];
  minAge = 14;
  maxAge = 120;

  constructor(
    private http: HttpClient,
    private httpService: HttpService,
    private fb: FormBuilder,
    private manageProfileService: ManageProfileService,
    private registrationService: RegistrationService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private staffDocNoticeModal: StaffDocumentUploadNoticeModalService,
    private duplicateDocGuard: EducatorDuplicateDocumentGuardService,
    private commonService: CommonService,
    private localService: LocalService,
    private applicationOpenFromGrid: ApplicationOpenFromGridService,
    private commonNotificationService: CommonNotificationService,
    private documentPreviewModal: EducatorDocumentPreviewModalService,
    private supportingDocLookupService: SupportingDocumentRuleConfigService
  ) {
    this.personalForm = this.fb.group({
      dateOfBirth: ['', [Validators.required, this.validateDate]],
      gender: [null as string | null, Validators.required],
      race: [null as string | null, Validators.required],
      ethnicity: [null as string | null, Validators.required],
      itin: ['', [this.validateITIN.bind(this)]],
      ssn: ['', [this.validateSSN.bind(this)]]
    });

    this.contactForm = this.fb.group({
      email: [
        '',
        [Validators.required, Validators.maxLength(255), Validators.pattern(patternMatch.registrationEmail)]
      ],
      recoveryEmail: [
        { value: '', disabled: true },
        [Validators.maxLength(255), Validators.pattern(patternMatch.registrationEmail)]
      ],
      phone: [{ value: '', disabled: true }, [Validators.required, this.validatePhone]],
      alternatePhone: ['', this.validatePhoneOptional]
    });

    this.addressForm = this.fb.group({
      primaryCountry: ['', Validators.required],
      primaryStreet: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9][A-Za-z0-9\s.,\-'/#]*$/), Validators.minLength(3), Validators.maxLength(60)]],
      primaryLine2: ['', this.optionalAddressLine2Validator.bind(this)],
      primaryCity: ['', this.cityStateRule6Validators()],
      primaryCounty: ['', Validators.required],
      primaryState: ['', this.cityStateRule6Validators()],
      primaryZip: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/), Validators.minLength(5), Validators.maxLength(10)]],
      mailingSameAsAbove: [false],
      mailingCountry: [''],
      mailingStreet: ['', [Validators.required,Validators.pattern(/^[A-Za-z0-9][A-Za-z0-9\s.,\-'/#]*$/), Validators.minLength(3), Validators.maxLength(60)]],
      mailingLine2: [''],
      mailingCity: ['',this.cityStateRule6Validators()],
      mailingCounty: ['',Validators.required],
      mailingState: ['',this.cityStateRule6Validators()],
      mailingZip: ['',[Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/), Validators.minLength(5), Validators.maxLength(10)]]
    });

    this.addressForm
      .get('mailingSameAsAbove')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(same => this.applyMailingSameAsAbove(!!same));

    const primaryMirrorKeys = [
      'primaryCountry',
      'primaryStreet',
      'primaryLine2',
      'primaryCity',
      'primaryCounty',
      'primaryState',
      'primaryZip'
    ] as const;
    const primaryStreams = primaryMirrorKeys
      .map(k => this.addressForm.get(k))
      .filter((c): c is NonNullable<typeof c> => c != null)
      .map(c => c.valueChanges);
    if (primaryStreams.length > 0) {
      merge(...primaryStreams)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.addressForm.get('mailingSameAsAbove')?.value) return;
          this.syncMailingFromPrimary();
          if (this.isMailingCountryUS) {
            const mz = this.normalizeUsZipDisplay(this.addressForm.get('mailingZip')?.value);
            this.addressForm.patchValue({ mailingZip: mz }, { emitEvent: false });
          }
          this.updateAddressValidatorsForCountry();
          this.applyAddressCountryGates();
        });
    }

    this.applyAddressCountryGates();

    this.nameChangeForm = this.fb.group({
      newLegalFirstName: [
        '',
        [
          Validators.required,
          Validators.pattern(legalNameFirstLastPattern),
          Validators.minLength(2),
          Validators.maxLength(30)
        ]
      ],
      newLegalMiddleName: ['', [Validators.pattern(legalNameMiddleOptionalPattern), Validators.maxLength(30)]],
      newLegalLastName: [
        '',
        [
          Validators.required,
          Validators.pattern(legalNameFirstLastPattern),
          Validators.minLength(2),
          Validators.maxLength(30)
        ]
      ],
      newLegalSuffix: [null as string | null]
    });
    this.addPriorNameForm = this.fb.group({
      priorFirst: [
        '',
        [
          Validators.required,
          Validators.pattern(legalNameFirstLastPattern),
          Validators.minLength(2),
          Validators.maxLength(30)
        ]
      ],
      priorMiddle: ['', [Validators.pattern(legalNameMiddleOptionalPattern), Validators.maxLength(30)]],
      priorLast: [
        '',
        [
          Validators.required,
          Validators.pattern(legalNameFirstLastPattern),
          Validators.minLength(2),
          Validators.maxLength(30)
        ]
      ],
      priorSuffix: [null as string | null]
    });
    this.addTransactionForm = this.fb.group({
      transactionType: [this.addTxnRefundLookupKey, Validators.required],
      applicationKey: [''],
      revenueKeys: [[] as string[], this.addTransactionRevenueKeysValidator.bind(this)],
      voucherNumber: ['', this.addTransactionVoucherNumberValidator.bind(this)],
      amount: ['', [Validators.required, this.addTransactionAmountValidator.bind(this)]]
    });
  }

  /**
   * Add Transaction visibility: NDE Administrator / Lead Analyst, plus role_id **2** when that is the session
   * role for your environment (often stored in session / LocalService as `role_id`).
   */
  get canAddManualTransaction(): boolean {
    const rid = this.resolveLoginRoleId();
    if (rid == null) return false;
    return (
      rid === 2 ||
      rid === ROLE_ID.NDE_Administrator ||
      rid === ROLE_ID.NDE_LeadAnalyst
    );
  }

  private resolveLoginRoleId(): number | null {
    const tryParse = (v: unknown): number | null => {
      if (v === null || v === undefined || v === '') return null;
      const n = Number(v);
      return Number.isNaN(n) ? null : n;
    };

    let r = tryParse(this.localService.getItem('role_id'));
    if (r != null) return r;

    if (typeof sessionStorage !== 'undefined') {
      r = tryParse(sessionStorage.getItem('role_id'));
      if (r != null) return r;
    }

    try {
      const state = this.commonService.getCurrentApplicationState();
      r = tryParse(state?.role_id);
      if (r != null) return r;
    } catch {
      /* ignore */
    }

    try {
      const userRaw = this.localService.getItem('user');
      const user =
        typeof userRaw === 'string' && userRaw.trim() !== ''
          ? JSON.parse(userRaw)
          : userRaw;
      if (user && typeof user === 'object') {
        const role =
          (user as any).role_id ??
          (user as any).roleId ??
          (user as any).role?.id ??
          (user as any).role?.role_id ??
          (user as any).userroles?.[0]?.id ??
          (user as any).user_role_id ??
          (user as any).userRoleId;
        r = tryParse(role);
        if (r != null) return r;
      }
    } catch {
      return null;
    }

    return null;
  }

  ngOnInit(): void {
    this.priorNamesGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: true,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false
    });
    this.supportingDocumentsGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: false,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: true
    });
    this.transactionHistoryGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: false,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false
    });
    this.paymentBreakdownGridConfig = DataGridVO.create({
      customHeaders: [],
      data: [],
      showActionsColumn: false,
      showSearchRow: true,
      showPaging: true,
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false
    });
    this.nameChangeFormSnapshot = this.nameChangeForm.getRawValue();
    this.nameChangeForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.nameChangeFormMatchesSnapshot()) {
        this.nameChangeForm.markAsPristine();
      }
    });
    this.loadDemographicLookups();
    this.loadAddTransactionTypeLookup();
    this.loadRevenueCodeDescLookup();
    this.supportingDocLookupService
      .getAllTopLevelCategories()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((all) => {
        this.allSupportingDocCategories = all;
        if (this.educatorDashboardRow) {
          this.refreshDocumentGridsWithLabelPreload();
        }
      });
    if (this._rowInput) {
      this.educatorDashboardRow = this._rowInput;
      this.patchPersonalFormFromRow();
      this.patchContactFormFromRow();
      this.patchAddressFormFromRow();
      this.patchNameChangeFormFromRow();
      this.refreshDocumentGridsWithLabelPreload();
    } else if (this.personId) {
      this.loadEducatorDashboardData();
    }
    this.personalForm.get('ssn')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.ssnExists = false;
    });
    this.personalForm.get('itin')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.itinExists = false;
      this.personalForm.get('ssn')?.updateValueAndValidity({ emitEvent: false });
    });
    this.contactForm
      .get('email')
      ?.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.runContactEmailDuplicateCheck();
        this.runContactRecoveryEmailDuplicateCheck();
      });
    this.contactForm
      .get('recoveryEmail')
      ?.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.runContactRecoveryEmailDuplicateCheck());

    this.ssnDupDebounced$.pipe(debounceTime(350), takeUntil(this.destroy$)).subscribe(() => {
      const digits = (this.personalForm.get('ssn')?.value ?? '').toString().replace(/\D/g, '');
      this.personalForm.get('ssn')?.updateValueAndValidity({ emitEvent: false });
      if (digits.length !== 9 || this.personalForm.get('ssn')?.invalid) {
        this.ssnExists = false;
        return;
      }
      this.checkSSNOrITIN('SSN');
    });
    this.itinDupDebounced$.pipe(debounceTime(350), takeUntil(this.destroy$)).subscribe(() => {
      const digits = (this.personalForm.get('itin')?.value ?? '').toString().replace(/\D/g, '');
      this.personalForm.get('itin')?.markAsTouched();
      this.personalForm.get('itin')?.updateValueAndValidity({ emitEvent: false });
      if (digits.length === 9 && this.isValidItinNineDigits(digits)) {
        this.checkSSNOrITIN('ITIN');
      } else {
        this.itinExists = false;
      }
    });
    this.primaryPhoneDupDebounced$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.runPrimaryPhoneDuplicateCheck();
    });
    this.altPhoneDupDebounced$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(() => {
      this.runAlternatePhoneDuplicateCheck();
    });
    this.addressForm.get('primaryCountry')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.clearPrimaryAddressFieldsExceptCountry();
      this.updateAddressValidatorsForCountry();
      this.applyPrimaryAddressCountryGate();
      if (this.addressForm.get('mailingSameAsAbove')?.value) {
        this.syncMailingFromPrimary();
      }
    });
    this.addressForm.get('mailingCountry')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.addressForm.get('mailingSameAsAbove')?.value) {
        this.clearMailingAddressFieldsExceptCountry();
      }
      this.updateAddressValidatorsForCountry();
      this.applyMailingAddressCountryGate();
    });

    this.addTransactionForm
      .get('transactionType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.addTransactionForm.get('amount')?.setValue('', { emitEvent: false });
        this.applyAddTransactionTypeValidators();
        this.applyAddTransactionApplicationOptionsFilter();
      });
    this.addTransactionForm
      .get('applicationKey')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.onAddTransactionApplicationChanged());
    this.addTransactionForm
      .get('revenueKeys')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.patchAddTransactionAmountDefault());
    this.applyAddTransactionTypeValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['personId'] && !changes['personId'].firstChange && !this._rowInput) {
      this.loadEducatorDashboardData();
    }
  }

  /**
   * SSN is required unless the ITIN field holds a valid 9-digit ITIN.
   * Partial SSN uses `pattern`; full 9-digit values use the same rules as before.
   */
  validateSSN(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    const digits = String(value).replace(/\D/g, '');
    const itinDigits = String(control.parent?.get('itin')?.value ?? '').replace(/\D/g, '');
    const hasValidItin = itinDigits.length === 9 && this.isValidItinNineDigits(itinDigits);

    if (digits.length === 0) {
      return hasValidItin ? null : { required: true };
    }
    if (digits.length !== 9) return { pattern: true };
    return this.validateSsnNineDigits(digits);
  }

  /** Nine-digit SSN format rules (invalid combinations). */
  private validateSsnNineDigits(digits: string): ValidationErrors | null {
    if (
      digits.substring(0, 3) === '000' ||
      digits.substring(3, 5) === '00' ||
      digits.substring(5, 9) === '0000'
    )
      return { pattern: true };
    if (/^(\d)\1{8}$/.test(digits)) return { pattern: true };
    const firstThree = parseInt(digits.substring(0, 3), 10);
    if (firstThree >= 900 && firstThree <= 999) return { pattern: true };
    if (digits.substring(0, 3) === '666') return { pattern: true };
    if (
      digits.substring(0, 6) === '987654' &&
      parseInt(digits.substring(6), 10) >= 320 &&
      parseInt(digits.substring(6), 10) <= 329
    )
      return { pattern: true };
    if (digits === '078051120') return { pattern: true };
    return null;
  }

  /** When false, SSN may be omitted if ITIN is a valid 9-digit ITIN (label asterisk / aria-required). */
  get ssnRequired(): boolean {
    const itinDigits = String(this.personalForm?.get('itin')?.value ?? '').replace(/\D/g, '');
    return !(itinDigits.length === 9 && this.isValidItinNineDigits(itinDigits));
  }

  /**
   * Exactly 9 digits; same ITIN rules as registration-form `validateSSNITIN` when first digit is 9.
   * Used by `validateITIN` and to gate duplicate API calls.
   */
  private isValidItinNineDigits(digits: string): boolean {
    if (digits.length !== 9) return false;
    if (
      digits.substring(0, 3) === '000' ||
      digits.substring(3, 5) === '00' ||
      digits.substring(5, 9) === '0000'
    ) {
      return false;
    }
    if (/^(\d)\1{8}$/.test(digits)) return false;
    if (digits.charAt(0) !== '9') return false;
    const middleDigits = parseInt(digits.substring(3, 5), 10);
    const validRanges =
      (middleDigits >= 50 && middleDigits <= 65) ||
      (middleDigits >= 70 && middleDigits <= 88) ||
      (middleDigits >= 90 && middleDigits <= 92) ||
      (middleDigits >= 94 && middleDigits <= 99);
    return validRanges;
  }

  /** Optional field: empty is valid. Non-empty requires a complete valid 9-digit ITIN (M0040 inline when not). */
  validateITIN(control: AbstractControl): ValidationErrors | null {
    const value = control.value || '';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 0) return null;
    if (digits.length === 9 && this.isValidItinNineDigits(digits)) return null;
    return { itinInvalid: true };
  }

  /** US phone: 10 or 11 digits (optional +1). Same as manage-profile update-contact. */
  validatePhone = (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value || '').toString().replace(/\D/g, '');
    if (value.length === 0) return null; // required is handled by Validators.required
    if (value.length === 10 || value.length === 11) return null;
    return { phoneInvalid: true };
  };

  validatePhoneOptional = (control: AbstractControl): ValidationErrors | null => {
    const db = toAlternatePhoneDb((control.value ?? '').toString());
    if (db === undefined && !(control.value ?? '').toString().trim()) return null;
    if (db !== undefined) return null;
    return { phoneInvalid: true };
  };

  validateDate = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    let selectedDate: Date;
    const val = control.value;
    if (typeof val !== 'string') return null;
    if (val.includes('/')) {
      const parts = val.split('/');
      if (parts.length !== 3) return { futureDate: true };
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return { futureDate: true };
      selectedDate = new Date(year, month - 1, day);
      if (selectedDate.getMonth() !== month - 1 || selectedDate.getDate() !== day || selectedDate.getFullYear() !== year) return { futureDate: true };
    } else {
      selectedDate = new Date(val);
      if (isNaN(selectedDate.getTime())) return { futureDate: true };
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    if (selectedDate > today) return { futureDate: true };
    let age = today.getFullYear() - selectedDate.getFullYear();
    const monthDiff = today.getMonth() - selectedDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < selectedDate.getDate())) age--;
    if (age < this.minAge) return { ageInvalid: true };
    if (age > this.maxAge) return { ageInvalid: true };
    return null;
  };

  private formatDobForDisplay(raw: string): string {
    if (!raw) return '';
    if (raw.includes('/')) return raw;
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[2]}/${match[3]}/${match[1]}`;
    return raw;
  }

  openDatePicker(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const dobInput = document.getElementById('lookup_dob');
    if (!dobInput) return;
    const rect = dobInput.getBoundingClientRect();
    const dateInput = document.createElement('input') as HTMLInputElement;
    dateInput.type = 'date';
    dateInput.style.position = 'absolute';
    dateInput.style.top = rect.top + window.scrollY + 5 + 'px';
    dateInput.style.left = rect.left + 'px';
    dateInput.style.width = '120px';
    dateInput.style.height = '35px';
    dateInput.style.opacity = '0.01';
    dateInput.style.border = 'none';
    dateInput.style.zIndex = '9999';
    dateInput.style.pointerEvents = 'auto';
    dateInput.max = this.currentDate;
    const currentValue = this.personalForm.get('dateOfBirth')?.value;
    if (currentValue && currentValue.includes('/')) {
      const parts = currentValue.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        dateInput.value = `${year}-${month}-${day}`;
      }
    }
    document.body.appendChild(dateInput);
    const cleanup = () => {
      if (document.body.contains(dateInput)) {
        dateInput.removeEventListener('change', changeHandler);
        dateInput.removeEventListener('blur', blurHandler);
        document.body.removeChild(dateInput);
      }
    };
    const changeHandler = (changeEvent: Event) => {
      const target = (changeEvent.target as HTMLInputElement);
      const selectedDate = target?.value;
      if (selectedDate) {
        const dateParts = selectedDate.split('-');
        if (dateParts.length === 3) {
          const formattedDate = `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`;
          this.personalForm.get('dateOfBirth')?.setValue(formattedDate);
          this.personalForm.get('dateOfBirth')?.markAsTouched();
          this.personalForm.get('dateOfBirth')?.updateValueAndValidity();
        }
      }
      cleanup();
    };
    const blurHandler = () => setTimeout(cleanup, 100);
    dateInput.addEventListener('change', changeHandler);
    dateInput.addEventListener('blur', blurHandler);
    dateInput.focus();
    const inputEl = dateInput as HTMLInputElement;
    if (typeof (inputEl as any).showPicker === 'function') {
      try {
        (inputEl as any).showPicker();
      } catch {
        inputEl.click();
      }
    } else {
      inputEl.click();
    }
  }

  formatDateInput(event: Event): void {
    const target = (event.target as HTMLInputElement);
    const value = (target?.value || '').replace(/\D/g, '');
    const limited = value.length > 8 ? value.substring(0, 8) : value;
    let formatted = limited;
    if (limited.length >= 5) formatted = `${limited.substring(0, 2)}/${limited.substring(2, 4)}/${limited.substring(4, 8)}`;
    else if (limited.length >= 3) formatted = `${limited.substring(0, 2)}/${limited.substring(2, 4)}`;
    else if (limited.length >= 1) formatted = limited.substring(0, 2);
    this.personalForm.get('dateOfBirth')?.setValue(formatted, { emitEvent: false });
  }

  private loadAddTransactionTypeLookup(): void {
    const fallback = [
      { key: this.addTxnCreditLookupKey, value: 'Credit' },
      { key: this.addTxnRefundLookupKey, value: 'Refund' }
    ];
    this.httpService
      .get('/domain/lookup?requestType=paymentTransactionTypeLookup')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const raw = Array.isArray(res?.data) ? res.data : [];
          const allowed = new Set([this.addTxnCreditLookupKey, this.addTxnRefundLookupKey]);
          let opts = raw
            .filter((x: any) => x?.key != null && allowed.has(String(x.key).trim()))
            .map((x: any) => ({ key: String(x.key).trim(), value: String(x.value ?? x.key).trim() }))
            .filter((x: { value: string }) => x.value.length > 0);
          if (opts.length === 0) opts = [...fallback];
          opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }));
          this.addTransactionTypeLookupOptions = opts;
        },
        error: () => {
          this.addTransactionTypeLookupOptions = [...fallback];
        }
      });
  }

  private loadRevenueCodeDescLookup(): void {
    this.httpService
      .get(API_ENDPOINTS.LOOKUP_REVENUE_CODE_DESC)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          const raw = Array.isArray(res?.data) ? res.data : [];
          this.addTransactionRevenueLookupAll = raw
            .filter((x: any) => x?.key != null && String(x.key).trim() !== '')
            .map((x: any) => {
              const key = String(x.key).trim();
              const label = this.formatRevenueMultiselectLabel(String(x.value ?? key));
              const sumAmount = this.parseLookupOptionalDataAmount(x.optionaldata);
              return { key, label, sumAmount };
            })
            .filter((o: { label: string }) => o.label.length > 0)
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
            );
          this.rebuildAddTransactionRevenueOptionsForCurrentApplication();
        },
        error: () => {
          this.addTransactionRevenueLookupAll = [];
          this.addTransactionRevenueOptions = [];
        }
      });
  }

  /**
   * @param feeLinesOnly when true, excludes credit utilisation rows (`cd_revenue_code` null) from the Figma / Credit matching path.
   */
  private filterPaymentBreakdownForApplication(applicationKey: string, feeLinesOnly = false): any[] {
    const row = this.educatorDashboardRow as any;
    const breakdown = Array.isArray(row?.p_payment_breakdown) ? row.p_payment_breakdown : [];
    const meta = applicationKey ? this.lookupApplicationMetaByKey(applicationKey) : null;
    const internalStr = meta != null ? String(meta.internalId) : '';
    const displayStr = meta?.displayId ? String(meta.displayId) : '';
    const keyTrim = String(applicationKey).trim();
    return breakdown.filter((p: any) => {
      if (!keyTrim) return false;
      const aid = p.application_id != null ? String(p.application_id).trim() : '';
      const anum = p.application_number != null ? String(p.application_number).trim() : '';
      let appMatch = false;
      if (internalStr && aid === internalStr) appMatch = true;
      else if (displayStr && (anum === displayStr || aid === displayStr)) appMatch = true;
      else if (aid === keyTrim) appMatch = true;
      if (!appMatch) return false;
      if (feeLinesOnly) {
        const cd = p.cd_revenue_code != null ? String(p.cd_revenue_code).trim() : '';
        if (cd === '') return false;
      }
      return true;
    });
  }

  /**
   * Payment Breakdown grid: only fee lines (lookup `cd_revenue_code`). Credit utilisation rows have
   * null/empty `cd_revenue_code` and must not appear even if legacy `revenue_code` is populated.
   */
  private paymentBreakdownRowHasFeeCode(p: any): boolean {
    const cd = p?.cd_revenue_code != null ? String(p.cd_revenue_code).trim() : '';
    const txType = p?.cd_revenue_code_transaction_type != null ? String(p.cd_revenue_code_transaction_type).trim() : '';
    // Show any row that has a revenue code OR is a recognised ORD/ORV transaction
    return cd !== '' || txType !== '';
  }

  /** Lookup 417 / fee model: `3` = Additional Endorsement (per product and refund alloc order). */
  private isAdditionalEndorsementPaymentRow(p: any): boolean {
    const cd = p?.cd_revenue_code != null ? String(p.cd_revenue_code).trim() : '';
    if (cd === '3') return true;
    const d = String(p?.revenue_code_desc ?? p?.revenue_code_description ?? '').toLowerCase();
    return d.includes('additional') && d.includes('endorsement');
  }

  private paymentBreakdownEndorsementGroupKey(p: any): string {
    const app = p?.application_id != null ? String(p.application_id).trim() : '';
    const inv = p?.invoice_number != null ? String(p.invoice_number).trim() : '';
    return `${app}\u0001${inv}`;
  }

  private parseBreakdownLineQuantity(p: any): number {
    const raw = p?.quantity;
    const s = raw != null && String(raw).trim() !== '' ? String(raw) : '1';
    const n = parseFloat(s.replace(/[,]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  private mergePaymentBreakdownAdditionalEndorsementGroup(rows: any[]): any {
    if (rows.length <= 1) return rows[0];
    let totalQty = 0;
    let totalPay = 0;
    let bestTs = -1;
    let bestTxn: any = null;
    for (const p of rows) {
      totalQty += this.parseBreakdownLineQuantity(p);
      const pay = this.parseMoneyToNumber(p?.payment_amount ?? p?.amount);
      if (Number.isFinite(pay)) {
        totalPay += pay;
      }
      const d = this.parseDashboardDate(p?.transaction_date ?? p?.transaction_date_time);
      const t = d ? d.getTime() : 0;
      if (t >= bestTs) {
        bestTs = t;
        bestTxn = p?.transaction_date ?? p?.transaction_date_time;
      }
    }
    const base = { ...rows[0] };
    base.quantity = String(totalQty);
    base.payment_amount = totalPay;
    if (totalQty > 0 && totalPay > 0) {
      base.price = totalPay / totalQty;
    }
    if (bestTxn != null) {
      base.transaction_date = bestTxn;
    }
    base._aggregatedAdditionalEndorsement = true;
    base._aggregatedLineCount = rows.length;
    return base;
  }

  /**
   * Only ORD Additional Endorsement charges are aggregated. Refund/credit ORV AE rows
   * stay as separate breakdown lines so multi-line refunds are not collapsed.
   */
  private shouldAggregateAdditionalEndorsementPaymentRow(p: any): boolean {
    if (!this.isAdditionalEndorsementPaymentRow(p)) return false;
    if (p?.is_refund === true || p?.is_credit === true) return false;
    const txType = p?.cd_revenue_code_transaction_type != null
      ? String(p.cd_revenue_code_transaction_type).trim()
      : '1';
    return txType === '1';
  }

  /**
   * Payment Breakdown grid: combine multiple Additional Endorsement line items (same app + invoice)
   * into one row with total quantity and summed payment (unit price = total / qty).
   */
  private aggregateAdditionalEndorsementPaymentBreakdownRows(paymentRows: any[]): any[] {
    if (!Array.isArray(paymentRows) || paymentRows.length === 0) {
      return paymentRows;
    }
    const buckets = new Map<string, any[]>();
    for (const p of paymentRows) {
      if (!this.shouldAggregateAdditionalEndorsementPaymentRow(p)) continue;
      const k = this.paymentBreakdownEndorsementGroupKey(p);
      const list = buckets.get(k) ?? [];
      list.push(p);
      buckets.set(k, list);
    }
    if (buckets.size === 0) {
      return paymentRows;
    }
    const emitted = new Set<string>();
    const out: any[] = [];
    for (const p of paymentRows) {
      if (!this.shouldAggregateAdditionalEndorsementPaymentRow(p)) {
        out.push(p);
        continue;
      }
      const k = this.paymentBreakdownEndorsementGroupKey(p);
      if (emitted.has(k)) {
        continue;
      }
      emitted.add(k);
      const group = buckets.get(k) ?? [p];
      out.push(this.mergePaymentBreakdownAdditionalEndorsementGroup(group));
    }
    return out;
  }

  /** Keys that appear as `revenue_code` or `revenue_code_desc` on payment breakdown for this app (match REVENUE_CODE_DESC lookup `key`). */
  private collectEligibleRevenueCodeKeysFromBreakdown(applicationKey: string): Set<string> {
    const set = new Set<string>();
    for (const p of this.filterPaymentBreakdownForApplication(applicationKey, true)) {
      const rc = String(p.revenue_code ?? '').trim();
      const rd = String(p.revenue_code_desc ?? '').trim();
      const code = rc || rd;
      if (code) set.add(code);
    }
    return set;
  }

  private rebuildAddTransactionRevenueOptionsForCurrentApplication(): void {
    const type = this.addTransactionForm?.get('transactionType')?.value;
    if (type === this.addTxnRefundLookupKey) {
      this.rebuildRefundRevenueOptionsFromBreakdown();
      return;
    }
    const all = this.addTransactionRevenueLookupAll;
    if (!all.length) {
      this.addTransactionRevenueOptions = [];
      this.trimRevenueKeysToAllowed();
      return;
    }
    const applicationKey = String(this.addTransactionForm?.get('applicationKey')?.value ?? '').trim();
    if (!applicationKey) {
      this.addTransactionRevenueOptions = [...all];
    } else {
      const eligible = this.collectEligibleRevenueCodeKeysFromBreakdown(applicationKey);
      this.addTransactionRevenueOptions =
        eligible.size === 0 ? [] : all.filter(o => eligible.has(String(o.key).trim()));
    }
    this.trimRevenueKeysToAllowed();
  }

  /**
   * Refund: one multiselect option per fee `payment_history` line; credit/Veteran/discount lines omitted or shown disabled per rules.
   */
  private rebuildRefundRevenueOptionsFromBreakdown(): void {
    const applicationKey = String(this.addTransactionForm?.get('applicationKey')?.value ?? '').trim();
    if (!applicationKey) {
      this.addTransactionRevenueOptions = [];
      this.trimRevenueKeysToAllowed();
      return;
    }
    const rows = this.filterPaymentBreakdownForApplication(applicationKey, false);
    if (!rows.length) {
      this.addTransactionRevenueOptions = [];
      this.trimRevenueKeysToAllowed();
      return;
    }
    const allocRows = rows.filter((p: any) => p.refund_pending !== true);
    // With the new payment model, paid_amount on ORD rows already reflects the
    // pro-rated net charge per line — use it directly as the refundable amount.
    const refundableByRow = new Map<number, number>();
    for (const p of allocRows) {
      const id = Number(p.payment_history_id);
      if (Number.isFinite(id)) {
        refundableByRow.set(id, Math.max(0, parseFloat(String(p.payment_amount ?? 0)) || 0));
      }
    }
    const opts: { key: string; label: string; sumAmount: number; disabled?: boolean }[] = [];
    for (const p of rows) {
      const idRaw = p.payment_history_id;
      if (idRaw == null || idRaw === '') continue;
      const phId = Number(idRaw);
      if (!Number.isFinite(phId)) continue;
      // Skip all ORV rows (type '2': exemptions, credits, refunds) — only ORD charges are refundable
      if (p.cd_revenue_code_transaction_type === '2') {
        continue;
      }
      if (p.refund_pending === true) {
        continue;
      }
      const ref = refundableByRow.get(phId) ?? 0;
      const rcd = String(p.revenue_code_desc ?? p.revenue_code_description ?? '').trim();
      const rcode = String(p.revenue_code ?? '').trim();
      const labelBase = rcd || rcode;
      const label = this.formatRevenueMultiselectLabel(labelBase) || `Line ${phId}`;
      const disabled = p.is_refund === true || ref <= 0;
      opts.push({ key: String(phId), label, sumAmount: ref, disabled });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    this.addTransactionRevenueOptions = opts;
    this.trimRevenueKeysToAllowed();
  }

  private trimRevenueKeysToAllowed(): void {
    const allowed = new Set(this.addTransactionRevenueOptions.filter(o => !o.disabled).map(o => o.key));
    const cur = (this.addTransactionForm?.get('revenueKeys')?.value as string[] | null) ?? [];
    const next = cur.filter(k => allowed.has(String(k).trim()));
    if (next.length !== cur.length) {
      this.addTransactionForm.patchValue({ revenueKeys: next }, { emitEvent: false });
    }
  }

  private findAddTransactionRevenueRowByKey(key: string):
    | { key: string; label: string; sumAmount: number; disabled?: boolean }
    | undefined {
    const k = String(key).trim();
    return this.addTransactionRevenueOptions.find(x => x.key === k);
  }

  /**
   * Revenue multiselect: lookup `value` and breakdown text can be multiline (description + `(code_key)` + `(amount)`).
   * Template appends `sumAmount` again — strip redundant parenthetical-only lines so options show "Label (nn.nn)" on one line.
   */
  private formatRevenueMultiselectLabel(raw: string): string {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) {
      return '';
    }
    const lines = trimmed
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 0);
    const kept = lines.filter(line => {
      const m = line.match(/^\(([^)]*)\)$/);
      if (!m) {
        return true;
      }
      const inner = m[1].trim();
      if (!inner) {
        return false;
      }
      if (/^[A-Za-z0-9_]+$/.test(inner)) {
        return false;
      }
      const n = inner.replace(/[$,\s]/g, '');
      if (/^[\d.]+$/.test(n) && n.length > 0) {
        return false;
      }
      return true;
    });
    const out = (kept.length > 0 ? kept.join(' ') : lines[0] ?? '').trim();
    return out || trimmed;
  }

  private parseLookupOptionalDataAmount(optionaldata: unknown): number {
    if (optionaldata == null || optionaldata === '') return 0;
    try {
      let obj: Record<string, unknown>;
      if (typeof optionaldata === 'string') {
        const s = optionaldata.trim();
        if (!s) return 0;
        obj = JSON.parse(s) as Record<string, unknown>;
      } else if (typeof optionaldata === 'object' && !Array.isArray(optionaldata)) {
        obj = optionaldata as Record<string, unknown>;
      } else {
        return 0;
      }
      const amt = obj['amount'];
      const n = parseFloat(String(amt ?? '0').replace(/[$,\s]/g, ''));
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  }

  private isApplicationFeeTransactionType(transactionType: unknown): boolean {
    const s = String(transactionType ?? '').trim().toLowerCase();
    return s === this.applicationFeeTypeNormalized || s.includes('application fee');
  }

  private findApplicationFeeTransactionForInternalId(internalId: number): any | null {
    const row = this.educatorDashboardRow as any;
    const transactions = Array.isArray(row?.p_transaction_dtls) ? row.p_transaction_dtls : [];
    for (const t of transactions) {
      const aid =
        t.application_id != null && String(t.application_id).trim() !== ''
          ? Number(t.application_id)
          : NaN;
      if (!Number.isFinite(aid) || aid !== internalId) continue;
      if (this.isApplicationFeeTransactionType(t.transaction_type)) return t;
    }
    return null;
  }

  private applicationHasApplicationFee(internalId: number): boolean {
    return this.findApplicationFeeTransactionForInternalId(internalId) != null;
  }

  private getApplicationFeeAmountForInternalId(internalId: number): number {
    const t = this.findApplicationFeeTransactionForInternalId(internalId);
    if (!t) return 0;
    const amt = t.amount;
    const n = typeof amt === 'number' ? amt : this.parseMoneyToNumber(amt);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  private updateAddTransactionApplicationFeeCap(applicationKey: string): void {
    if (!applicationKey) {
      this.addTransactionApplicationFeeCap = 0;
      return;
    }
    const meta = this.lookupApplicationMetaByKey(applicationKey);
    if (!meta?.internalId) {
      this.addTransactionApplicationFeeCap = 0;
      return;
    }
    this.addTransactionApplicationFeeCap = this.getApplicationFeeAmountForInternalId(meta.internalId);
  }

  private applyAddTransactionApplicationOptionsFilter(): void {
    const type = this.addTransactionForm?.get('transactionType')?.value;
    if (type === this.addTxnRefundLookupKey) {
      this.addTransactionApplicationOptions = this.addTransactionApplicationOptionsAll.filter(o =>
        this.applicationHasApplicationFee(o.internalId)
      );
    } else {
      this.addTransactionApplicationOptions = [...this.addTransactionApplicationOptionsAll];
    }
    const currentKey = String(this.addTransactionForm?.get('applicationKey')?.value ?? '').trim();
    if (currentKey && !this.addTransactionApplicationOptions.some(o => o.key === currentKey)) {
      this.addTransactionForm.patchValue({ applicationKey: '' }, { emitEvent: true });
      return;
    }
    this.updateAddTransactionApplicationFeeCap(currentKey);
    this.rebuildAddTransactionRevenueOptionsForCurrentApplication();
    this.patchAddTransactionAmountDefault();
    this.addTransactionForm.get('amount')?.updateValueAndValidity({ emitEvent: false });
    this.addTransactionForm.get('revenueKeys')?.updateValueAndValidity({ emitEvent: false });
  }

  private formatTransactionTypeForGrid(t: any): string {
    const resolved = String(t?.transaction_type ?? '').trim();
    if (resolved) return resolved;
    const code = String(t?.cd_transaction_type ?? t?.cdTransactionType ?? '').trim();
    if (!code) return '';
    const upper = code.toUpperCase();
    if (upper === 'REFUND') return 'Refund';
    if (upper === 'CREDIT') return 'Credit';
    if (upper === '3') return 'Credit';
    if (upper === '4') return 'Refund';
    return code.length > 1
      ? code.charAt(0) + code.slice(1).toLowerCase()
      : code;
  }

  /**
   * SUFFIX lookup rows are ordered for the name-change UI to match registration / spec:
   * Jr., Sr., II, III, IV, V, None — then any other API-only values before None if present.
   */
  private orderLegalSuffixList(rows: { key: string; value: string }[]): { key: string; value: string }[] {
    const rank = (raw: string): number => {
      const v = String(raw ?? '')
        .trim()
        .toLowerCase()
        .replace(/\.$/, '');
      const known: Record<string, number> = {
        jr: 0,
        sr: 1,
        ii: 2,
        iii: 3,
        iv: 4,
        v: 5,
        none: 100
      };
      if (known[v] !== undefined) {
        return known[v];
      }
      return 50;
    };
    return [...(rows || [])].sort((a, b) => {
      const ra = rank(a.value);
      const rb = rank(b.value);
      if (ra !== rb) {
        return ra - rb;
      }
      return (a.value || '').localeCompare(b.value || '');
    });
  }



  private loadDemographicLookups(): void {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=GENDER_TYPES'),
      this.httpService.get('/domain/lookup?requestType=ETHICITY_TYPES'),
      this.httpService.get('/domain/lookup?requestType=RACE_CODES'),
      this.httpService.get('/domain/lookup?requestType=COUNTRY'),
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=SUFFIX'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES')
    ]).subscribe({
      next: (item: any) => {
        this.genderList = (item[0].data || []).sort((a: any, b: any) =>
          a.value.localeCompare(b.value)
        );
        this.ethinicityList = (item[1].data || []).sort((a: any, b: any) =>
          a.value.localeCompare(b.value)
        );
        this.raceList = [...(item[2].data || [])];
        const preferNotToAnswerIndex = this.raceList.findIndex((r: { value?: string }) => {
          const v = r.value ?? '';
          return (
            v === 'Prefer Not to Answer' ||
            v === 'Prefer not to answer' ||
            v.toLowerCase().includes('prefer not to answer')
          );
        });
        if (preferNotToAnswerIndex !== -1) {
          const preferNotToAnswerItem = this.raceList.splice(preferNotToAnswerIndex, 1)[0];
          this.raceList.sort((a: any, b: any) => (a.value || '').localeCompare(b.value || ''));
          this.raceList.push(preferNotToAnswerItem);
        } else {
          this.raceList.sort((a: any, b: any) => (a.value || '').localeCompare(b.value || ''));
        }
        this.countryList = sortCountryListWithUnitedStatesFirst(item[3].data || []);
        this.errorMessage = {};
        (item[4]?.data || []).forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.errorMessage[msg.key] = msg.value;
        });
        this.toastMessage = {};
        (item[5]?.data || []).forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.toastMessage[msg.key] = msg.value;
        });
        this.suffixList = this.orderLegalSuffixList(item[6]?.data || []);
        this.informationMessage = {};
        (item[7]?.data || []).forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.informationMessage[msg.key] = msg.value;
        });
        this.placeholderMessage = {};
        (item[8]?.data || []).forEach((msg: { key?: string; value?: string }) => {
          if (msg.key != null && msg.value !== undefined) this.placeholderMessage[msg.key] = msg.value;
        });
        if (this.educatorDashboardRow) {
          this.patchPersonalFormFromRow();
          this.patchContactFormFromRow();
          this.patchAddressFormFromRow();
          this.patchNameChangeFormFromRow();
        } else if (this.countryList.length > 0) {
          this.addressForm.patchValue(
            {
              primaryCountry: this.countryList[0].key,
              mailingCountry: this.countryList[0].key
            },
            { emitEvent: false }
          );
          this.updateAddressValidatorsForCountry();
          this.applyAddressCountryGates();
        }
      },
      error: () => {
        if (this.educatorDashboardRow) {
          this.patchPersonalFormFromRow();
          this.patchContactFormFromRow();
          this.patchAddressFormFromRow();
          this.patchNameChangeFormFromRow();
        }
      }
    });
  }

  private loadEducatorDashboardData(): void {
    const personId = this.personId;
    if (!personId) {
      this.educatorDashboardRow = null;
      return;
    }
    const payload = { personId: Number(personId) };
    const url = this.buildEducatorDashboardUrl();
    const headers = this.buildJsonHeaders();
    this.http
      .post<EducatorDashboardDataHttpResponse>(url, payload, { headers })
      .pipe(take(1))
      .subscribe({
        next: response => {
          const payloadInner = response?.data;
          if (
            response?.status === 'success' &&
            payloadInner?.success &&
            Array.isArray(payloadInner.data) &&
            payloadInner.data.length > 0
          ) {
            this.educatorDashboardRow = payloadInner.data[0];
            this.mergePendingUploadTypesIntoRow(this.educatorDashboardRow);
            this.patchPersonalFormFromRow();
            this.patchContactFormFromRow();
            this.patchAddressFormFromRow();
            this.patchNameChangeFormFromRow();
            this.refreshDocumentGridsWithLabelPreload();
          } else {
            this.educatorDashboardRow = null;
          }
        },
        error: err => console.error('[LookupUserPersonalTab] educator-dashboard-data error:', err)
      });
  }

  private buildEducatorDashboardUrl(): string {
    return this.buildSearchUrl('/search/educator-dashboard-data');
  }

  private getCurrentUserId(): number | null {
    try {
      const userRaw = sessionStorage.getItem('user');
      if (!userRaw) return null;
      const user = JSON.parse(userRaw);
      const id = user?.user_id ?? user?.id ?? user?.userId ?? null;
      return id !== null && id !== undefined && String(id).trim() !== '' ? Number(id) : null;
    } catch {
      return null;
    }
  }

  /** Body for POST /search/educator-update-name-change (person, pf_prior_names, pf_name_history, pics_core sync). */
  private buildEducatorNameChangeRequestBody(
    personId: number,
    raw: Record<string, string | null>
  ): Record<string, unknown> {
    const row = this.educatorDashboardRow as Record<string, unknown> | null;
    const auditUserId = this.getCurrentUserId();

    let applicationId: number | undefined;
    const apps = row && Array.isArray(row['p_application']) ? (row['p_application'] as unknown[]) : [];
    if (apps.length) {
      const ids = apps
        .map((a: unknown) => Number((a as Record<string, unknown>)?.['application_id']))
        .filter((n: number) => Number.isFinite(n));
      if (ids.length) {
        applicationId = Math.max(...ids);
      }
    }

    const picsRaw =
      row?.['pics_user_id'] ??
      row?.['user_id'] ??
      row?.['picsUserId'] ??
      (row?.['p_profile'] as Record<string, unknown> | undefined)?.['user_id'] ??
      (row?.['p_profile'] as Record<string, unknown> | undefined)?.['pics_user_id'];
    const picsUserId =
      picsRaw != null && String(picsRaw).trim() !== '' && Number.isFinite(Number(picsRaw))
        ? Number(picsRaw)
        : undefined;

    const body: Record<string, unknown> = {
      personId,
      newLegalFirstName: raw['newLegalFirstName'] ?? '',
      newLegalMiddleName: raw['newLegalMiddleName'] ?? '',
      newLegalLastName: raw['newLegalLastName'] ?? '',
      newLegalSuffix: this.suffixFormToString(raw['newLegalSuffix'])
    };
    if (applicationId != null) {
      body['applicationId'] = applicationId;
    }
    if (picsUserId != null) {
      body['picsUserId'] = picsUserId;
    }
    if (auditUserId != null) {
      body['auditUserId'] = auditUserId;
    }
    return body;
  }

  /** Loads category options for Identity Photo section and resets selection state. */
  initIdentityPhotoStaffUploadDocMeta(): void {
    this.nameChangeUploadCategoryLabel = '';
    this.nameChangeUploadCategoryKey = '';
    this.nameChangeUploadTypeOptions = [];
    this.nameChangeUploadTypeKey = '';
    this.activeUploadCategoryOptions = [];
    this.supportingDocLookupService
      .getCategoriesBySectionName('Identity and Contact Information')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((cats: SupportingDocumentGridOption[]) => {
        this.activeUploadCategoryOptions = cats;
        this.applySingleCategoryPreselect(cats);
      });
  }

  /** Loads category options for Name History section and resets selection state. */
  initNameChangeStaffUploadDocMeta(): void {
    this.nameChangeUploadCategoryLabel = '';
    this.nameChangeUploadCategoryKey = '';
    this.nameChangeUploadTypeOptions = [];
    this.nameChangeUploadTypeKey = '';
    this.activeUploadCategoryOptions = [];
    this.supportingDocLookupService
      .getCategoriesBySectionName('Name History')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((cats: SupportingDocumentGridOption[]) => {
        this.activeUploadCategoryOptions = cats;
        this.applySingleCategoryPreselect(cats);
      });
  }

  /** Pre-select and lock category using the first lookup option for this upload section. */
  private applySingleCategoryPreselect(cats: SupportingDocumentGridOption[]): void {
    if (cats.length > 0) {
      this.onUploadCategorySelect(cats[0].key);
      this.staffUploadCategoryDisabled = true;
      this.initialUploadCategoryKey = cats[0].key;
    } else {
      this.staffUploadCategoryDisabled = false;
    }
  }

  /** Called when user selects a category in the upload modal; loads matching document types. */
  onUploadCategorySelect(categoryKey: string): void {
    const selected = this.activeUploadCategoryOptions.find(c => c.key === categoryKey);
    if (!selected) {
      return;
    }
    this.nameChangeUploadCategoryKey = selected.key;
    this.nameChangeUploadCategoryLabel = selected.value;
    this.nameChangeUploadTypeKey = '';
    this.nameChangeUploadTypeOptions = [];
    this.supportingDocLookupService
      .getTypesByCategory(selected)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((types: SupportingDocumentGridOption[]) => {
        this.nameChangeUploadTypeOptions = types;
        this.supportingDocTypesCache[selected.key] = types;
      });
  }

  private buildSearchUrl(path: string): string {
    if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
      const localApi = 'http://localhost:4050/api';
      const stored = sessionStorage.getItem('apiHost');
      if (stored && stored.includes('localhost')) {
        return stored.replace(/\/$/, '') + path;
      }
      return localApi + path;
    }
    const base = (environment as any).apiHost || '';
    return (base.endsWith('/') ? base.slice(0, -1) : base) + path;
  }

  private buildJsonHeaders(): HttpHeaders {
    let h = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json');
    const token = this.localService.getItem('jwt-token');
    if (token != null && String(token).trim() !== '') {
      h = h.set('Authorization', `Bearer ${String(token)}`);
    }
    return h;
  }

  toggleIdentityPanel(): void {
    this.showIdentityPanel = !this.showIdentityPanel;
  }

  toggleContactPanel(): void {
    this.showContactPanel = !this.showContactPanel;
  }

  toggleAddressPanel(): void {
    this.showAddressPanel = !this.showAddressPanel;
  }

  toggleNameChangePanel(): void {
    this.showNameChangePanel = !this.showNameChangePanel;
    if (this.showNameChangePanel) {
      this.patchNameChangeFormFromRow();
    }
  }

  toggleTransactionHistoryPanel(): void {
    this.showTransactionHistoryPanel = !this.showTransactionHistoryPanel;
  }

  togglePaymentBreakdownPanel(): void {
    this.showPaymentBreakdownPanel = !this.showPaymentBreakdownPanel;
  }

  onNameChangeReset(): void {
    this.nameChangeForm.patchValue(this.nameChangeFormSnapshot);
    this.nameChangeForm.markAsPristine();
  }

  /** Case-sensitive quad match vs last loaded snapshot (legal name change detection). */
  private nameChangeFormMatchesSnapshot(): boolean {
    const raw = this.nameChangeForm.getRawValue() as Record<string, string | null>;
    return this.namesFourEqualExact(this.quadFromNameChangeFormRaw(raw), this.quadFromNameChangeSnapshot());
  }

  private quadFromNameChangeSnapshot(
    snap: Record<string, unknown> = this.nameChangeFormSnapshot
  ): { f: string; m: string; l: string; s: string } {
    return {
      f: String(snap['newLegalFirstName'] ?? ''),
      m: String(snap['newLegalMiddleName'] ?? ''),
      l: String(snap['newLegalLastName'] ?? ''),
      s: this.suffixFormToString(snap['newLegalSuffix'])
    };
  }

  private quadFromNameChangeFormRaw(
    raw: Record<string, string | null>
  ): { f: string; m: string; l: string; s: string } {
    return {
      f: raw['newLegalFirstName'] ?? '',
      m: raw['newLegalMiddleName'] ?? '',
      l: raw['newLegalLastName'] ?? '',
      s: this.suffixFormToString(raw['newLegalSuffix'])
    };
  }

  private priorNameItemFromQuad(quad: { f: string; m: string; l: string; s: string }): {
    priorFirstName: string;
    priorMiddleName: string;
    priorLastName: string;
    suffix: string;
  } {
    return {
      priorFirstName: quad.f,
      priorMiddleName: quad.m,
      priorLastName: quad.l,
      suffix: quad.s
    };
  }

  private shouldPromptAddPriorNameOnLegalChange(): boolean {
    return !this.isPriorNameDuplicateInTable(this.quadFromNameChangeSnapshot(), null);
  }

  private submitEducatorNameChange(body: Record<string, unknown>): void {
    const url = this.buildSearchUrl('/search/educator-update-name-change');
    this.http
      .post<{ status?: string; data?: { success?: boolean; message?: string }; message?: string }>(url, body, {
        headers: this.buildJsonHeaders()
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const success = res?.status === 'success' || res?.data?.success;
          if (success) {
            this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
            this.nameChangeFormSnapshot = this.nameChangeForm.getRawValue();
            this.nameChangeForm.markAsPristine();
            this.requestDashboardRefresh.emit();
            if (!this._rowInput) {
              this.loadEducatorDashboardData();
            }
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Update failed.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to update name.');
        }
      });
  }

  private openAddPriorNameOnNameChangeModal(onResult: (addCurrentAsPrior: boolean) => void): void {
    const modalOpts: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'md',
      windowClass: 'lookup-add-prior-name-modal'
    };
    const modalRef = this.modalService.open(ModalComponent, modalOpts);
    const config: ModalConfig = {
      heading: 'Add Prior Name',
      headingAlign: 'left',
      modalContent:
        'A new legal name has been entered. Would you like to add the current name as a prior name?',
      hasTemplate: true,
      buttons: [
        { text: 'No', code: 'NP', btnOutline: true },
        { text: 'Yes, Add Prior Name', code: 'Y' }
      ]
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: { code?: string; confirmed?: boolean }) => {
      modalRef.close();
      if (response?.code === 'N') {
        return; // Cross/dismiss -> cancel the name-change transaction (do not submit)
      }
      onResult(response?.code === 'Y' || response?.confirmed === true);
    });
  }

  onNameChangeUpdate(): void {
    if (this.nameChangeForm.invalid) {
      this.nameChangeForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    const raw = this.nameChangeForm.getRawValue() as Record<string, string | null>;
    const personId = this.personId;
    if (!personId) {
      this.alertService.error('Person ID is required.');
      return;
    }
    const body = this.buildEducatorNameChangeRequestBody(Number(personId), raw);
    const nameChanged = !this.nameChangeFormMatchesSnapshot();

    if (nameChanged && this.shouldPromptAddPriorNameOnLegalChange()) {
      const currentQuad = this.quadFromNameChangeSnapshot();
      this.openAddPriorNameOnNameChangeModal(addCurrentAsPrior => {
        const requestBody = { ...body };
        if (addCurrentAsPrior) {
          requestBody['priorNames'] = [this.priorNameItemFromQuad(currentQuad)];
        }
        this.submitEducatorNameChange(requestBody);
      });
      return;
    }

    this.submitEducatorNameChange(body);
  }

  onAddPriorName(): void {
    this.editingPriorNameOriginal = null;
    this.showAddPriorForm = true;
    this.showPriorNamesColumnChooser = false;
    this.addPriorNameForm.reset({
      priorFirst: '',
      priorMiddle: '',
      priorLast: '',
      priorSuffix: null
    });
    this.addPriorNameForm.markAsPristine();
    this.addPriorNameForm.markAsUntouched();
  }

  onCancelAddPriorName(): void {
    this.showAddPriorForm = false;
    this.editingPriorNameOriginal = null;
    this.addPriorNameForm.reset();
    this.addPriorNameForm.markAsPristine();
  }

  /** Edit Prior Name template — matches Activities OOS test edit modal chrome. */
  private priorNameEditModalOptions(): NgbModalOptions {
    return { backdrop: 'static', centered: true, size: 'xl', windowClass: 'activities-doc-modal' };
  }

  onEditPriorNameRow(row: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    suffix?: string;
    cd_suffix?: string;
  }): void {
    const key =
      row?.cd_suffix != null && String(row.cd_suffix).trim() !== ''
        ? String(row.cd_suffix).trim()
        : this.resolveSuffixKeyFromDisplay(String(row?.suffix ?? ''));
    this.editingPriorNameOriginal = {
      f: String(row?.first_name ?? '').trim(),
      m: String(row?.middle_name ?? '').trim(),
      l: String(row?.last_name ?? '').trim(),
      s: this.suffixFormToString(key)
    };
    this.showPriorNamesColumnChooser = false;
    this.addPriorNameForm.reset();
    this.addPriorNameForm.patchValue({
      priorFirst: this.editingPriorNameOriginal.f,
      priorMiddle: this.editingPriorNameOriginal.m,
      priorLast: this.editingPriorNameOriginal.l,
      priorSuffix: this.editingPriorNameOriginal.s ? this.editingPriorNameOriginal.s : null
    });
    this.priorNameEditFormSnapshot = {
      priorFirst: this.editingPriorNameOriginal.f,
      priorMiddle: this.editingPriorNameOriginal.m,
      priorLast: this.editingPriorNameOriginal.l,
      priorSuffix: this.editingPriorNameOriginal.s ? this.editingPriorNameOriginal.s : null
    };
    this.addPriorNameForm.markAsPristine();
    this.addPriorNameForm.markAsUntouched();
    if (!this.editPriorNameModalTpl) {
      return;
    }
    this.editPriorNameModalRef = this.modalService.open(this.editPriorNameModalTpl, this.priorNameEditModalOptions());
    this.editPriorNameModalRef.result.then(
      () => {
        this.clearPriorNameEditModalState();
      },
      () => {
        this.clearPriorNameEditModalState();
      }
    );
  }

  private clearPriorNameEditModalState(): void {
    this.editPriorNameModalRef = null;
    this.editingPriorNameOriginal = null;
    this.priorNameEditFormSnapshot = null;
    this.addPriorNameForm.reset();
    this.addPriorNameForm.markAsPristine();
  }

  onResetEditPriorNameForm(): void {
    const snap = this.priorNameEditFormSnapshot;
    if (!snap) {
      return;
    }
    this.addPriorNameForm.patchValue({
      priorFirst: snap.priorFirst,
      priorMiddle: snap.priorMiddle,
      priorLast: snap.priorLast,
      priorSuffix: snap.priorSuffix
    });
    this.addPriorNameForm.markAsPristine();
    this.addPriorNameForm.markAsUntouched();
  }

  onUpdatePriorNameFromModal(modal: NgbActiveModal): void {
    if (this.addPriorNameForm.invalid) {
      this.addPriorNameForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    const v = this.addPriorNameForm.getRawValue() as Record<string, string>;
    const currentLegal = this.quadFromNameChangeSnapshot();
    const newPrior = {
      f: v['priorFirst'] ?? '',
      m: v['priorMiddle'] ?? '',
      l: v['priorLast'] ?? '',
      s: this.suffixFormToString(v['priorSuffix'])
    };
    if (this.namesFourEqual(newPrior, currentLegal)) {
      this.alertService.error(
        this.toastMessage['T0018'] || 'This name matches the current legal name.'
      );
      return;
    }
    if (
      this.editingPriorNameOriginal &&
      this.namesFourEqual(newPrior, this.editingPriorNameOriginal)
    ) {
      this.alertService.error(
        this.toastMessage['T0019'] || 'This prior name is already in the list.'
      );
      return;
    }
    if (this.isPriorNameDuplicateInTable(newPrior, this.editingPriorNameOriginal)) {
      this.alertService.error(
        this.toastMessage['T0019'] || 'This prior name is already in the list.'
      );
      return;
    }
    const personId = this.personId;
    const orig = this.editingPriorNameOriginal;
    if (!personId || !orig) {
      this.alertService.error('Person ID is required.');
      return;
    }
    const body = {
      personId: Number(personId),
      auditUserId: this.getCurrentUserId(),
      match: {
        priorFirstName: orig.f,
        priorMiddleName: orig.m,
        priorLastName: orig.l,
        suffix: orig.s
      },
      updated: {
        priorFirstName: newPrior.f,
        priorMiddleName: newPrior.m,
        priorLastName: newPrior.l,
        suffix: newPrior.s
      }
    };
    const url = this.buildSearchUrl('/search/educator-update-prior-name');
    this.http
      .post<{ status?: string; data?: { success?: boolean; message?: string }; message?: string; success?: boolean }>(
        url,
        body,
        { headers: this.buildJsonHeaders() }
      )
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const success = res?.status === 'success' || res?.data?.success === true || res?.success === true;
          if (success) {
            this.alertService.success(this.toastMessage['T0020'] || 'Updated successfully.');
            modal.close('saved');
            this.requestDashboardRefresh.emit();
            if (!this._rowInput) {
              this.loadEducatorDashboardData();
            }
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Save failed.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to update prior name.');
        }
      });
  }

  onDeletePriorNameRow(row: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    suffix?: string;
    cd_suffix?: string;
  }): void {
    const personId = this.personId;
    if (!personId) {
      this.alertService.error('Person ID is required.');
      return;
    }
    const key =
      row?.cd_suffix != null && String(row.cd_suffix).trim() !== ''
        ? String(row.cd_suffix).trim()
        : this.resolveSuffixKeyFromDisplay(String(row?.suffix ?? ''));
    const body = {
      personId: Number(personId),
      auditUserId: this.getCurrentUserId(),
      match: {
        priorFirstName: String(row?.first_name ?? '').trim(),
        priorMiddleName: String(row?.middle_name ?? '').trim(),
        priorLastName: String(row?.last_name ?? '').trim(),
        suffix: this.suffixFormToString(key)
      }
    };
    const deleteModalOpts: NgbModalOptions = { backdrop: 'static', size: 'md', centered: true, windowClass: 'custom-delete-record-modal' };
    const modalRef = this.modalService.open(ModalComponent, deleteModalOpts);
    const config: ModalConfig = {
      heading: 'Delete Record',
      headingAlign: 'left',
      modalContent:
        'Are you sure you want to delete this record? This action cannot be undone.',
      modalContentClass: 'actdelcontent',
      hasTemplate: true,
      isDangerButton: true,
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Delete', code: 'Y', btnOutline: false }
      ],
      panelClass: 'lookup-act-delete-modal'
    };
    modalRef.componentInstance.modalConfig = config;
    modalRef.componentInstance.emitData.pipe(take(1)).subscribe((response: { code?: string; confirmed?: boolean }) => {
      modalRef.close();
      if (response?.code === 'Y' || response?.confirmed === true) {
        this.executePriorNameDelete(body);
      }
    });
  }

  private executePriorNameDelete(body: {
    personId: number;
    match: {
      priorFirstName: string;
      priorMiddleName: string;
      priorLastName: string;
      suffix: string;
    };
  }): void {
    const url = this.buildSearchUrl('/search/educator-delete-prior-name');
    this.http
      .post<{ status?: string; data?: { success?: boolean; message?: string }; message?: string; success?: boolean }>(
        url,
        body,
        { headers: this.buildJsonHeaders() }
      )
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const success = res?.status === 'success' || res?.data?.success === true || res?.success === true;
          if (success) {
            this.alertService.success(this.toastMessage['T0040'] || 'Record deleted successfully.');
            this.requestDashboardRefresh.emit();
            if (!this._rowInput) {
              this.loadEducatorDashboardData();
            }
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Delete failed.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to delete prior name.');
        }
      });
  }

  onSubmitAddPriorName(): void {
    if (this.addPriorNameForm.invalid) {
      this.addPriorNameForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    const v = this.addPriorNameForm.getRawValue() as Record<string, string>;
    const currentLegal = this.quadFromNameChangeSnapshot();
    const newPrior = {
      f: v['priorFirst'] ?? '',
      m: v['priorMiddle'] ?? '',
      l: v['priorLast'] ?? '',
      s: this.suffixFormToString(v['priorSuffix'])
    };
    if (this.namesFourEqual(newPrior, currentLegal)) {
      this.alertService.error(
        this.toastMessage['T0018'] || 'This name matches the current legal name.'
      );
      return;
    }
    if (this.isPriorNameDuplicateInTable(newPrior, null)) {
      this.alertService.error(
        this.toastMessage['T0019'] || 'This prior name is already in the list.'
      );
      return;
    }
    const personId = this.personId;
    if (!personId) {
      this.alertService.error('Person ID is required.');
      return;
    }

    const body = {
      personId: Number(personId),
      auditUserId: this.getCurrentUserId(),
      priorNames: [
        {
          priorFirstName: newPrior.f,
          priorMiddleName: newPrior.m,
          priorLastName: newPrior.l,
          suffix: newPrior.s
        }
      ]
    };
    const url = this.buildSearchUrl('/search/educator-update-name-change');
    this.http
      .post<{ status?: string; data?: { success?: boolean; message?: string }; message?: string }>(url, body, {
        headers: this.buildJsonHeaders()
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const success = res?.status === 'success' || res?.data?.success;
          if (success) {
            this.alertService.success(this.toastMessage['T0005'] || 'Saved successfully.');
            this.showAddPriorForm = false;
            this.addPriorNameForm.reset();
            this.addPriorNameForm.markAsPristine();
            this.requestDashboardRefresh.emit();
            if (!this._rowInput) {
              this.loadEducatorDashboardData();
            }
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Save failed.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to add prior name.');
        }
      });
  }

  private exportPersonalGridExcel(
    grid: DataGridComponent | undefined,
    cols: { label: string; key: string; dataType?: string }[],
    fileName: string
  ): void {
    if (!grid || typeof grid.exportFilteredDataToExcel !== 'function') {
      this.alertService.warn('Grid is not ready to export.');
      return;
    }
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export.');
      return;
    }
    const inst = grid.dataGrid?.instance;
    if (inst && inst.pageSize && inst.totalCount) {
      const oldPageSize = inst.pageSize();
      const totalCount = inst.totalCount();
      if (oldPageSize != null && totalCount != null && oldPageSize < totalCount) {
        inst.pageSize(totalCount);
        setTimeout(() => {
          grid.exportFilteredDataToExcel(cols, fileName);
          setTimeout(() => inst.pageSize(oldPageSize), 400);
        }, 200);
        return;
      }
    }
    grid.exportFilteredDataToExcel(cols, fileName);
  }

  onPriorNamesExportExcel(): void {
    const cols = columnsForExcelExport(
      this.priorNamesGridConfig.customHeaders,
      this.priorNamesColumnVisibility
    );
    this.exportPersonalGridExcel(this.priorNamesGridRef, cols, 'PriorNames.xlsx');
  }

  togglePriorNamesColumnChooser(): void {
    this.showPriorNamesColumnChooser = !this.showPriorNamesColumnChooser;
  }

  closePriorNamesColumnChooser(): void {
    this.showPriorNamesColumnChooser = false;
  }

  onPriorNameGridColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.priorNamesColumnVisibility[ev.key] !== false;
    this.priorNamesColumnVisibility = { ...this.priorNamesColumnVisibility, [ev.key]: !cur };
    const prev = this.priorNamesGridConfig;
    this.priorNamesGridConfig = prev.updateConfig({
      visibleColumns: { ...this.priorNamesColumnVisibility },
      _changeKey: Date.now()
    });
  }

  private normNamePart(v: unknown): string {
    return (v ?? '').toString().trim().toLowerCase();
  }

  private trimNamePart(v: unknown): string {
    return (v ?? '').toString().trim();
  }

  /** Suffix select uses `null` for “none”; API / comparisons use empty string. */
  private suffixFormToString(v: unknown): string {
    return v != null && String(v).trim() !== '' ? String(v) : '';
  }

  getSuffixAriaLabel(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const normalizedValue = value.trim().toUpperCase();
    return ROMAN_SUFFIX_MAP[normalizedValue] || value;
  }

  private namesFourEqual(
    a: { f: string; m: string; l: string; s: string },
    b: { f: string; m: string; l: string; s: string }
  ): boolean {
    return (
      this.normNamePart(a.f) === this.normNamePart(b.f) &&
      this.normNamePart(a.m) === this.normNamePart(b.m) &&
      this.normNamePart(a.l) === this.normNamePart(b.l) &&
      this.normNamePart(a.s) === this.normNamePart(b.s)
    );
  }

  private namesFourEqualExact(
    a: { f: string; m: string; l: string; s: string },
    b: { f: string; m: string; l: string; s: string }
  ): boolean {
    return (
      this.trimNamePart(a.f) === this.trimNamePart(b.f) &&
      this.trimNamePart(a.m) === this.trimNamePart(b.m) &&
      this.trimNamePart(a.l) === this.trimNamePart(b.l) &&
      this.normNamePart(a.s) === this.normNamePart(b.s)
    );
  }

  private priorRowToQuad(p: any): { f: string; m: string; l: string; s: string } {
    const keyFromRow =
      p?.cd_suffix != null && String(p.cd_suffix).trim() !== '' ? String(p.cd_suffix).trim() : '';
    const suffixKey = keyFromRow || this.resolveSuffixKeyFromDisplay(String(p?.suffix ?? ''));
    return {
      f: String(p?.prior_first_name ?? p?.first_name ?? p?.legal_first_name ?? p?.priorFN ?? ''),
      m: String(p?.prior_middle_name ?? p?.middle_name ?? p?.legal_middle_name ?? p?.priorMN ?? ''),
      l: String(p?.prior_last_name ?? p?.last_name ?? p?.legal_last_name ?? p?.priorLN ?? ''),
      s: this.suffixFormToString(suffixKey)
    };
  }

  /** Map suffix display label to lookup key when `cd_suffix` is missing from dashboard JSON. */
  private resolveSuffixKeyFromDisplay(display: string): string {
    const d = String(display ?? '').trim();
    if (!d) {
      return '';
    }
    const hit = this.suffixList.find(
      x => String(x.value ?? '').trim().toLowerCase() === d.toLowerCase()
    );
    return hit ? String(hit.key).trim() : d;
  }

  private isPriorNameDuplicateInTable(
    quad: { f: string; m: string; l: string; s: string },
    exclude?: { f: string; m: string; l: string; s: string } | null
  ): boolean {
    const row = this.educatorDashboardRow as any;
    const list = Array.isArray(row?.p_prior_names) ? row.p_prior_names : [];
    for (const p of list) {
      const q = this.priorRowToQuad(p);
      if (exclude && this.namesFourEqual(q, exclude)) {
        continue;
      }
      if (this.namesFourEqual(quad, q)) return true;
    }
    return false;
  }

  /**
   * G0067: show only when the Add Prior Name values match an existing prior-name row (FN/MN/LN/suffix), same check as T0019.
   */
  shouldShowAddPriorNameG0067Info(): boolean {
    if (!this.showAddPriorForm) {
      return false;
    }
    return this.isPriorNameDuplicateInTable(this.priorNameQuadFromAddForm(), null);
  }

  /**
   * G0067 in Edit Prior Name modal: same duplicate check as add, excluding the row being edited (so unchanged original does not warn).
   */
  shouldShowEditPriorNameG0067Info(): boolean {
    if (!this.editingPriorNameOriginal) {
      return false;
    }
    return this.isPriorNameDuplicateInTable(this.priorNameQuadFromAddForm(), this.editingPriorNameOriginal);
  }

  private priorNameQuadFromAddForm(): { f: string; m: string; l: string; s: string } {
    const v = this.addPriorNameForm.getRawValue() as Record<string, string | null>;
    return {
      f: v['priorFirst'] ?? '',
      m: v['priorMiddle'] ?? '',
      l: v['priorLast'] ?? '',
      s: this.suffixFormToString(v['priorSuffix'])
    };
  }

  onSupportingDocumentsExportExcel(): void {
    const cols = columnsForExcelExport(
      this.supportingDocumentsGridConfig.customHeaders,
      this.supportingDocumentsColumnVisibility
    );
    this.exportPersonalGridExcel(this.supportingDocumentsGridRef, cols, 'SupportingDocuments.xlsx');
  }

  toggleSupportingDocumentsColumnChooser(): void {
    this.showSupportingDocumentsColumnChooser = !this.showSupportingDocumentsColumnChooser;
  }

  closeSupportingDocumentsColumnChooser(): void {
    this.showSupportingDocumentsColumnChooser = false;
  }

  onSupportingDocumentsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.supportingDocumentsColumnVisibility[ev.key] !== false;
    this.supportingDocumentsColumnVisibility = {
      ...this.supportingDocumentsColumnVisibility,
      [ev.key]: !cur
    };
    this.supportingDocumentsGridConfig = this.supportingDocumentsGridConfig.updateConfig({
      visibleColumns: { ...this.supportingDocumentsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  onIdentityPhotoSupportingDocumentsExportExcel(): void {
    const cols = columnsForExcelExport(
      this.identityPhotoSupportingDocumentsGridConfig.customHeaders,
      this.identityPhotoSupportingDocumentsColumnVisibility
    );
    this.exportPersonalGridExcel(
      this.identityPhotoSupportingDocumentsGridRef,
      cols,
      'IdentityPhotoSupportingDocuments.xlsx'
    );
  }

  toggleIdentityPhotoSupportingDocumentsColumnChooser(): void {
    this.showIdentityPhotoSupportingDocumentsColumnChooser = !this.showIdentityPhotoSupportingDocumentsColumnChooser;
  }

  closeIdentityPhotoSupportingDocumentsColumnChooser(): void {
    this.showIdentityPhotoSupportingDocumentsColumnChooser = false;
  }

  onIdentityPhotoSupportingDocumentsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.identityPhotoSupportingDocumentsColumnVisibility[ev.key] !== false;
    this.identityPhotoSupportingDocumentsColumnVisibility = {
      ...this.identityPhotoSupportingDocumentsColumnVisibility,
      [ev.key]: !cur
    };
    this.identityPhotoSupportingDocumentsGridConfig = this.identityPhotoSupportingDocumentsGridConfig.updateConfig({
      visibleColumns: { ...this.identityPhotoSupportingDocumentsColumnVisibility },
      _changeKey: Date.now()
    });
  }

  async onSupportingDocumentsUpload(): Promise<void> {
    if (!this.personId) {
      this.alertService.warn('Person ID is required.');
      return;
    }

    this.staffProfileUploadContext = 'nameChange';
    const confirmed = await this.staffDocNoticeModal.open({ contentVariant: 'default' });
    if (confirmed) {
      this.openStaffUploadFilesAfterNotice();
    }
  }

  async onIdentityPhotoSupportingDocumentsUpload(): Promise<void> {
    if (!this.personId) {
      this.alertService.warn('Person ID is required.');
      return;
    }

    this.staffProfileUploadContext = 'identityPhoto';
    const confirmed = await this.staffDocNoticeModal.open({ contentVariant: 'default' });
    if (confirmed) {
      this.openStaffUploadFilesAfterNotice();
    }
  }

  private openStaffUploadFilesAfterNotice(): void {
    const filesTpl = this.staffUploadFilesTpl;
    if (!filesTpl) {
      return;
    }
    this.resetStaffProfileUploadState();
    if (this.staffProfileUploadContext === 'identityPhoto') {
      this.initIdentityPhotoStaffUploadDocMeta();
    } else {
      this.initNameChangeStaffUploadDocMeta();
    }
    this.staffUploadFilesModalRef = this.modalService.open(filesTpl, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      keyboard: true,
      windowClass: 'lookup-staff-doc-modal lookup-custom-staff-doc-modal'
    });
    this.staffUploadFilesModalRef.result.finally(() => {
      this.staffUploadFilesModalRef = null;
    });
  }

  private resetStaffProfileUploadState(): void {
    this.staffProfileUploadFile = null;
    this.staffProfileUploadNotes = '';
    this.staffProfileUploadError = '';
    this.staffProfileDropzoneActive = false;
    this.nameChangeUploadCategoryLabel = '';
    this.nameChangeUploadCategoryKey = '';
    this.nameChangeUploadTypeOptions = [];
    this.nameChangeUploadTypeKey = '';
    this.activeUploadCategoryOptions = [];
    this.staffUploadCategoryDisabled = false;
    this.initialUploadCategoryKey = '';
    this.initialUploadTypeKey = '';
  }

  /**
   * Check if there is any unsaved draft data in the upload modal.
   * Returns true if:
   * - File has been selected, OR
   * - Notes have been entered, OR
   * - Category has been changed from initial value, OR
   * - Type has been changed from initial value
   */
  private hasStaffProfileUploadDraft(): boolean {
    const notes = String(this.staffProfileUploadNotes ?? '').trim();
    const currentCategory = String(this.nameChangeUploadCategoryKey ?? '').trim();
    const currentType = String(this.nameChangeUploadTypeKey ?? '').trim();
    const initialCategory = String(this.initialUploadCategoryKey ?? '').trim();
    const initialType = String(this.initialUploadTypeKey ?? '').trim();

    const categoryChanged = currentCategory !== initialCategory;
    const typeChanged = currentType !== initialType;

    return !!(
      this.staffProfileUploadFile ||
      notes ||
      categoryChanged ||
      typeChanged
    );
  }

  /**
   * Opens discard upload draft confirmation modal.
   * Matches Activities tab `openDiscardUploadDraftModal` ModalComponent config.
   */
  private openDiscardStaffUploadDraftModal(onYes: () => void): void {
    const modalRef = this.modalService.open(ModalComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.modalConfig = getCancelFileUploadModalConfig();
    modalRef.result
      .then((result: any) => {
        if (result?.code === 'Y' || result?.confirmed === true) {
          onYes();
        }
      })
      .catch(() => {
        /* dismissed — keep upload modal open */
      });
  }

  /**
   * Shows confirmation modal when canceling file upload with unsaved changes.
   * If no file/notes have been entered or category/type have been changed from initial values,
   * closes the modal immediately without showing cancel confirmation.
   */
  showCancelUploadConfirmation(): void {
    if (this.staffProfileUploading) {
      return;
    }
    if (this.hasStaffProfileUploadDraft()) {
      this.openDiscardStaffUploadDraftModal(() => this._actuallyCloseStaffUploadModal());
    } else {
      this._actuallyCloseStaffUploadModal();
    }
  }

  /**
   * Actually closes the staff upload modal and resets all state.
   */
  private _actuallyCloseStaffUploadModal(): void {
    this.staffUploadFilesModalRef?.close();
    this.staffUploadFilesModalRef = null;
    this.resetStaffProfileUploadState();
  }

  onUploadTypeDropdownShow(dropdown: Dropdown): void {
    flipLookupUploadDropdownAbove(dropdown);
  }

  onStaffProfileFileInputChange(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) {
      this.trySetStaffProfileUploadFile(file);
    }
  }

  private trySetStaffProfileUploadFile(file: File): void {
    this.staffProfileUploadError = '';
    const name = file.name || '';
    const lower = name.toLowerCase();
    const ext = lower.includes('.') ? lower.substring(lower.lastIndexOf('.')) : '';
    if (!ext || !this.staffProfileAllowedExt.includes(ext)) {
      this.staffProfileUploadError = this.errorMessage['M0129'];
      return;
    }
    if (file.size > this.staffProfileMaxBytes) {
      this.staffProfileUploadError = this.errorMessage['M0128'];
      return;
    }
    this.staffProfileUploadFile = file;
  }

  onStaffProfileDragOver(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.staffProfileDropzoneActive = true;
  }

  onStaffProfileDragLeave(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.staffProfileDropzoneActive = false;
  }

  onStaffProfileDrop(ev: DragEvent): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.staffProfileDropzoneActive = false;
    const file = ev.dataTransfer?.files?.[0];
    if (file) {
      this.trySetStaffProfileUploadFile(file);
    }
  }

  clearStaffProfileUploadFile(): void {
    this.staffProfileUploadFile = null;
    this.staffProfileUploadError = '';
  }

  private getStaffProfileUploadDuplicateLookups(): {
    categoryLookups: ReturnType<typeof toDuplicateLookupOptions>;
    typeLookups: ReturnType<typeof toDuplicateLookupOptions>;
  } {
    const categoryLookups = toDuplicateLookupOptions(
      this.nameChangeUploadCategoryKey
        ? [{ key: this.nameChangeUploadCategoryKey, value: this.nameChangeUploadCategoryLabel }]
        : []
    );
    return {
      categoryLookups,
      typeLookups: toDuplicateLookupOptions(this.nameChangeUploadTypeOptions)
    };
  }

  private cacheStaffProfileUploadAfterSuccess(params: {
    cd_document_category: string;
    cd_document_type: string;
    document_category: string;
    document_type: string;
    file_name: string;
    profile_tab_section: string;
    document_id?: string | number | null;
  }): void {
    const override = {
      cd_document_category: params.cd_document_category,
      cd_document_type: params.cd_document_type,
      document_category: params.document_category,
      document_type: params.document_type
    };
    const fileKey = `file:${String(params.file_name || '').trim().toLowerCase()}`;
    if (fileKey !== 'file:') {
      this.pendingUploadTypeOverrides.set(fileKey, override);
    }
    if (params.document_id != null && String(params.document_id).trim() !== '') {
      this.pendingUploadTypeOverrides.set(String(params.document_id), override);
    }
    if (params.cd_document_category && this.nameChangeUploadTypeOptions.length) {
      this.supportingDocTypesCache[params.cd_document_category] = this.nameChangeUploadTypeOptions;
    }
    appendProfileUploadToDashboardRow(
      this.educatorDashboardRow as Record<string, unknown> | null,
      buildProfileUploadRowForLocalCache(params)
    );
    this.refreshDocumentGridsWithLabelPreload();
  }

  private extractUploadedDocumentId(res: any): string | null {
    const row = Array.isArray(res?.data) ? res.data[0] : res?.data;
    const id = row?.document_id ?? row?.profile_document_upload_id ?? null;
    if (id == null || String(id).trim() === '') {
      return null;
    }
    return String(id);
  }

  private mergePendingUploadTypesIntoRow(row: EducatorDashboardDataRow | null): void {
    if (!row || !this.pendingUploadTypeOverrides.size) {
      return;
    }
    const uploads = profileUploadDocumentsFromDashboardRow(row as Record<string, unknown>);
    if (!uploads.length) {
      return;
    }
    let changed = false;
    const merged = uploads.map((doc) => {
      const docId = String(doc['document_id'] ?? doc['profile_document_upload_id'] ?? '').trim();
      const fileName = String(doc['file_name'] ?? '').trim().toLowerCase();
      const override =
        (docId ? this.pendingUploadTypeOverrides.get(docId) : undefined) ??
        (fileName ? this.pendingUploadTypeOverrides.get(`file:${fileName}`) : undefined);
      if (!override) {
        return doc;
      }
      changed = true;
      if (docId) {
        this.pendingUploadTypeOverrides.delete(docId);
      }
      if (fileName) {
        this.pendingUploadTypeOverrides.delete(`file:${fileName}`);
      }
      return {
        ...doc,
        cd_document_category: override.cd_document_category,
        cd_document_type: override.cd_document_type,
        document_category: override.document_category ?? override.cd_document_category,
        document_type: override.document_type ?? override.cd_document_type
      };
    });
    if (changed) {
      (row as Record<string, unknown>)['p_profile_upload'] = merged;
    }
  }

  private getProfileDocumentLabelContext(): ProfileDocumentLabelContext {
    return {
      categories: this.allSupportingDocCategories,
      typesByCategory: this.supportingDocTypesCache
    };
  }

  private preloadSupportingDocTypesForDocuments(): Observable<void> {
    const docs = profileUploadDocumentsFromDashboardRow(
      this.educatorDashboardRow as Record<string, unknown> | null
    );
    const ctx = this.getProfileDocumentLabelContext();
    const missingKeys = uniqueSystemACategoryKeysFromDocuments(docs, ctx).filter(
      (key) => !this.supportingDocTypesCache[key]?.length
    );
    if (!missingKeys.length) {
      return of(void 0);
    }
    const requests = missingKeys.map((key) => {
      const cat = this.allSupportingDocCategories.find((c) => c.key === key);
      if (!cat) {
        return of([] as SupportingDocumentGridOption[]);
      }
      return this.supportingDocLookupService.getTypesByCategory(cat).pipe(
        tap((types) => {
          this.supportingDocTypesCache[key] = types;
        })
      );
    });
    return forkJoin(requests).pipe(map(() => void 0));
  }

  private refreshDocumentGridsWithLabelPreload(): void {
    this.preloadSupportingDocTypesForDocuments()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => this.buildDocumentAndTransactionGrids());
  }

  triggerStaffProfileFilePicker(input: HTMLInputElement): void {
    input?.click();
  }

  async onStaffProfileUploadSave(): Promise<void> {
    this.staffProfileUploadError = '';
    if (!this.staffProfileUploadFile) {
      this.staffProfileUploadError = 'Please upload a file.';
      return;
    }
    const personId = this.personId;
    if (!personId) {
      this.alertService.warn('Person ID is required.');
      return;
    }
    const uploadedBy = this.getCurrentUserId();
    if (uploadedBy == null || !Number.isFinite(uploadedBy) || uploadedBy <= 0) {
      this.alertService.error('Unable to determine the current user. Please sign in again.');
      return;
    }
    if (!this.nameChangeUploadCategoryKey?.trim()) {
      this.alertService.error(
        this.staffProfileUploadContext === 'identityPhoto'
          ? 'Document category for Proof of Photo Identification is not configured. Please contact support or verify lookup data.'
          : 'Document category for name change is not configured. Please contact support or verify lookup data.'
      );
      return;
    }
    const typeKey = (this.nameChangeUploadTypeKey ?? '').trim();
    if (!typeKey) {
      this.staffProfileUploadError = 'Please select a document type.';
      return;
    }
    const file = this.staffProfileUploadFile;
    const personIdNum = Number(personId);
    const typeLabel =
      this.nameChangeUploadTypeOptions.find((t) => t.key === typeKey)?.value ?? typeKey;
    const { categoryLookups, typeLookups } = this.getStaffProfileUploadDuplicateLookups();
    const proceed = await this.duplicateDocGuard.ensureProceedOrCancel({
      educatorDashboardRow: this.educatorDashboardRow as Record<string, unknown> | null,
      categoryKey: this.nameChangeUploadCategoryKey,
      typeKey,
      fileName: file.name,
      screenName: 'Lookup User - Personal',
      personId: personIdNum,
      categoryDisplay: this.nameChangeUploadCategoryLabel,
      typeDisplay: typeLabel,
      categoryLookups,
      typeLookups
    });
    if (!proceed) {
      this.clearStaffProfileUploadFile();
      return;
    }
    const profileTabSection =
      this.staffProfileUploadContext === 'identityPhoto'
        ? 'Identity and Contact Information'
        : 'Name History';
    const uploadData = {
      person_id: Number(personId),
      uploaded_by: uploadedBy,
      uploaded_at: new Date().toISOString(),
      file_name: file.name,
      cd_document_category: this.nameChangeUploadCategoryKey,
      cd_document_type: typeKey,
      notes: (this.staffProfileUploadNotes || '').trim(),
      profile_tab_section: profileTabSection,
      formid: LOOKUP_USER_SECTION_FORM_IDS[profileTabSection] ?? null
    };
    this.staffProfileUploading = true;
    educatorDocumentUploadWithClientBlobPut(this.httpService, file, uploadData)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.staffProfileUploading = false;
          if (res?.success === false) {
            this.alertService.error(res.message || 'Failed to upload document.');
            return;
          }
          this.alertService.success(this.toastMessage['T0005'] || 'Document uploaded successfully.');
          this.cacheStaffProfileUploadAfterSuccess({
            cd_document_category: this.nameChangeUploadCategoryKey,
            cd_document_type: typeKey,
            document_category: this.nameChangeUploadCategoryLabel,
            document_type: typeLabel,
            file_name: file.name,
            profile_tab_section: profileTabSection,
            document_id: this.extractUploadedDocumentId(res)
          });
          this.staffUploadFilesModalRef?.close();
          this.resetStaffProfileUploadState();
          this.requestDashboardRefresh.emit();
        },
        error: (err: any) => {
          this.staffProfileUploading = false;
          const msg =
            err?.error?.message ||
            err?.error?.data?.message ||
            err?.message ||
            'Failed to upload document.';
          this.alertService.error(msg);
        }
      });
  }

  supportingDocumentHasValidId(row: { document_id?: number | null }): boolean {
    const n = Number(row?.document_id);
    return Number.isFinite(n) && n > 0;
  }

  viewSupportingDocument(row: { document_id?: number | null; file_name?: string }): void {
    const id = Number(row?.document_id);
    if (!Number.isFinite(id) || id <= 0) {
      this.alertService.error('Document information is missing');
      return;
    }
    const payload = { profile_document_upload_id: id };
    this.httpService
      .post(API_ENDPOINTS.EDUCATOR_COMMUNICATION_DOWNLOAD, payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const fileUrl = resolveEducatorDocumentFileUrl(response);
          if (!fileUrl) {
            this.alertService.error('Failed to retrieve document URL');
            return;
          }
          this.documentPreviewModal.openFromUrl(fileUrl, row.file_name);
        },
        error: () => {
          this.alertService.error('Failed to view document. Please try again.');
        }
      });
  }

  downloadSupportingDocument(row: { document_id?: number | null; file_name?: string }): void {
    const id = Number(row?.document_id);
    if (!Number.isFinite(id) || id <= 0) {
      this.alertService.error('Document information is missing');
      return;
    }
    const payload = { profile_document_upload_id: id };
    this.httpService
      .post(API_ENDPOINTS.EDUCATOR_COMMUNICATION_DOWNLOAD, payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const fileUrl = this.resolveSupportingDocumentFileUrl(response);
          if (!fileUrl) {
            this.alertService.error('Failed to download document');
            return;
          }
          fetch(fileUrl)
            .then(res => res.blob())
            .then(blob => {
              const fileName = String(row.file_name || `document_${id}`);
              FileSaver.saveAs(blob, fileName);
              //this.alertService.success('Document downloaded successfully');
            })
            .catch(() => {
              this.alertService.error('Failed to download document file');
            });
        },
        error: () => {
          this.alertService.error('Failed to download document. Please try again.');
        }
      });
  }

  private resolveSupportingDocumentFileUrl(response: any): string | null {
    return (
      response?.data?.data?.p_document_blob?.file_url ||
      response?.data?.data?.p_document_blob?.fileUrl ||
      response?.data?.data?.p_document_blob?.strfileHash ||
      response?.data?.data?.p_document_blob?.file_hash ||
      response?.data?.data?.p_document_blob?.fileHash ||
      response?.data?.file_url ||
      response?.data?.fileUrl ||
      response?.data?.url ||
      response?.file_url ||
      response?.fileUrl ||
      null
    );
  }

  onTransactionHistoryDownload(): void {
    const cols = columnsForExcelExport(
      this.transactionHistoryGridConfig.customHeaders,
      this.transactionHistoryColumnVisibility
    );
    this.exportPersonalGridExcel(this.transactionHistoryGridRef, cols, 'TransactionHistory.xlsx');
  }

  toggleTransactionHistoryColumnChooser(): void {
    this.showTransactionHistoryColumnChooser = !this.showTransactionHistoryColumnChooser;
  }

  closeTransactionHistoryColumnChooser(): void {
    this.showTransactionHistoryColumnChooser = false;
  }

  onTransactionHistoryGridColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.transactionHistoryColumnVisibility[ev.key] !== false;
    this.transactionHistoryColumnVisibility = {
      ...this.transactionHistoryColumnVisibility,
      [ev.key]: !cur
    };
    const prev = this.transactionHistoryGridConfig;
    this.transactionHistoryGridConfig = prev.updateConfig({
      visibleColumns: { ...this.transactionHistoryColumnVisibility },
      _changeKey: Date.now()
    });
  }

  /** Same shape as initial controls so native selects match value="" placeholder options after reset. */
  private resetAddTransactionFormDefaults(): void {
    this.addTransactionForm.reset({
      transactionType: this.addTxnRefundLookupKey,
      applicationKey: '',
      revenueKeys: [] as string[],
      voucherNumber: '',
      amount: ''
    });
  }

  onTransactionHistoryAdd(): void {
    if (!this.canAddManualTransaction) return;
    this.showAddTransactionForm = true;
    this.showTransactionHistoryColumnChooser = false;
    this.resetAddTransactionFormDefaults();
    this.addTransactionForm.markAsPristine();
    this.addTransactionForm.markAsUntouched();
    this.addTransactionApplicationFeeCap = 0;
    this.addTransactionMaxAmount = 0;
    this.applyAddTransactionTypeValidators();
    this.applyAddTransactionApplicationOptionsFilter();
  }

  onCancelAddTransaction(): void {
    this.showAddTransactionForm = false;
    this.resetAddTransactionFormDefaults();
    this.addTransactionApplicationFeeCap = 0;
    this.addTransactionMaxAmount = 0;
    this.applyAddTransactionTypeValidators();
    this.applyAddTransactionApplicationOptionsFilter();
  }

  /** Normalize amount to two decimal places on blur when value is a valid positive number. */
  onAddTransactionAmountBlur(): void {
    if (this.addTransactionForm.get('transactionType')?.value === this.addTxnRefundLookupKey) {
      return;
    }
    const ctrl = this.addTransactionForm.get('amount');
    if (!ctrl) return;
    const n = this.parseMoneyToNumber(ctrl.value);
    if (Number.isFinite(n) && n > 0) {
      ctrl.setValue(String(parseFloat(n.toFixed(2))), { emitEvent: true });
    }
  }

  onSaveAddTransaction(): void {
    this.applyAddTransactionTypeValidators();
    this.addTransactionForm.markAllAsTouched();
    if (this.addTransactionForm.invalid) {
      this.alertService.error('Please correct the form before saving.');
      return;
    }
    const v = this.addTransactionForm.getRawValue() as Record<string, string>;
    const typeKey = String(v['transactionType'] ?? '').trim();
    const amountNum = this.parseMoneyToNumber(v['amount']);
    if (typeKey === this.addTxnRefundLookupKey) {
      this.rebuildRefundRevenueOptionsFromBreakdown();
      const expected = this.getAddTransactionSelectedRevenueSum();
      if (
        !Number.isFinite(amountNum) ||
        expected <= 0 ||
        Math.abs(amountNum - expected) > 0.02
      ) {
        this.alertService.error(
          'Refund amount does not match the selected revenue lines. Adjust your selection and try again.'
        );
        return;
      }
    }
    const typeLabel =
      this.addTransactionTypeLookupOptions.find(o => o.key === typeKey)?.value ??
      (typeKey === this.addTxnCreditLookupKey ? 'Credit' : 'Refund');
    const appKey = String(v['applicationKey'] ?? '').trim();
    const appMeta = this.lookupApplicationMetaByKey(appKey);
    const displayApp = appMeta?.displayId || (appKey ? appKey : 'N/A');
    const educatorName = this.educatorDisplayNameForModal();
    const modalRef = this.modalService.open(ModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true
    });
    const promptHtml =
      '<div class="lookup-confirm-txn-prompt">Are you sure you want to add this transaction?</div>';
    modalRef.componentInstance.modalConfig = {
      heading: 'Confirm Transaction',
      headingAlign: 'left',
      panelClass: 'lookup-confirm-transaction-modal',
      modalContent: promptHtml,
      modalContentClass: 'lookup-confirm-txn-prompt',
      hasTemplate: true,
      confirmContent: [
        { label: 'Type', value: typeLabel },
        { label: 'Amount', value: `$${parseFloat(amountNum.toFixed(2))}` },
        { label: 'Educator', value: educatorName },
        { label: 'Application ID', value: displayApp }
      ],
      buttons: [
        { text: 'Cancel', code: 'N', btnOutline: true },
        { text: 'Confirm', code: 'Y' }
      ],
      align: false,
      hideCloseIcon: false
    };
    modalRef.componentInstance.emitData.subscribe((result: { code?: string }) => {
      modalRef.close();
      if (result?.code === 'Y') {
        this.submitManualTransactionRequest(typeKey, amountNum, appMeta);
      }
    });
  }

  private educatorDisplayNameForModal(): string {
    const p = this.profileObject();
    const fn = (p?.['firstName'] ?? p?.['first_name'] ?? '') as string;
    const ln = (p?.['lastName'] ?? p?.['last_name'] ?? '') as string;
    const combined = `${fn} ${ln}`.trim();
    if (combined) return combined;
    const prof = this.educatorDashboardRow?.p_profile;
    const profName =
      prof && typeof prof === 'object' && !Array.isArray(prof) ? (prof as any)['person_name'] : '';
    return String(p?.['person_name'] ?? profName ?? '—');
  }

  /**
   * License for application banner: grid row first, then `p_license_dtls` (match by application_id, else active, else first).
   */
  private licenseNumberForOpenFromLookup(internalId: string, gridRow?: any): string | undefined {
    const fromGrid = gridRow?.license_number ?? gridRow?.licenseNumber;
    if (fromGrid != null) {
      const g = String(fromGrid).trim();
      if (g && g !== '---') {
        return g;
      }
    }
    const dtl = this.educatorDashboardRow?.p_license_dtls;
    if (!Array.isArray(dtl) || dtl.length === 0) {
      return undefined;
    }
    const idNum = Number(internalId);
    const byApp = dtl.find(
      (l: any) =>
        l?.license_number != null &&
        String(l.license_number).trim() !== '' &&
        l.application_id != null &&
        Number(l.application_id) === idNum
    );
    if (byApp?.license_number) {
      return String(byApp.license_number).trim();
    }
    const active = dtl.find((l: any) => l?.is_active === true && l?.license_number != null && String(l.license_number).trim() !== '');
    if (active?.license_number) {
      return String(active.license_number).trim();
    }
    const first = dtl.find((l: any) => l?.license_number != null && String(l.license_number).trim() !== '');
    return first?.license_number != null ? String(first.license_number).trim() : undefined;
  }

  /**
   * Merge dashboard `p_application` + grid row into the shape expected by {@link ApplicationOpenFromGridService}.
   */
  private buildSelectedAppForOpenFromLookup(internalId: string, gridRow?: any): ApplicationOpenGridRow {
    const row = this.educatorDashboardRow;
    const apps = Array.isArray(row?.p_application) ? row.p_application : [];
    const appMatch = apps.find((a: any) => String(a.application_id) === String(internalId));

    const rawDisplay = gridRow?.application_id != null ? String(gridRow.application_id).trim() : '';
    const displayApp =
      rawDisplay !== '' && rawDisplay !== '---'
        ? rawDisplay
        : appMatch?.app_id != null && String(appMatch.app_id).trim() !== ''
          ? String(appMatch.app_id).trim()
          : String(internalId);

    const idNumeric = normalizeNumericApplicationId(internalId);
    const licenseRaw = this.licenseNumberForOpenFromLookup(internalId, gridRow);
    return {
      id: idNumeric !== null ? idNumeric : internalId,
      application_id: displayApp,
      application_number: gridRow?.application_number ?? displayApp,
      application_type: (gridRow?.application_type ?? appMatch?.app_type ?? '') as string,
      date_submitted: (gridRow?.date_submitted ?? appMatch?.date_submitted ?? '') as string,
      application_status: (gridRow?.application_status ?? appMatch?.app_status ?? '') as string,
      re_submitted_date: gridRow?.re_submitted_date as string | undefined,
      educator_name: this.educatorDisplayNameForModal(),
      is_archived: gridRow?.is_archived as boolean | undefined,
      ...(licenseRaw ? { license_number: licenseRaw } : {})
    };
  }

  private submitManualTransactionRequest(
    transactionTypeLookupKey: string,
    amountNum: number,
    appMeta: { internalId: number; typeCode: string; displayId: string } | null
  ): void {
    const personId = this.personId;
    if (!personId) {
      this.alertService.error('Person ID is required.');
      return;
    }
    const v = this.addTransactionForm.getRawValue() as Record<string, unknown>;
    const uid = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('id') : null;
    const createdBy = uid != null && !Number.isNaN(Number(uid)) ? Number(uid) : undefined;
    const body: Record<string, unknown> = {
      personId: Number(personId),
      transactionType: transactionTypeLookupKey,
      amount: amountNum,
      voucherNumber: String(v['voucherNumber'] ?? '').trim() || undefined,
      revenueCodeDescription: undefined as string | undefined,
      applicationId: undefined as number | undefined,
      applicationTypeCode: undefined as string | undefined,
      ...(createdBy != null ? { createdBy } : {})
    };
    const revKeys = (v['revenueKeys'] as string[] | null) ?? [];
    const revLabels = revKeys
      .map(k => this.findAddTransactionRevenueRowByKey(String(k))?.label)
      .filter((x): x is string => !!x);
    if (revLabels.length > 0) {
      body['revenueCodeDescription'] = revLabels.join('; ');
    }
    if (transactionTypeLookupKey === this.addTxnRefundLookupKey) {
      const ids = revKeys.map(k => Number(k)).filter(n => Number.isFinite(n));
      if (ids.length > 0) {
        body['paymentHistoryIds'] = ids;
      }
    }
    if (appMeta?.internalId != null && !Number.isNaN(Number(appMeta.internalId))) {
      body['applicationId'] = Number(appMeta.internalId);
      if (appMeta.typeCode) body['applicationTypeCode'] = appMeta.typeCode;
    }
    const fullUrl = this.buildSearchUrl('/search/educator-admin-manual-transaction');
    this.http
      .post<{ success?: boolean; message?: string }>(fullUrl, body, { headers: this.buildJsonHeaders() })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const r = res as any;
          const ok =
            r?.success === true ||
            r?.data?.success === true;
          if (ok) {
            this.alertService.success('Transaction saved.');
            this.sendTransactionNotification(transactionTypeLookupKey, amountNum);
            this.onCancelAddTransaction();
            this.requestDashboardRefresh.emit();
            if (!this._rowInput) this.loadEducatorDashboardData();
          } else {
            this.alertService.error(r?.message || r?.data?.message || 'Save failed.');
          }
        },
        error: err => {
          this.alertService.error(err?.error?.message || err?.message || 'Failed to save transaction.');
        }
      });
  }


  private sendTransactionNotification(transactionTypeLookupKey: string, amount: number): void {
    const educatorUserId = this.firstPersonInfo()?.user_id;
    if (educatorUserId == null) {
      console.warn('Transaction notification skipped: educator user_id not available in dashboard person info.');
      return;
    }

    // Determine notification message based on transaction type
    // Type 3 = Credit (N0006), Type 4 = Refund (N0007)
    let notificationMessage: string;

    if (transactionTypeLookupKey === '3') {
      // Credit transaction - N0006
      notificationMessage = `A credit of $${amount} has been added to your account. This amount will be applied to your next application fee. View the transaction in the transaction history table on your Dashboard.`;
    } else if (transactionTypeLookupKey === '4') {
      // Refund transaction - N0007
      notificationMessage = `A refund of $${amount} has been processed for your account. View the transaction in the transaction history table on your Dashboard.`;
    } else {
      // Unknown transaction type, skip notification
      return;
    }

    // Create and send notification to educator (API expects pics_core user id, not person id)
    const notificationPayload = this.commonNotificationService.createNotificationPayload(
      notificationMessage,
      educatorUserId
    );
    this.commonNotificationService.sendNotification(notificationPayload)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          // Notification success - no user feedback needed
          console.log('Transaction notification sent successfully');
        },
        error: (err) => {
          // Log error but don't block transaction flow
          console.error('Failed to send transaction notification:', err);
        }
      });
  }

  /** Transaction History: hide receipt actions for Denied / empty status (BR rule). */
  private static readonly TXN_STATUS_EMPTY_PLACEHOLDER = '\u2014';

  showTransactionReceiptActions(row: any): boolean {
    const raw = row?._raw ?? {};
    const code = raw.cd_transaction_status;
    if (code != null && String(code).trim() === '3') {
      return false;
    }
    const status = String(row?.transactionStatus ?? '').trim();
    if (!status || status === LookupUserPersonalTabComponent.TXN_STATUS_EMPTY_PLACEHOLDER || status === '---') {
      return false;
    }
    if (status === 'Denied') {
      return false;
    }
    return true;
  }

  viewTransactionReceipt(row: any): void {
    if (!this.showTransactionReceiptActions(row)) {
      return;
    }
    if (!this.rowHasReceiptApplication(row)) {
      this.alertService.warn('Not enough transaction information to generate a receipt.');
      return;
    }

    // Extract payment transaction ID from row
    const raw = row?._raw ?? {};
    const paymentTransactionId = raw?.payment_transaction_id ?? raw?.payment_history_id;

    if (!paymentTransactionId) {
      this.alertService.warn('Payment transaction ID not found.');
      return;
    }

    // Fetch payment breakdown from API

    this.httpService.post(API_ENDPOINTS.PAYMENT_BREAKDOWN, { paymentTransactionId })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const breakdownItems = response?.data?.data || [];
          const receiptData = this.buildTransactionReceiptDataFromRow(row, breakdownItems);

          void this.commonService
            .generateReceiptPdfBlob(receiptData)
            .then(blob => {
              this.documentPreviewModal.openFromBlob(blob, 'receipt.pdf', { title: 'View Receipt' });
            })
            .catch(() => {
              this.alertService.error('Failed to generate receipt. Please try again.');
            });
        },
        error: () => {
          // Fallback: generate receipt without breakdown
          const receiptData = this.buildTransactionReceiptDataFromRow(row, []);
          void this.commonService
            .generateReceiptPdfBlob(receiptData)
            .then(blob => {
              this.documentPreviewModal.openFromBlob(blob, 'receipt.pdf', { title: 'View Receipt' });
            })
            .catch(() => {
              this.alertService.error('Failed to generate receipt. Please try again.');
            });
        }
      });
  }

  downloadTransactionReceipt(row: any): void {
    if (!this.showTransactionReceiptActions(row)) {
      return;
    }
    if (!this.rowHasReceiptApplication(row)) {
      this.alertService.warn('Not enough transaction information to generate a receipt.');
      return;
    }

    // Extract payment transaction ID from row
    const raw = row?._raw ?? {};
    const paymentTransactionId = raw?.payment_transaction_id ?? raw?.payment_history_id;

    if (!paymentTransactionId) {
      this.alertService.warn('Payment transaction ID not found.');
      return;
    }

    // Fetch payment breakdown from API

    this.httpService.post(API_ENDPOINTS.PAYMENT_BREAKDOWN, { paymentTransactionId })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const breakdownItems = response?.data?.data || [];
          const receiptData = this.buildTransactionReceiptDataFromRow(row, breakdownItems);
          void this.commonService.generateReceiptPDF(receiptData, 'download');
        },
        error: () => {
          // Fallback: generate receipt without breakdown
          const receiptData = this.buildTransactionReceiptDataFromRow(row, []);
          void this.commonService.generateReceiptPDF(receiptData, 'download');
        }
      });
  }

  rowHasReceiptApplication(row: any): boolean {
    const ref = String(row?.paymentTransactionId ?? row?._raw?.payment_transaction_id ?? '').trim();
    if (ref) {
      return true;
    }
    const app = String(row?.application_id ?? '').trim();
    const amt = String(row?.amount ?? '').trim();
    return !!(app && amt);
  }

  /** Same receipt payload shape as application-dashboard `downloadTransactionReceipt` / `viewTransactionReceipt`. */
  private buildTransactionReceiptDataFromRow(row: any, breakdownItems: any[] = []): Record<string, any> {
    const raw = row?._raw ?? {};
    const currentDate = new Date();

    const getValueOrNA = (...values: any[]): string => {
      for (const val of values) {
        const str = String(val ?? '').trim();
        if (str) return str;
      }
      return 'N/A';
    };

    // Extract additional details from first breakdown item if available
    const firstItem = breakdownItems.length > 0 ? breakdownItems[0] : null;

    // Extract raw UTC transaction date string for EST timezone formatting
    let transactionDateStr = '';
    if (firstItem?.transaction_date) {
      transactionDateStr = String(firstItem.transaction_date);
    } else if (firstItem?.transaction_date_time) {
      transactionDateStr = String(firstItem.transaction_date_time);
    } else if (raw?.transaction_date) {
      transactionDateStr = String(raw.transaction_date);
    } else if (raw?.transaction_date_time) {
      transactionDateStr = String(raw.transaction_date_time);
    } else if (row?.receiptTransactionDate) {
      transactionDateStr = String(row.receiptTransactionDate);
    } else if (row?.transactionDate && typeof row.transactionDate === 'string') {
      transactionDateStr = row.transactionDate;
    }

    // Format transaction date in EST timezone with comma and seconds for receipt
    const formattedTransactionDate = transactionDateStr
      ? this.formatUtcTo12HourLocalWithSeconds(transactionDateStr, FIXED_EST_TZ)
      : this.formatUtcTo12HourLocalWithSeconds(currentDate.toISOString(), FIXED_EST_TZ);

    return {
      applicantName: this.educatorDisplayNameForModal(),
      applicationId: getValueOrNA(firstItem?.application_number, row?.application_id, row?.applicationId, raw?.application_number, raw?.application_id),
      referenceNumber: getValueOrNA(row?.paymentTransactionId, raw?.payment_transaction_id, firstItem?.receipt_number),
      invoiceNumber: getValueOrNA(firstItem?.invoice_number, row?.invoice_number, raw?.invoice_number, row?.invoiceNumber),
      paymentMethod: firstItem?.payment_method || 'Card',
      transactionDate: formattedTransactionDate,
      paymentAmount: String(row?.amount ?? raw?.amount ?? '').trim(),
      transactionType: String(row?.transaction_type ?? raw?.transaction_type ?? '').trim(),
      receiptDate: new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(currentDate),
      breakdownItems: breakdownItems
    };
  }

  private applyAddTransactionTypeValidators(): void {
    const typeCtrl = this.addTransactionForm.get('transactionType');
    const appCtrl = this.addTransactionForm.get('applicationKey');
    const revCtrl = this.addTransactionForm.get('revenueKeys');
    const type = typeCtrl?.value;
    if (type === this.addTxnRefundLookupKey) {
      appCtrl?.setValidators([Validators.required]);
    } else {
      appCtrl?.clearValidators();
    }
    appCtrl?.updateValueAndValidity({ emitEvent: false });
    if (type === this.addTxnCreditLookupKey) {
      revCtrl?.patchValue([] as string[], { emitEvent: true });
      if (revCtrl && !revCtrl.disabled) {
        revCtrl.disable({ emitEvent: false });
      }
    } else if (type === this.addTxnRefundLookupKey) {
      if (revCtrl?.disabled) {
        revCtrl.enable({ emitEvent: false });
      }
    } else {
      if (revCtrl?.disabled) {
        revCtrl.enable({ emitEvent: false });
      }
    }
    revCtrl?.updateValueAndValidity({ emitEvent: false });
    this.addTransactionForm.get('amount')?.updateValueAndValidity({ emitEvent: false });
    this.patchAddTransactionAmountDefault();
  }

  /** REFUND requires at least one revenue line; Application Fee cap is enforced on Amount only. */
  private addTransactionRevenueKeysValidator(control: AbstractControl): ValidationErrors | null {
    const type = this.addTransactionForm?.get('transactionType')?.value;
    if (type !== this.addTxnRefundLookupKey) return null;
    const v = control.value;
    const arr = Array.isArray(v) ? v : [];
    if (arr.length === 0) return { revenueRequired: true };
    return null;
  }

  /**
   * Optional. When non-empty (after trim): length 3–60; only A–Z, a–z, 0–9, spaces, . , - ' # /
   */
  private addTransactionVoucherNumberValidator(control: AbstractControl): ValidationErrors | null {
    const s = String(control.value ?? '').trim();
    if (s.length === 0) return null;
    if (s.length < 3 || s.length > 60) return { voucherLength: true };
    const allowed = /^[A-Za-z0-9.,'#\/\s-]+$/;
    if (!allowed.test(s)) return { voucherPattern: true };
    return null;
  }

  private addTransactionAmountValidator(control: AbstractControl): ValidationErrors | null {
    const raw = control.value;
    const digitsOnly = String(raw ?? '')
      .replace(/[$,\s]/g, '')
      .trim();
    if (raw === '' || raw === null || raw === undefined || digitsOnly === '') return { required: true };
    if (!/^\d*\.?\d*$/.test(digitsOnly)) return { amountFormat: true };
    const dot = digitsOnly.indexOf('.');
    if (dot !== -1 && digitsOnly.length - dot - 1 > 2) return { maxDecimals: true };
    const n = this.parseMoneyToNumber(raw);
    if (Number.isNaN(n) || n <= 0) return { min: true };
    const type = this.addTransactionForm?.get('transactionType')?.value;
    if (type === this.addTxnRefundLookupKey) {
      const expected = this.getAddTransactionSelectedRevenueSum();
      if (expected > 0 && Math.abs(n - expected) > 0.02) {
        return { maxRefund: true };
      }
    }
    return null;
  }

  private parseMoneyToNumber(v: unknown): number {
    if (v == null) return NaN;
    const s = String(v).replace(/[$,\s]/g, '');
    const n = parseFloat(s);
    return n;
  }

  private formatUsdForGrid(v: unknown): string {
    const n = this.parseMoneyToNumber(v);
    if (!Number.isFinite(n)) {
      return v == null || v === '' ? '' : String(v);
    }
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  }

  /**
   * Parses API/PostgreSQL timestamps: ISO with `T`, or `YYYY-MM-DD HH:mm:ss...` with optional offset.
   */
  private parseDashboardDate(value: unknown): Date | null {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value;
    }
    if (value == null || value === '') return null;
    const s = String(value).trim();
    if (!s) return null;
    const normalized =
      /^\d{4}-\d{2}-\d{2} /.test(s) && !s.includes('T') ? s.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T') : s;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

private formatUtcTo12HourLocal(utcStr: string, timeZone: string): string {
  if (!utcStr) return '';

  let normalized = utcStr;
  if (!/Z$/.test(normalized) && !/\+\d{2}:?\d{2}$/.test(normalized)) {
    normalized += 'Z';
  }

  const date = new Date(normalized);

  const systemLocale =
    (typeof navigator !== 'undefined' && navigator.language)
      ? navigator.language
      : 'en-US';
  const datePart = new Intl.DateTimeFormat(systemLocale, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
  const timePart = new Intl.DateTimeFormat(systemLocale, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date);

  // Return without comma
  return `${datePart} ${timePart}`;
}

private formatUtcTo12HourLocalWithSeconds(utcStr: string, timeZone: string): string {
  if (!utcStr) return '';

  let normalized = utcStr;
  if (!/Z$/.test(normalized) && !/\+\d{2}:?\d{2}$/.test(normalized)) {
    normalized += 'Z';
  }

  const date = new Date(normalized);

  const systemLocale =
    (typeof navigator !== 'undefined' && navigator.language)
      ? navigator.language
      : 'en-US';
  const datePart = new Intl.DateTimeFormat(systemLocale, {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
  const timePart = new Intl.DateTimeFormat(systemLocale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(date);

  // Return with comma and seconds for receipt
  return `${datePart}, ${timePart}`;
}

  private formatPaymentBreakdownDateTime(value: unknown): string {
    if (value == null || value === '') return '';
    const strValue = String(value);
    // Browser local timezone (timestamptz from DB → wall-clock local display)
    const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.formatUtcTo12HourLocalWithSeconds(strValue, localTz);
  }

  /**
   * Applications shown in Add Transaction (Credit/Refund) — unique list derived from
   * `p_transaction_dtls` (same source as Transaction History grid), not `p_application`,
   * so SQL duplicates from license-kind joins do not appear. Internal id is resolved
   * from the transaction row or, if missing, matched against `p_application` by number.
   */
  private computeAddTransactionApplicationOptions(): {
    key: string;
    displayId: string;
    internalId: number;
    typeCode: string;
  }[] {
    const row = this.educatorDashboardRow as any;
    const transactions = Array.isArray(row?.p_transaction_dtls) ? row.p_transaction_dtls : [];
    const apps = Array.isArray(row?.p_application) ? row.p_application : [];

    const resolveFromDashboardApps = (display: string): number => {
      if (!display) return NaN;
      const d = display.trim();
      const m = apps.find((a: any) => {
        const aid = String(a.app_id ?? a.application_number ?? a.strAppId ?? '').trim();
        return aid !== '' && aid === d;
      });
      if (m?.application_id == null) return NaN;
      const n = Number(m.application_id);
      return Number.isFinite(n) ? n : NaN;
    };

    const byInternal = new Map<
      number,
      { key: string; displayId: string; internalId: number; typeCode: string }
    >();

    for (const t of transactions) {
      let internal =
        t.application_id != null && String(t.application_id).trim() !== ''
          ? Number(t.application_id)
          : NaN;
      const displayNum =
        t.application_number != null && String(t.application_number).trim() !== ''
          ? String(t.application_number).trim()
          : '';
      const display = displayNum || (Number.isFinite(internal) ? String(internal) : '');
      const typeCode = String(t.application_type ?? '').trim();

      if (!Number.isFinite(internal) && display) {
        internal = resolveFromDashboardApps(display);
      }
      if (!Number.isFinite(internal)) continue;
      if (byInternal.has(internal)) continue;

      const displayId = display || String(internal);
      byInternal.set(internal, {
        key: String(internal),
        displayId,
        internalId: internal,
        typeCode
      });
    }

    const list = Array.from(byInternal.values());
    list.sort((a, b) =>
      (a.displayId || a.key).localeCompare(b.displayId || b.key, undefined, {
        numeric: true,
        sensitivity: 'base'
      })
    );
    return list;
  }

  listApplicationOptionsForAddTransaction(): {
    key: string;
    displayId: string;
    internalId: number;
    typeCode: string;
  }[] {
    return this.addTransactionApplicationOptions;
  }

  private lookupApplicationMetaByKey(
    key: string
  ): { internalId: number; typeCode: string; displayId: string } | null {
    if (!key) return null;
    const found =
      this.addTransactionApplicationOptions.find(o => o.key === key) ??
      this.addTransactionApplicationOptionsAll.find(o => o.key === key);
    if (!found || !Number.isFinite(found.internalId)) return null;
    return { internalId: found.internalId, typeCode: found.typeCode, displayId: found.displayId };
  }

  private onAddTransactionApplicationChanged(): void {
    const key = String(this.addTransactionForm.get('applicationKey')?.value ?? '').trim();
    this.addTransactionForm.patchValue({ revenueKeys: [] as string[] }, { emitEvent: false });
    this.updateAddTransactionApplicationFeeCap(key);
    this.rebuildAddTransactionRevenueOptionsForCurrentApplication();
    this.patchAddTransactionAmountDefault();
    this.addTransactionForm.get('amount')?.updateValueAndValidity({ emitEvent: false });
    this.addTransactionForm.get('revenueKeys')?.updateValueAndValidity({ emitEvent: false });
  }

  private getAddTransactionSelectedRevenueSum(): number {
    const keys = (this.addTransactionForm.get('revenueKeys')?.value as string[] | null) ?? [];
    return keys.reduce((sum, k) => {
      const o = this.findAddTransactionRevenueRowByKey(String(k));
      return sum + (o?.sumAmount && Number.isFinite(o.sumAmount) ? o.sumAmount : 0);
    }, 0);
  }

  private patchAddTransactionAmountDefault(): void {
    const type = this.addTransactionForm.get('transactionType')?.value;
    const sumSelected = this.getAddTransactionSelectedRevenueSum();
    const fee = this.addTransactionApplicationFeeCap;
    if (type === this.addTxnRefundLookupKey) {
      this.addTransactionMaxAmount = sumSelected;
      this.addTransactionForm.patchValue(
        { amount: sumSelected > 0 ? String(parseFloat(sumSelected.toFixed(2))) : '' },
        { emitEvent: false }
      );
    } else if (type === this.addTxnCreditLookupKey) {
      this.addTransactionMaxAmount = 0;
      if (sumSelected > 0) {
        this.addTransactionForm.patchValue({ amount: String(parseFloat(sumSelected.toFixed(2))) }, { emitEvent: false });
      }
      // When sumSelected === 0, leave the amount untouched — user enters it manually for Credit
    }
    this.addTransactionForm.get('amount')?.updateValueAndValidity({ emitEvent: false });
  }

  private formatTransactionGridDate(value: unknown): string {
    if (value == null || value === '') return '';
    const d = this.parseDashboardDate(value);
    if (!d) return String(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }

  private mapTransactionStatusDisplay(status: unknown): string {
    const s = String(status ?? '').trim().toUpperCase();
    if (!s) return '—';
    if (s.includes('AUTH') || s === 'AUTHORIZED' || s === '4' || s === 'SUCCESS' || s === 'COMPLETED') return 'Authorized';
    if (s.includes('DEN') || s.includes('DECLIN') || s === 'DENIED' || s === '3' || s === 'FAILED') return 'Denied';
    return String(status);
  }

  onPaymentBreakdownExportExcel(): void {
    const cols = columnsForExcelExport(
      this.paymentBreakdownGridConfig.customHeaders,
      this.paymentBreakdownColumnVisibility
    );
    this.exportPersonalGridExcel(this.paymentBreakdownGridRef, cols, 'PaymentBreakdown.xlsx');
  }

  togglePaymentBreakdownColumnChooser(): void {
    this.showPaymentBreakdownColumnChooser = !this.showPaymentBreakdownColumnChooser;
  }

  closePaymentBreakdownColumnChooser(): void {
    this.showPaymentBreakdownColumnChooser = false;
  }

  onPaymentBreakdownColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.paymentBreakdownColumnVisibility[ev.key] !== false;
    this.paymentBreakdownColumnVisibility = {
      ...this.paymentBreakdownColumnVisibility,
      [ev.key]: !cur
    };
    this.paymentBreakdownGridConfig = this.paymentBreakdownGridConfig.updateConfig({
      visibleColumns: { ...this.paymentBreakdownColumnVisibility },
      _changeKey: Date.now()
    });
  }

  /**
   * Name-change grid expects `uploaded_date` like application docs (often `YYYY-MM-DD`).
   * Uploaded date from `p_profile_upload` row (`uploaded_at` / `uploaded_date`).
   */
  private resolveSupportingDocUploadedDate(d: any, profileUploadRow?: any): string {
    const raw =
      d.uploaded_date ??
      d.uploadedDate ??
      profileUploadRow?.uploaded_date ??
      profileUploadRow?.uploadedDate ??
      d.uploaded_at ??
      d.uploadedAt ??
      '';
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (s.includes('T')) {
      return s.slice(0, 10);
    }
    return s;
  }

  private getCombinedDocumentCategoryLower(d: any): string {
    return String(
      d.document_category ??
        d.category_name ??
        d.category ??
        d.str_document_category ??
        d.cd_document_category_name ??
        d.cd_document_category ??
        ''
    ).toLowerCase();
  }

  private isProofOfPhotoIdentificationCategory(categoryLower: string): boolean {
    return categoryLower.includes('proof of photo') && categoryLower.includes('identification');
  }

  private isProofOfServiceCategory(d: any, categoryLower: string): boolean {
    if (categoryLower.includes('proof of service')) {
      return true;
    }
    const key = String(
      d?.cd_document_category ?? d?._raw?.cd_document_category ?? d?.document_category ?? ''
    ).trim();
    if (!key) {
      return false;
    }
    const fromLookup = this.getProfileDocumentLabelContext().categories.find(
      (c) => String(c.key) === key
    );
    return String(fromLookup?.value ?? '')
      .toLowerCase()
      .includes('proof of service');
  }

  private buildSupportingDocumentGridRow(d: any, apps: any[]): Record<string, unknown> {
    const docIdRaw = d.document_id ?? d.profile_document_upload_id ?? d.documentId;
    const document_id =
      docIdRaw != null && docIdRaw !== undefined && String(docIdRaw).trim() !== ''
        ? Number(docIdRaw)
        : NaN;
    const internalId =
      d.application_id != null && String(d.application_id).trim() !== ''
        ? String(d.application_id).trim()
        : '';
    const matchedApp = apps.find((a: any) => String(a.application_id) === internalId);
    const displayId =
      d.application_number != null && String(d.application_number).trim() !== ''
        ? String(d.application_number).trim()
        : matchedApp?.app_id != null && String(matchedApp.app_id).trim() !== ''
          ? String(matchedApp.app_id).trim()
          : internalId;
    const docRecord = { ...d, _raw: d } as Record<string, unknown>;
    const labelCtx = this.getProfileDocumentLabelContext();
    const document_category = resolveProfileDocumentCategoryLabel(docRecord, labelCtx);
    const document_type = resolveProfileDocumentTypeLabel(docRecord, labelCtx);
    return {
      id: internalId,
      application_id: displayId || '---',
      application_number: displayId,
      application_type: d.application_type ?? matchedApp?.app_type ?? '',
      application_status: d.application_status ?? matchedApp?.app_status ?? '',
      date_submitted: matchedApp?.date_submitted ?? '',
      document_id: Number.isFinite(document_id) ? document_id : null,
      document_category,
      document_type,
      file_name: d.file_name ?? '',
      uploaded_by: d.uploaded_by ?? d.uploadedBy ?? '',
      uploaded_date: this.resolveSupportingDocUploadedDate(d, undefined),
      document_status: d.status ?? d.document_status ?? '',
      _raw: d
    };
  }

  private buildDocumentAndTransactionGrids(): void {
    this.addTransactionApplicationOptionsAll = this.computeAddTransactionApplicationOptions();
    this.applyAddTransactionApplicationOptionsFilter();
    const row = this.educatorDashboardRow;
    const combinedDocs = profileUploadDocumentsFromDashboardRow(
      row as Record<string, unknown> | null
    );
    const NC_SECTIONS = ['name history', 'lookup_personal_name_change'];
    const ID_SECTIONS = ['identity and contact information', 'lookup_personal_identity_photo_id'];
    const documents = combinedDocs.filter((d: any) => {
      const pts = String(d.profile_tab_section ?? d._raw?.profile_tab_section ?? '').toLowerCase().trim();
      if (pts) return NC_SECTIONS.includes(pts);
      // fallback: category label for legacy data without profile_tab_section
      const cat = this.getCombinedDocumentCategoryLower(d);
      return cat.includes('name change') && !this.isProofOfPhotoIdentificationCategory(cat);
    });
    const labelCtx = this.getProfileDocumentLabelContext();
    const identityPhotoDocuments = combinedDocs.filter((d: any) => {
      const pts = String(d.profile_tab_section ?? d._raw?.profile_tab_section ?? '').toLowerCase().trim();
      const catLabel = resolveProfileDocumentCategoryLabel(
        { ...d, _raw: d } as Record<string, unknown>,
        labelCtx
      ).toLowerCase();
      const cat = catLabel || this.getCombinedDocumentCategoryLower(d);
      // Exclude Proof of Service mis-saved under Identity (Communication tab stale section).
      if (this.isProofOfServiceCategory(d, cat)) {
        return false;
      }
      if (pts) {
        return ID_SECTIONS.includes(pts);
      }
      // fallback: category label for legacy data
      return this.isProofOfPhotoIdentificationCategory(cat);
    });
    const transactions = Array.isArray(row?.p_transaction_dtls) ? row.p_transaction_dtls : [];
    const priorNames = Array.isArray((row as any)?.p_prior_names) ? (row as any).p_prior_names : [];

    const apps = Array.isArray(row?.p_application) ? row.p_application : [];
    const documentData = documents.map((d: any) => this.buildSupportingDocumentGridRow(d, apps));
    this._lastSupportingDocumentsRows = documentData;

    const identityPhotoDocumentData = identityPhotoDocuments.map((d: any) =>
      this.buildSupportingDocumentGridRow(d, apps)
    );
    this._lastIdentityPhotoSupportingRows = identityPhotoDocumentData;

    const transactionData = transactions.map((t: any) => {
      const internalApp =
        t.application_id != null && String(t.application_id).trim() !== ''
          ? String(t.application_id)
          : '';
      const displayApp =
        t.application_number != null && String(t.application_number).trim() !== ''
          ? String(t.application_number)
          : internalApp;
      const amt =
        t.amount != null ? (typeof t.amount === 'number' ? `$${t.amount}` : String(t.amount)) : '';
      return {
        id: internalApp,
        paymentTransactionId: t.transaction_id || t.payment_transaction_id || '',
        transaction_id: t.transaction_id ?? '',
        invoice_number: t.invoice_number ?? '',
        transactionType: this.formatTransactionTypeForGrid(t),
        application_id: displayApp || '',
        application_type: t.application_type ?? '',
        amount: amt,
        // Display date-only; sort uses raw _raw.transaction_date / payment_transaction_id
        transactionDate: this.formatTransactionGridDate(t.transaction_date),
        transactionStatus: this.mapTransactionStatusDisplay(t.transaction_status),
        _raw: t
      };
    });
    const toTxnTime = (v: unknown): number => {
      if (v == null || v === '') return 0;
      const t = new Date(v as string | Date).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    const toTxnId = (v: unknown): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    transactionData.sort((a, b) => {
      const rawA = a._raw || {};
      const rawB = b._raw || {};
      const dateDiff =
        toTxnTime(rawB.transaction_date) - toTxnTime(rawA.transaction_date);
      if (dateDiff !== 0) return dateDiff;
      return (
        toTxnId(rawB.payment_transaction_id) - toTxnId(rawA.payment_transaction_id)
      );
    });
    this._lastTransactionGridRows = transactionData;

    const priorNamesData = priorNames.map((p: any) => {
      const q = this.priorRowToQuad(p);
      const displaySuffix = String(p?.suffix ?? '').trim();
      return {
        first_name: q.f,
        middle_name: q.m,
        last_name: q.l,
        suffix: displaySuffix,
        cd_suffix: q.s,
        updated_by: p?.updated_by || '---',
        _raw: p
      };
    });

    const priorNamesHeaders = [
      this.searchHeader('First Name', 'first_name'),
      this.searchHeader('Middle Name', 'middle_name'),
      this.searchHeader('Last Name', 'last_name'),
      this.searchHeader('Suffix', 'suffix'),
      this.searchHeader('Updated By', 'updated_by')
    ];

    const priorNameKeys = priorNamesHeaders.map(h => String(h.dataField));
    this.priorNamesColumnVisibility = mergeVisibleColumnDefaultsWithChooserLock(
      priorNameKeys,
      this.priorNamesColumnVisibility
    );

    this.priorNamesGridConfig = DataGridVO.create({
      customHeaders: priorNamesHeaders,
      data: priorNamesData,
      visibleColumns: { ...this.priorNamesColumnVisibility },
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: 'No prior names',
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      _changeKey: Date.now()
    });

    const docW = 140;
    const docLeadW = 130;
    const docHeaders = [
      this.searchHeader('Application ID', 'application_id', {
        minWidth: docLeadW,
        allowFiltering: true
      }),
      this.searchHeader('Category', 'document_category', {
        minWidth: docLeadW + 50,
        allowFiltering: true
      }),
      this.searchHeader('Type', 'document_type', {
        minWidth: docLeadW,
        allowFiltering: true
      }),
      this.searchHeader('Filename', 'file_name', { minWidth: 200, allowFiltering: true }),
      this.searchHeader('Uploaded By', 'uploaded_by', { minWidth: docW, allowFiltering: true }),
      {
        dataField: 'uploaded_date',
        caption: 'Uploaded Date',
        minWidth: docW,
        allowSearch: { isSearchEnable: true, type: 'date' as const },
        allowFiltering: true,
        dataType: 'date' as const,
        filterOperations: ['=', '<', '>'],
        editorOptions: {
          displayFormat: 'MM/dd/yyyy',
          inputAttr: { inputmode: 'numeric', pattern: '[0-9/]*' }
        }
      },
      this.searchHeader('Document Status', 'document_status', { minWidth: docW, allowFiltering: true })
    ];

    const docHeaderKeys = docHeaders.map(h => String(h.dataField));
    this.supportingDocumentsColumnVisibility = mergeVisibleColumnDefaultsWithChooserLock(
      docHeaderKeys,
      this.supportingDocumentsColumnVisibility
    );
    this.supportingDocumentsAllColumns = withColumnChooserLock(headersToColumnConfigs(docHeaders));
    this.identityPhotoSupportingDocumentsColumnVisibility = mergeVisibleColumnDefaultsWithChooserLock(
      docHeaderKeys,
      this.identityPhotoSupportingDocumentsColumnVisibility
    );
    this.identityPhotoSupportingDocumentsAllColumns = withColumnChooserLock(headersToColumnConfigs(docHeaders));

    const txnHeaders = [
      this.searchHeader('Transaction ID', 'paymentTransactionId'),
      this.searchHeader('Transaction Type', 'transactionType'),
      this.searchHeader('Application ID', 'application_id'),
      this.searchHeader('Application Type', 'application_type'),
      this.searchHeader('Amount', 'amount'),
      {
        dataField: 'transactionDate',
        caption: 'Date',
        allowSearch: { isSearchEnable: true, type: 'date' as const },
        allowFiltering: true,
        dataType: 'date' as const,
        filterOperations: ['=', '<', '>'],
        editorOptions: {
          displayFormat: 'MM/dd/yyyy',
          inputAttr: { inputmode: 'numeric', pattern: '[0-9/]*' }
        }
      },
      this.searchHeader('Status', 'transactionStatus')
    ];

    const txnHeaderKeys = txnHeaders.map(h => String(h.dataField));
    this.transactionHistoryColumnVisibility = mergeVisibleColumnDefaultsWithChooserLock(
      txnHeaderKeys,
      this.transactionHistoryColumnVisibility
    );

    this.supportingDocumentsGridConfig = DataGridVO.create({
      customHeaders: docHeaders,
      data: documentData,
      visibleColumns: { ...this.supportingDocumentsColumnVisibility },
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: 'No records',
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: true,
      _changeKey: Date.now()
    });

    this.identityPhotoSupportingDocumentsGridConfig = DataGridVO.create({
      customHeaders: docHeaders,
      data: identityPhotoDocumentData,
      visibleColumns: { ...this.identityPhotoSupportingDocumentsColumnVisibility },
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: 'No records',
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      disableApplicationClick: true,
      _changeKey: Date.now()
    });

    this.transactionHistoryGridConfig = DataGridVO.create({
      customHeaders: txnHeaders,
      data: transactionData,
      visibleColumns: { ...this.transactionHistoryColumnVisibility },
      showActionsColumn: true,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: 'No transactions',
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      _changeKey: Date.now()
    });

    const paymentRowsRaw = Array.isArray((row as any)?.p_payment_breakdown)
      ? (row as any).p_payment_breakdown
      : [];
    const paymentRows = paymentRowsRaw.filter((p: any) => this.paymentBreakdownRowHasFeeCode(p));
    const paymentRowsForGrid = [...paymentRows].sort((a, b) =>
      comparePaymentBreakdownRows(
        {
          transactionDate: a.transaction_date ?? a.transaction_date_time,
          cdRevenueCode: a.cd_revenue_code,
          paymentHistoryId: a.payment_history_id ?? a.payment_transaction_detail_id
        },
        {
          transactionDate: b.transaction_date ?? b.transaction_date_time,
          cdRevenueCode: b.cd_revenue_code,
          paymentHistoryId: b.payment_history_id ?? b.payment_transaction_detail_id
        }
      )
    );
    const pbw = 128;
    const paymentData = paymentRowsForGrid.map((p: any) => {
      const internalApp =
        p.application_id != null && String(p.application_id).trim() !== ''
          ? String(p.application_id).trim()
          : '';
      const appNum =
        p.application_number != null && String(p.application_number).trim() !== ''
          ? String(p.application_number).trim()
          : '';
      return {
        id: internalApp,
        application_id: appNum || '',
        invoice_number: p.invoice_number ?? '',
        revenue_code: p.revenue_code ?? '',
        revenue_code_description:
          p.revenue_code_desc ?? p.revenue_code_description ?? p.description ?? '',
        transaction_date_time: this.formatPaymentBreakdownDateTime(
          p.transaction_date ?? p.transaction_date_time ?? ''
        ),
        receipt_number: p.receipt_number ?? p.receipt ?? '',
        quantity: p.quantity != null && String(p.quantity).trim() !== '' ? String(p.quantity) : '',
        price: formatPaymentBreakdownPrice(p),
        payment_amount: formatPaymentBreakdownAmount(p),
        payment_method: p.payment_method ?? '',
        comments: p.payment_comment ?? '',
        _raw: p
      };
    });
    this._lastPaymentBreakdownRows = paymentData;

    const paymentHeaders = [
      this.searchHeader('Application ID', 'application_id', { minWidth: pbw, allowFiltering: true }),
      this.searchHeader('Invoice Number', 'invoice_number', { minWidth: pbw, allowFiltering: true }),
      this.searchHeader('Revenue Code', 'revenue_code', { minWidth: 100, allowFiltering: true }),
      this.searchHeader('Revenue Code Description', 'revenue_code_description', {
        minWidth: 200,
        allowFiltering: true
      }),
      {
        dataField: 'transaction_date_time',
        caption: 'Transaction Date/Time',
        allowSearch: { isSearchEnable: true },
        allowFiltering: true,
        minWidth: 200
      },
      this.searchHeader('Receipt Number', 'receipt_number', { minWidth: pbw, allowFiltering: true }),
      this.searchHeader('Quantity', 'quantity', { minWidth: 96, allowFiltering: true }),
      this.searchHeader('Price', 'price', { minWidth: pbw, allowFiltering: true }),
      this.searchHeader('Payment Amount', 'payment_amount', { minWidth: pbw, allowFiltering: true }),
      this.searchHeader('Payment Method', 'payment_method', { minWidth: pbw, allowFiltering: true }),
      this.searchHeader('Comments', 'comments', { minWidth: 220, allowFiltering: true })
    ];

    const paymentHeaderKeys = paymentHeaders.map(h => String(h.dataField));
    this.paymentBreakdownColumnVisibility = mergeVisibleColumnDefaultsWithChooserLock(
      paymentHeaderKeys,
      this.paymentBreakdownColumnVisibility
    );
    this.paymentBreakdownAllColumns = withColumnChooserLock(headersToColumnConfigs(paymentHeaders));

    this.paymentBreakdownGridConfig = DataGridVO.create({
      customHeaders: paymentHeaders,
      data: paymentData,
      visibleColumns: { ...this.paymentBreakdownColumnVisibility },
      showActionsColumn: false,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customPageSize: 10,
      customNoDataText: 'No payment breakdown records',
      role: 'registration',
      activeTab: 'default',
      showQueueActions: false,
      _changeKey: Date.now()
    });
  }

  private firstAddressInfo(): EducatorDashboardAddressInfo | null {
    const list = this.educatorDashboardRow?.p_address_info;
    if (!Array.isArray(list) || list.length === 0) return null;
    if (list.length === 1) return list[0];
    return [...list].sort((a, b) => {
      const idA = Number(a?.profile_address_id ?? 0);
      const idB = Number(b?.profile_address_id ?? 0);
      return idB - idA;
    })[0];
  }

  private firstPersonInfo(): EducatorDashboardPersonInfo | null {
    const list = this.educatorDashboardRow?.p_person_info;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0];
  }

  private keyByValue(list: { key: string; value: string }[], display: string): string {
    if (!display) return '';
    const found = list.find(l => l.value === display || l.key === display);
    return found ? found.key : display;
  }

  private digitsOnly(v: string | null | undefined): string {
    if (v == null) return '';
    return String(v).replace(/\D/g, '');
  }

  /**
   * Formats phone for display: +1 (XXX) XXX-XXXX or (XXX) XXX-XXXX. Same as manage-profile update-contact maskPhoneNumber.
   */
  formatPhoneForDisplay(input: string | null | undefined): string {
    if (!input) return '';
    let value = String(input).replace(/[a-zA-Z]/g, '');
    if (value.startsWith('+1')) {
      if (value.length > 2 && value[2] !== ' ') {
        value = '+1 ' + value.slice(2);
      }
      const numberPart = value.startsWith('+1 ') ? value.slice(3) : value.slice(2);
      let digits = numberPart.replace(/\D/g, '');
      if (digits.length > 10) digits = digits.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      return value.startsWith('+1 ') ? '+1 ' + formatted : '+1' + formatted;
    }
    if (value.startsWith('(')) {
      let digits = value.replace(/\D/g, '');
      if (digits.length > 10) digits = digits.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      return formatted;
    }
    if (/^\d+$/.test(value)) {
      const digits = value.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      return formatted;
    }
    if (value === '+' || value === '+1' || value === '+1 ') return value;
    return '';
  }

  onAlternatePhoneInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    if (!el) return;
    const isDeleting = !!(event as InputEvent).inputType?.includes('delete');
    let val = el.value;
    if (!isDeleting) {
      if (!val.startsWith('+1')) val = '+1 ' + val.replace(/^\+?1?/, '');
      val = this.formatPhoneForDisplay(val);
    }
    this.contactForm.get('alternatePhone')?.setValue(val, { emitEvent: false });
    el.value = val;
    this.alternatePhoneExists = false;
    this.altPhoneDupDebounced$.next();
  }

  onAlternatePhoneKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (event.key.length === 1 && !/[\d+]/.test(event.key)) {
      event.preventDefault();
    }
  }

  onAlternatePhonePaste(event: ClipboardEvent): void {
    event.preventDefault();
    let pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted.startsWith('+1')) pasted = '+1 ' + pasted.replace(/^\+?1?/, '');
    const formatted = this.formatPhoneForDisplay(pasted);
    this.contactForm.get('alternatePhone')?.setValue(formatted, { emitEvent: false });
    const el = event.target as HTMLInputElement;
    if (el) el.value = formatted;
    this.alternatePhoneExists = false;
    this.altPhoneDupDebounced$.next();
  }

  private clearGeoFields(cityCtrl: string, countyCtrl: string, stateCtrl: string): void {
    this.addressForm.patchValue(
      {
        [cityCtrl]: '',
        [countyCtrl]: '',
        [stateCtrl]: ''
      },
      { emitEvent: false }
    );
  }

  /** US: city/county/state are readonly (filled via Smarty only). Non-US: editable. Never clears fields. */
  private applyUsGeoFieldsLock(
    cityCtrl: string,
    countyCtrl: string,
    stateCtrl: string,
    isUS: boolean
  ): void {
    const opts = { emitEvent: false };
    if (isUS) {
      this.addressForm.get(cityCtrl)?.disable(opts);
      this.addressForm.get(countyCtrl)?.disable(opts);
      this.addressForm.get(stateCtrl)?.disable(opts);
    } else {
      this.addressForm.get(cityCtrl)?.enable(opts);
      this.addressForm.get(countyCtrl)?.enable(opts);
      this.addressForm.get(stateCtrl)?.enable(opts);
    }
  }

  private applyPrimaryAddressCountryGate(): void {
    const hasCountry = !!this.addressForm.get('primaryCountry')?.value;
    const fieldKeys = [
      'primaryStreet',
      'primaryLine2',
      'primaryCity',
      'primaryCounty',
      'primaryState',
      'primaryZip'
    ] as const;
    if (!hasCountry) {
      fieldKeys.forEach(k => this.addressForm.get(k)?.disable({ emitEvent: false }));
      return;
    }
    (['primaryStreet', 'primaryLine2', 'primaryZip'] as const).forEach(k =>
      this.addressForm.get(k)?.enable({ emitEvent: false })
    );
    this.applyUsGeoFieldsLock('primaryCity', 'primaryCounty', 'primaryState', this.isPrimaryCountryUS);
  }

  private applyMailingAddressCountryGate(): void {
    if (this.addressForm.get('mailingSameAsAbove')?.value) return;
    const hasCountry = !!this.addressForm.get('mailingCountry')?.value;
    const fieldKeys = [
      'mailingStreet',
      'mailingLine2',
      'mailingCity',
      'mailingCounty',
      'mailingState',
      'mailingZip'
    ] as const;
    if (!hasCountry) {
      fieldKeys.forEach(k => this.addressForm.get(k)?.disable({ emitEvent: false }));
      return;
    }
    (['mailingStreet', 'mailingLine2', 'mailingZip'] as const).forEach(k =>
      this.addressForm.get(k)?.enable({ emitEvent: false })
    );
    this.applyUsGeoFieldsLock('mailingCity', 'mailingCounty', 'mailingState', this.isMailingCountryUS);
  }

  private applyAddressCountryGates(): void {
    this.applyPrimaryAddressCountryGate();
    this.applyMailingAddressCountryGate();
  }

  private applyInitialUsGeoLocks(): void {
    this.applyAddressCountryGates();
  }

  /** When US ZIP is incomplete (not 5 or 9 digits), clear geo fields; Smarty repopulates when ZIP is complete. */
  private handleEmptyZipClear(
    zipControlName: 'primaryZip' | 'mailingZip',
    cityCtrl: string,
    countyCtrl: string,
    stateCtrl: string,
    isUS: boolean
  ): void {
    if (!isUS) return;
    const digits = String(this.addressForm.get(zipControlName)?.value ?? '').replace(/\D/g, '');
    if (digits.length !== 5 && digits.length !== 9) {
      this.clearGeoFields(cityCtrl, countyCtrl, stateCtrl);
    }
  }

  onPrimaryZipInput(event: Event): void {
    this.zipCodeFormat(event, 'primaryZip', 'primaryCity', 'primaryState', 'primaryCounty');
    this.handleEmptyZipClear(
      'primaryZip',
      'primaryCity',
      'primaryCounty',
      'primaryState',
      this.isPrimaryCountryUS
    );
  }

  onMailingZipInput(event: Event): void {
    this.zipCodeFormat(event, 'mailingZip', 'mailingCity', 'mailingState', 'mailingCounty');
    this.handleEmptyZipClear(
      'mailingZip',
      'mailingCity',
      'mailingCounty',
      'mailingState',
      this.isMailingCountryUS
    );
  }

  private zipCodeFormat(
    event: Event,
    zipControlName: 'primaryZip' | 'mailingZip',
    _cityControlName: string,
    _stateControlName: string,
    _countyControlName: string
  ): void {
    const el = event.target as HTMLInputElement;
    if (!el) return;
    const isUS = zipControlName === 'primaryZip' ? this.isPrimaryCountryUS : this.isMailingCountryUS;
    if (!isUS) {
      let raw = (el.value || '').toString().replace(/[^A-Za-z0-9\s-]/g, '');
      if (raw.length > 10) raw = raw.substring(0, 10);
      this.addressForm.patchValue({ [zipControlName]: raw }, { emitEvent: false });
      el.value = raw;
      return;
    }
    let value = (el.value || '').toString().replace(/\D/g, '');
    if (value.length > 10) value = value.substring(0, 10);
    const zipCtrl = this.addressForm.get(zipControlName);
    if (value.length > 5 && value.length < 9) {
      zipCtrl?.setErrors({ invalidZipLength: true });
    } else {
      const err = zipCtrl?.errors;
      if (err && err['invalidZipLength']) {
        const next = { ...err };
        delete next['invalidZipLength'];
        zipCtrl?.setErrors(Object.keys(next).length ? next : null);
      }
    }
    let formatted = value;
    if (value.length > 5) {
      formatted = value.substring(0, 5) + '-' + value.substring(5);
    }
    if (value.length >= 5) {
      const allZerosPattern = /^0+$/;
      if (allZerosPattern.test(value)) {
        const inputType = (event as InputEvent).inputType || '';
        if (inputType !== 'deleteContentBackward' && inputType !== 'deleteContentForward') {
          const masked = value.length === 5 ? '00000' : '00000-0000';
          this.addressForm.patchValue({ [zipControlName]: masked }, { emitEvent: false });
          el.value = masked;
        }
        zipCtrl?.setErrors({ allZeros: true });
        return;
      }
    }
    this.addressForm.patchValue({ [zipControlName]: formatted }, { emitEvent: false });
    el.value = formatted;
    if (value.length === 5 || value.length === 9) {
      setTimeout(() => this.fetchSmartyForZip(zipControlName, formatted), 100);
    }
  }

  private fetchSmartyForZip(zipControlName: 'primaryZip' | 'mailingZip', zipValue: string): void {
    const isUS = zipControlName === 'primaryZip' ? this.isPrimaryCountryUS : this.isMailingCountryUS;
    if (!isUS) return;
    const zipCtrl = this.addressForm.get(zipControlName);
    if (zipCtrl?.invalid) return;
    const cleanZip = (zipValue || '').toString().replace(/\D/g, '');
    if (cleanZip.length !== 5 && cleanZip.length !== 9) return;
    this.registrationService
      .smartyValidation({ smartyInd: false, zipcode: zipValue })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: {
          status?: string;
          data?: Array<{ zipcodes?: Array<{ default_city?: string; state?: string; county_name?: string; county?: string }> }>;
        }) => {
          if (response?.status !== 'success' || !response?.data?.length) return;
          const zipData = response.data[0].zipcodes?.[0];
          if (!zipData) return;
          const patch: Record<string, string> = {};
          const city = zipData.default_city != null ? String(zipData.default_city).trim() : '';
          const state = zipData.state != null ? String(zipData.state).trim() : '';
          const countySource = zipData.county_name ?? zipData.county;
          const county = countySource != null ? String(countySource).trim() : '';
          if (zipControlName === 'primaryZip') {
            if (city) patch['primaryCity'] = city;
            if (state) patch['primaryState'] = state;
            if (county) patch['primaryCounty'] = county;
          } else {
            if (city) patch['mailingCity'] = city;
            if (state) patch['mailingState'] = state;
            if (county) patch['mailingCounty'] = county;
          }
          if (Object.keys(patch).length) {
            this.addressForm.patchValue(patch, { emitEvent: false });
          }
        }
      });
  }

  get isPrimaryCountryUS(): boolean {
    const key = this.addressForm.get('primaryCountry')?.value;
    if (!key) return false;
    if (key === 'US' || key === 'USA') return true;
    const found = this.countryList.find(c => c.key === key);
    return found ? (found.value || '').toLowerCase().includes('united states') : false;
  }

  get isMailingCountryUS(): boolean {
    const key = this.addressForm.get('mailingCountry')?.value;
    if (!key) return false;
    if (key === 'US' || key === 'USA') return true;
    const found = this.countryList.find(c => c.key === key);
    return found ? (found.value || '').toLowerCase().includes('united states') : false;
  }

  onStreetAddressInput(event: Event, field: 'primaryStreet' | 'mailingStreet'): void {
    const el = event.target as HTMLInputElement;
    if (!el) return;
    let value = (el.value || '').toString();
    value = value.replace(/[^A-Za-z0-9\s.,\-'#/]/g, '');
    value = value.substring(0, 60);
    value = this.preventBackToBackChars(value);
    this.addressForm.get(field)?.setValue(value, { emitEvent: false });
    el.value = value;
    this.checkForSpecialCharAtEndStreet(field);
  }

  onStreetAddressBlur(field: 'primaryStreet' | 'mailingStreet'): void {
    const ctrl = this.addressForm.get(field);
    const value = (ctrl?.value || '').toString().trim();
    ctrl?.setValue(value, { emitEvent: false });
    this.checkForSpecialCharAtEndStreet(field);
  }

  private preventBackToBackChars(value: string): string {
    value = value.replace(/^\s+/, '');
    value = value.replace(/\s{2,}/g, ' ');
    value = value.replace(/[.]{2,}/g, '.');
    value = value.replace(/[,]{2,}/g, ',');
    value = value.replace(/[-]{2,}/g, '-');
    value = value.replace(/[']{2,}/g, "'");
    value = value.replace(/[#]{2,}/g, '#');
    value = value.replace(/[/]{2,}/g, '/');
    value = value.replace(/([.,\-'#/])\1+/g, '$1');
    while (/[.,\-'#/][.,\-'#/]/.test(value)) {
      value = value.replace(/([.,\-'#/])([.,\-'#/])/, '$1');
    }
    return value;
  }

  private checkForSpecialCharAtEndStreet(field: 'primaryStreet' | 'mailingStreet'): void {
    const ctrl = this.addressForm.get(field);
    const value = (ctrl?.value || '').toString();
    if (!value) return;
    const specialCharsPattern = /[.,\-'#/]$/;
    if (specialCharsPattern.test(value)) {
      ctrl?.setErrors({ ...ctrl.errors, specialCharAtEnd: true });
    } else {
      const err = ctrl?.errors;
      if (err && err['specialCharAtEnd']) {
        const next = { ...err };
        delete next['specialCharAtEnd'];
        ctrl?.setErrors(Object.keys(next).length ? next : null);
      }
    }
  }

  private profileObject(): Record<string, unknown> | null {
    const row = this.educatorDashboardRow;
    if (!row?.p_profile) return null;
    const p = row.p_profile;
    if (Array.isArray(p) && p.length > 0) return p[0] as Record<string, unknown>;
    if (typeof p === 'object' && !Array.isArray(p)) return p as Record<string, unknown>;
    return null;
  }

  /** Legal first/last for duplicate API + modal; p_profile from get_educator_dashboard_data (firstname, lastname). */
  private getProfileNameForDuplicateCheck(): { firstName: string; lastName: string } {
    const profile = this.profileObject();
    if (!profile) {
      return { firstName: '', lastName: '' };
    }
    const str = (v: unknown): string => (v != null && String(v).trim() !== '' ? String(v).trim() : '');
    let firstName = str(profile['firstname']);
    let lastName = str(profile['lastname']);
    if (!firstName && !lastName) {
      const pn = str(profile['person_name']);
      if (pn) {
        const parts = pn.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
          firstName = parts[0];
          lastName = parts.slice(1).join(' ');
        } else if (parts.length === 1) {
          firstName = parts[0];
        }
      }
    }
    return { firstName, lastName };
  }

  private patchPersonalFormFromRow(): void {
    const person = this.firstPersonInfo();
    const genderKey = this.keyByValue(this.genderList, person?.gender || '');
    const raceKey = this.keyByValue(this.raceList, person?.race || '');
    const ethnicityKey = this.keyByValue(this.ethinicityList, person?.ethnicity || '');
    const lookupSelectModel = (raw: string): string | null =>
      raw == null || String(raw).trim() === '' ? null : String(raw).trim();
    const itinDigits = this.digitsOnly(person?.itin_number ?? undefined);
    const ssnDigits = this.digitsOnly(person?.ssn_number ?? undefined);

    const dobRaw = person?.date_of_birth ?? '';
    let dobDisplay = dobRaw;
    if (dobRaw && !dobRaw.includes('/')) {
      const m = String(dobRaw).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) dobDisplay = `${m[2]}/${m[3]}/${m[1]}`;
    }
    this.personalForm.patchValue({
      dateOfBirth: dobDisplay,
      gender: lookupSelectModel(genderKey),
      race: lookupSelectModel(raceKey),
      ethnicity: lookupSelectModel(ethnicityKey),
      itin: itinDigits,
      ssn: ssnDigits
    });
    this.originalSsn = ssnDigits;
    this.originalItin = itinDigits;
    this.personalFormSnapshot = this.personalForm.getRawValue();
  }

  /**
   * After mask directive updates the model (emitEvent false), schedule duplicate checks from input — not blur.
   */
  onPersonalSsnItinInput(field: 'ssn' | 'itin'): void {
    setTimeout(() => {
      if (field === 'ssn') {
        this.ssnDupDebounced$.next();
      } else {
        this.itinDupDebounced$.next();
      }
    }, 0);
  }

  /** Blur from SsnMaskDirective: UX only (no duplicate API). ITIN incomplete clears itinExists. */
  onSsnItinMaskDisplayBlur(event: { type: 'SSN' | 'ITIN'; value: string }): void {
    const digits = event.value.replace(/\D/g, '');
    if (event.type === 'ITIN') {
      this.personalForm.get('itin')?.markAsTouched();
      this.personalForm.get('itin')?.updateValueAndValidity({ emitEvent: false });
      if (!(digits.length === 9 && this.isValidItinNineDigits(digits))) {
        this.itinExists = false;
      }
    }
  }

  /** Async SSN/ITIN uniqueness check (blur removed; used from debounced input and submit guard). */
  private checkSSNOrITIN$(type: 'SSN' | 'ITIN'): Observable<void> {
    const ssnValue = this.personalForm.get('ssn')?.value?.replace(/\D/g, '') ?? '';
    const itinValue = this.personalForm.get('itin')?.value?.replace(/\D/g, '') ?? '';
    if (type === 'ITIN' && (itinValue.length !== 9 || !this.isValidItinNineDigits(itinValue))) {
      this.itinExists = false;
      return of(undefined);
    }
    if (type === 'SSN' && ssnValue.length === 9 && ssnValue === this.originalSsn) {
      this.ssnExists = false;
      return of(undefined);
    }
    if (type === 'ITIN' && itinValue.length === 9 && itinValue === this.originalItin) {
      this.itinExists = false;
      return of(undefined);
    }
    const request: { idType: string; ssn?: string; itin?: string } = { idType: type };
    if (type === 'SSN') {
      request.ssn = ssnValue;
    } else {
      request.itin = itinValue;
    }
    return this.manageProfileService.checkSSN(request).pipe(
      take(1),
      map((response: { data?: { exists?: boolean } }) => {
        const exists = response?.data?.exists === true;
        if (type === 'SSN') {
          this.ssnExists = exists;
        } else {
          this.itinExists = exists;
        }
        if (exists && type === 'SSN') {
          const msg =
            this.toastMessage['T0075'] ?? this.toastMessage['T0001'] ?? 'This SSN is already in use.';
          this.showFailureToastMsg(msg);
        }
        if (exists && type === 'ITIN') {
          const msg =
            this.toastMessage['T0074'] ?? this.toastMessage['T0016'] ?? 'This ITIN entered is already in file.';
          this.showFailureToastMsg(msg);
        }
        return undefined;
      }),
      catchError(() => {
        if (type === 'SSN') this.ssnExists = false;
        else this.itinExists = false;
        return of(undefined);
      })
    );
  }

  checkSSNOrITIN(type: 'SSN' | 'ITIN'): void {
    this.checkSSNOrITIN$(type).pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  /** Before identity save: ensure SSN/ITIN duplicate API ran for complete values (e.g. Update before debounce). */
  private ensureSsnItinDuplicateChecksForSubmit$(): Observable<void> {
    const ssnValue = this.personalForm.get('ssn')?.value?.replace(/\D/g, '') ?? '';
    const itinDigits = this.personalForm.get('itin')?.value?.replace(/\D/g, '') ?? '';
    const checks: Observable<void>[] = [];
    if (ssnValue.length === 9) {
      checks.push(this.checkSSNOrITIN$('SSN'));
    }
    if (itinDigits.length === 9 && this.isValidItinNineDigits(itinDigits)) {
      checks.push(this.checkSSNOrITIN$('ITIN'));
    }
    if (checks.length === 0) {
      return of(undefined);
    }
    return forkJoin(checks).pipe(map(() => undefined));
  }

  showFailureToastMsg(msg: string): void {
    this.alertService.error(msg, true);
    window.scrollTo(0, 0);
  }

  private maskEmail(email: string): string {
    if (!email) return '';
    const [user, domain] = email.split('@');
    if (!domain) return email;
    const visible = user.length <= 2 ? user : user.slice(0, 2) + '*'.repeat(user.length - 2);
    return `${visible}@${domain}`;
  }

  private maskPhone(phone: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return '*'.repeat(Math.min(digits.length, 10));
    return `${digits[0]}**-${digits[3]}**-${digits.slice(6)}`;
  }

  private maskSSN(value: string): string {
    const digits = value ? String(value).replace(/\D/g, '') : '';
    if (digits.length !== 9) return value || '***-**-****';
    return `***-**-${digits.slice(-4)}`;
  }

  private formatSSN(value: string): string {
    const digits = String(value || '').replace(/\D/g, '').substring(0, 9);
    if (digits.length >= 6) return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    if (digits.length >= 4) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return digits;
  }

  private openDuplicateAccountModal(): void {
    const person = this.firstPersonInfo();
    const profile = this.profileObject();
    const { firstName, lastName } = this.getProfileNameForDuplicateCheck();
    const maskedName = `${firstName} ${lastName}`.trim() || '—';
    const email = (person?.primary_email ?? profile?.['email'] ?? '') as string;
    const phone = (person?.primary_phone ?? profile?.['phoneNumber'] ?? '') as string;
    const ssnVal = this.personalForm.get('ssn')?.value ?? '';
    const modalRef = this.modalService.open(ModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true
    });
    modalRef.componentInstance.modalConfig = {
      heading: modalHeading.accountMayExist,
      modalContent: modalContent.accountMayExistLookupEducator(
        maskedName,
        this.maskEmail(email),
        this.maskPhone(phone),
        this.maskSSN(ssnVal)
      ),
      hasTemplate: true,
      buttons: [{ text: modalButtonTitles.returnToProfile, code: 'N' }],
      align: false,
      hideCloseIcon: false
    };
    modalRef.componentInstance.emitData.subscribe((result: { confirmed?: boolean }) => {
      modalRef.close();
    });
  }

  onPersonalReset(): void {
    this.personalForm.patchValue(this.personalFormSnapshot);
    this.personalForm.markAsPristine();
  }

  private patchContactFormFromRow(): void {
    const person = this.firstPersonInfo();
    const profile = this.profileObject();
    const rawPhone = String(person?.primary_phone ?? profile?.['primary_phone'] ?? '');
    const rawAltPhone = String(person?.alternate_phone ?? '');
    this.contactForm.patchValue({
      email: person?.primary_email ?? profile?.['email'] ?? '',
      recoveryEmail: person?.recovery_email ?? '',
      phone: fromAlternatePhoneDb(rawPhone),
      alternatePhone: this.formatAltPhoneForDisplay(rawAltPhone)
    });
    this.contactFormSnapshot = this.contactForm.getRawValue();
    this.emailExists = false;
    this.recoveryEmailExists = false;
    this.phoneExists = false;
    this.alternatePhoneExists = false;
  }

  /**
   * Formats a phone number string to +1 (XXX) XXX-XXXX for 11-digit or (XXX) XXX-XXXX for 10-digit format.
   * @param phoneNumber The phone number to format.
   */
  private formatAltPhoneForDisplay(phone: string): string {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
    }
    if (digits.length === 10) {
      return `+1 (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }
    return phone;
  }

  /** True when normalized current values differ from last load/save snapshot (Contact Update/Reset). */
  contactFormDiffersFromSnapshot(): boolean {
    const cur = this.contactForm.getRawValue() as Record<string, unknown>;
    const snap = this.contactFormSnapshot;
    const emailCur = (cur['email'] ?? '').toString().trim();
    const emailSnap = (snap['email'] ?? '').toString().trim();
    if (emailCur !== emailSnap) return true;
    const recoveryCur = (cur['recoveryEmail'] ?? '').toString().trim();
    const recoverySnap = (snap['recoveryEmail'] ?? '').toString().trim();
    if (recoveryCur !== recoverySnap) return true;
    const phoneCur = toAlternatePhoneDb((cur['phone'] ?? '') as string) ?? '';
    const phoneSnap = toAlternatePhoneDb((snap['phone'] ?? '') as string) ?? '';
    if (phoneCur !== phoneSnap) return true;
    const altCur = toAlternatePhoneDb((cur['alternatePhone'] ?? '') as string) ?? '';
    const altSnap = toAlternatePhoneDb((snap['alternatePhone'] ?? '') as string) ?? '';
    if (altCur !== altSnap) return true;
    return false;
  }

  /** Recovery non-empty and same as primary after trim (spec M0088). */
  contactRecoveryMatchesPrimary(): boolean {
    const primary = (this.contactForm.get('email')?.value ?? '').toString().trim();
    const recovery = (this.contactForm.get('recoveryEmail')?.value ?? '').toString().trim();
    if (!recovery) return false;
    return primary.toLowerCase() === recovery.toLowerCase();
  }

  /**
   * SP `is_mailing_address`: true = mailing same as primary. Null if absent (use heuristic).
   */
  private resolveSameAsPrimaryFromAddress(addr: EducatorDashboardAddressInfo | null): boolean | null {
    if (!addr) return null;
    const raw = addr.is_mailing_address;
    if (raw === undefined || raw === null) return null;
    if (typeof raw === 'boolean') return raw;
    if (typeof raw === 'number') return raw === 1;
    const s = String(raw).trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 't' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'f' || s === 'no') return false;
    return null;
  }

  /** Copy primary address fields into mailing (used when mailing same as above). */
  private syncMailingFromPrimary(): void {
    const p = this.addressForm.getRawValue();
    this.addressForm.patchValue(
      {
        mailingCountry: p.primaryCountry,
        mailingStreet: p.primaryStreet,
        mailingLine2: p.primaryLine2,
        mailingCity: p.primaryCity,
        mailingCounty: p.primaryCounty,
        mailingState: p.primaryState,
        mailingZip: p.primaryZip
      },
      { emitEvent: false }
    );
  }

  private clearPrimaryAddressFieldsExceptCountry(): void {
    this.addressForm.patchValue(
      {
        primaryStreet: '',
        primaryLine2: '',
        primaryCity: '',
        primaryCounty: '',
        primaryState: '',
        primaryZip: ''
      },
      { emitEvent: false }
    );
  }

  private clearMailingAddressFieldsExceptCountry(): void {
    this.addressForm.patchValue(
      {
        mailingStreet: '',
        mailingLine2: '',
        mailingCity: '',
        mailingCounty: '',
        mailingState: '',
        mailingZip: ''
      },
      { emitEvent: false }
    );
  }

  private patchAddressFormFromRow(): void {
    const addr = this.firstAddressInfo();
    const rawId = addr?.profile_address_id;
    this.currentProfileAddressId =
      rawId != null && Number.isFinite(Number(rawId)) && Number(rawId) > 0 ? Number(rawId) : null;
    const hasMailing =
      !!addr &&
      !!(addr.m_street_address || addr.m_city || addr.m_state || addr.m_postal_code);
    const fromFlag = this.resolveSameAsPrimaryFromAddress(addr);
    const mailingSameAsAbove = fromFlag !== null ? fromFlag : false;
    const primaryCountryKey = this.keyByValue(this.countryList, addr?.country || '') || (addr?.country ?? '');
    const mailingCountryKey = this.keyByValue(this.countryList, addr?.m_country || addr?.country || '') || (addr?.m_country || addr?.country || '');
    this.apiMailingSnapshot = {
      mailingCountry: String(mailingCountryKey ?? ''),
      mailingStreet: addr?.m_street_address ?? '',
      mailingLine2: addr?.m_address_line2 ?? '',
      mailingCity: addr?.m_city ?? '',
      mailingCounty: addr?.m_county ?? '',
      mailingState: addr?.m_state ?? '',
      mailingZip: addr?.m_postal_code ?? ''
    };
    this.addressForm.patchValue(
      {
        primaryCountry: primaryCountryKey ?? '',
        primaryStreet: addr?.street_address ?? '',
        primaryLine2: addr?.address_line2 ?? '',
        primaryCity: addr?.city ?? '',
        primaryCounty: addr?.county ?? addr?.m_county ?? '',
        primaryState: addr?.state ?? '',
        primaryZip: addr?.postal_code ?? '',
        mailingSameAsAbove,
        mailingCountry: mailingCountryKey ?? '',
        mailingStreet: addr?.m_street_address ?? '',
        mailingLine2: addr?.m_address_line2 ?? '',
        mailingCity: addr?.m_city ?? '',
        mailingCounty: addr?.m_county ?? '',
        mailingState: addr?.m_state ?? '',
        mailingZip: addr?.m_postal_code ?? ''
      },
      { emitEvent: false }
    );
    if (this.isPrimaryCountryUS) {
      const z = this.normalizeUsZipDisplay(this.addressForm.get('primaryZip')?.value);
      this.addressForm.patchValue({ primaryZip: z }, { emitEvent: false });
    }
    if (!mailingSameAsAbove && hasMailing && this.isMailingCountryUS) {
      const mz = this.normalizeUsZipDisplay(this.addressForm.get('mailingZip')?.value);
      this.addressForm.patchValue({ mailingZip: mz }, { emitEvent: false });
    }
    this.applyMailingSameAsAbove(mailingSameAsAbove);
    this.applyAddressCountryGates();
    this.addressFormSnapshot = this.addressForm.getRawValue();
  }

  /** Rule 6: city / state — 2–60 chars, letters, spaces, apostrophe, hyphen. */
  private cityStateRule6Validators() {
    return [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(60),
      Validators.pattern(/^[A-Za-z\s'-]{2,60}$/)
    ];
  }

  /** Rule 4 line 2: when non-empty, same allowed character set as street, 3–60 chars. */
  private optionalAddressLine2Validator(control: AbstractControl): ValidationErrors | null {
    const v = String(control.value ?? '').trim();
    if (v === '') return null;
    if (v.length < 3 || v.length > 60) return { line2Length: true };
    if (!/^[A-Za-z0-9][A-Za-z0-9\s.,\-'/#]*$/.test(v)) return { line2Pattern: true };
    return null;
  }

  private updateAddressValidatorsForCountry(): void {
    const primaryCounty = this.addressForm.get('primaryCounty');
    if (this.isPrimaryCountryUS) primaryCounty?.setValidators(Validators.required);
    else primaryCounty?.clearValidators();
    primaryCounty?.updateValueAndValidity({ emitEvent: false });

    const primaryZip = this.addressForm.get('primaryZip');
    if (this.isPrimaryCountryUS) {
      primaryZip?.setValidators([
        Validators.required,
        Validators.pattern(/^\d{5}(-\d{4})?$/),
        Validators.minLength(5),
        Validators.maxLength(10)
      ]);
    } else {
      primaryZip?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z0-9\s-]{3,10}$/)]);
    }
    primaryZip?.updateValueAndValidity({ emitEvent: false });

    const same = !!this.addressForm.get('mailingSameAsAbove')?.value;
    if (same) return;

    const mailingCounty = this.addressForm.get('mailingCounty');
    if (this.isMailingCountryUS) mailingCounty?.setValidators(Validators.required);
    else mailingCounty?.clearValidators();
    mailingCounty?.updateValueAndValidity({ emitEvent: false });

    const mailingZip = this.addressForm.get('mailingZip');
    if (this.isMailingCountryUS) {
      mailingZip?.setValidators([
        Validators.required,
        Validators.pattern(/^\d{5}(-\d{4})?$/),
        Validators.minLength(5),
        Validators.maxLength(10)
      ]);
    } else {
      mailingZip?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z0-9\s-]{3,10}$/)]);
    }
    mailingZip?.updateValueAndValidity({ emitEvent: false });
  }

  /** Map dashboard suffix (key or display) to a `suffixList` key for the name-change select, or `null` if none. */
  private suffixForNameChangeForm(raw: string): string | null {
    const t = String(raw ?? '').trim();
    if (!t) {
      return null;
    }
    const byKey = this.suffixList.find(
      x => String(x.key ?? '').trim().toLowerCase() === t.toLowerCase()
    );
    if (byKey) {
      return String(byKey.key).trim();
    }
    const byVal = this.suffixList.find(
      x => String(x.value ?? '').trim().toLowerCase() === t.toLowerCase()
    );
    if (byVal) {
      return String(byVal.key).trim();
    }
    const r = this.resolveSuffixKeyFromDisplay(t);
    return r ? r : null;
  }

  /**
   * Current legal first/middle/last + suffix select value from `p_profile` (and `person_name` fallback),
   * same source as the Name Change prefill and Add Prior Name default.
   */
  private getCurrentLegalNameFieldsForForms(): {
    first: string;
    middle: string;
    last: string;
    suffix: string | null;
  } {
    const prof = this.profileObject() as Record<string, unknown> | null;
    const t = (k: string): string => {
      if (!prof) {
        return '';
      }
      const v = prof[k];
      return v != null && String(v).trim() !== '' ? String(v).trim() : '';
    };
    let first = t('firstname');
    let middle = t('middlename');
    let last = t('lastname');
    const suffixRaw = t('cd_suffix');
    if (!first && !last) {
      const fromPersonName = this.getProfileNameForDuplicateCheck();
      if (fromPersonName.firstName) {
        first = fromPersonName.firstName;
      }
      if (fromPersonName.lastName) {
        last = fromPersonName.lastName;
      }
    }
    return {
      first,
      middle,
      last,
      suffix: this.suffixForNameChangeForm(suffixRaw)
    };
  }

  private patchNameChangeFormFromRow(): void {
    const n = this.getCurrentLegalNameFieldsForForms();
    this.nameChangeForm.patchValue({
      newLegalFirstName: n.first,
      newLegalMiddleName: n.middle,
      newLegalLastName: n.last,
      newLegalSuffix: n.suffix
    });
    this.nameChangeFormSnapshot = this.nameChangeForm.getRawValue();
    this.nameChangeForm.markAsPristine();
  }

  private applyMailingSameAsAbove(same: boolean): void {
    const mailingKeys = [
      'mailingCountry',
      'mailingStreet',
      'mailingLine2',
      'mailingCity',
      'mailingCounty',
      'mailingState',
      'mailingZip'
    ] as const;
    if (same) {
      mailingKeys.forEach(k => {
        const c = this.addressForm.get(k);
        c?.clearValidators();
        c?.updateValueAndValidity({ emitEvent: false });
      });
      this.syncMailingFromPrimary();
      mailingKeys.forEach(k => this.addressForm.get(k)?.disable({ emitEvent: false }));
    } else {
      mailingKeys.forEach(k => this.addressForm.get(k)?.enable({ emitEvent: false }));
      this.addressForm.get('mailingCountry')?.setValidators(Validators.required);
      this.addressForm.get('mailingStreet')?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z0-9][A-Za-z0-9\s.,\-'/#]*$/), Validators.minLength(3), Validators.maxLength(60)]);
      this.addressForm.get('mailingLine2')?.setValidators([this.optionalAddressLine2Validator.bind(this)]);
      this.addressForm.get('mailingCity')?.setValidators(this.cityStateRule6Validators());
      this.addressForm.get('mailingState')?.setValidators(this.cityStateRule6Validators());
      const snap = this.apiMailingSnapshot;
      this.addressForm.patchValue(
        {
          mailingCountry: snap.mailingCountry,
          mailingStreet: snap.mailingStreet,
          mailingLine2: snap.mailingLine2,
          mailingCity: snap.mailingCity,
          mailingCounty: snap.mailingCounty,
          mailingState: snap.mailingState,
          mailingZip: snap.mailingZip
        },
        { emitEvent: false }
      );
      if (this.isMailingCountryUS) {
        const mz = this.normalizeUsZipDisplay(this.addressForm.get('mailingZip')?.value);
        this.addressForm.patchValue({ mailingZip: mz }, { emitEvent: false });
      }
      mailingKeys.forEach(k => this.addressForm.get(k)?.updateValueAndValidity({ emitEvent: false }));
    }
    this.updateAddressValidatorsForCountry();
    this.applyAddressCountryGates();
  }

  /** US ZIP mask display: `XXXXX` or `XXXXX-XXXX` (up to 9 digits). */
  private normalizeUsZipDisplay(raw: unknown): string {
    const digits = String(raw ?? '')
      .replace(/\D/g, '')
      .substring(0, 9);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }

  onPrimaryZipBlur(): void {
    if (!this.isPrimaryCountryUS) return;
    const ctrl = this.addressForm.get('primaryZip');
    this.applyUsGeoFieldsLock('primaryCity', 'primaryCounty', 'primaryState', this.isPrimaryCountryUS);
    if (!ctrl || ctrl.disabled) return;
    const cur = String(ctrl.value ?? '');
    const normalized = this.normalizeUsZipDisplay(cur);
    if (normalized !== cur) {
      ctrl.setValue(normalized, { emitEvent: false });
    }
    this.clearUsZipInvalidLengthIfComplete('primaryZip', normalized);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onMailingZipBlur(): void {
    if (!this.isMailingCountryUS) return;
    const ctrl = this.addressForm.get('mailingZip');
    this.applyUsGeoFieldsLock('mailingCity', 'mailingCounty', 'mailingState', this.isMailingCountryUS);
    if (!ctrl || ctrl.disabled) return;
    const cur = String(ctrl.value ?? '');
    const normalized = this.normalizeUsZipDisplay(cur);
    if (normalized !== cur) {
      ctrl.setValue(normalized, { emitEvent: false });
    }
    this.clearUsZipInvalidLengthIfComplete('mailingZip', normalized);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private clearUsZipInvalidLengthIfComplete(zipControlName: 'primaryZip' | 'mailingZip', displayValue: string): void {
    const zipCtrl = this.addressForm.get(zipControlName);
    if (!zipCtrl?.errors?.['invalidZipLength']) return;
    const digits = displayValue.replace(/\D/g, '');
    if (digits.length !== 5 && digits.length !== 9) return;
    const next = { ...zipCtrl.errors } as Record<string, unknown>;
    delete next['invalidZipLength'];
    zipCtrl.setErrors(Object.keys(next).length ? (next as ValidationErrors) : null);
  }

  onContactReset(): void {
    this.contactForm.patchValue(this.contactFormSnapshot);
    this.contactForm.markAsPristine();
  }

  /** Returns observable that completes after server duplicate check for primary email (if needed). */
  private verifyContactEmailDuplicateIfChanged$(): Observable<void> {
    const email = (this.contactForm.get('email')?.value ?? '').toString().trim();
    const emailCtrl = this.contactForm.get('email');
    if (!email || emailCtrl?.invalid) {
      this.emailExists = false;
      return of(undefined);
    }
    const original = (this.contactFormSnapshot['email'] ?? '').toString().trim();
    if (email === original) {
      this.emailExists = false;
      return of(undefined);
    }
    return this.manageProfileService.checkEmailExists(email).pipe(
      take(1),
      map((response: { data?: { exists?: boolean } }) => {
        this.emailExists = response?.data?.exists === true;
        if (this.emailExists) {
          this.showFailureToastMsg(this.toastMessage['T0032'] || 'The email entered is already on file.');
        }
        return undefined;
      }),
      catchError(() => {
        this.emailExists = false;
        return of(undefined);
      })
    );
  }

  private verifyContactRecoveryEmailDuplicateIfChanged$(): Observable<void> {
    const email = (this.contactForm.get('recoveryEmail')?.value ?? '').toString().trim();
    const emailCtrl = this.contactForm.get('recoveryEmail');
    if (!email) {
      this.recoveryEmailExists = false;
      return of(undefined);
    }
    if (emailCtrl?.invalid) {
      this.recoveryEmailExists = false;
      return of(undefined);
    }
    const primary = (this.contactForm.get('email')?.value ?? '').toString().trim().toLowerCase();
    if (email.toLowerCase() === primary) {
      this.recoveryEmailExists = false;
      return of(undefined);
    }
    const original = (this.contactFormSnapshot['recoveryEmail'] ?? '').toString().trim();
    if (email === original) {
      this.recoveryEmailExists = false;
      return of(undefined);
    }
    return this.manageProfileService.checkEmailExists(email).pipe(
      take(1),
      map((response: { data?: { exists?: boolean } }) => {
        this.recoveryEmailExists = response?.data?.exists === true;
        if (this.recoveryEmailExists) {
          this.showFailureToastMsg(this.toastMessage['T0003'] || 'The recovery email entered is already on file.');
        }
        return undefined;
      }),
      catchError(() => {
        this.recoveryEmailExists = false;
        return of(undefined);
      })
    );
  }

  private verifyPrimaryPhoneDuplicateIfChanged$(): Observable<void> {
    const raw = (this.contactForm.get('phone')?.value ?? '').toString();
    const digits = this.digitsOnly(raw);
    const phoneCtrl = this.contactForm.get('phone');
    if (!digits || phoneCtrl?.invalid) {
      this.phoneExists = false;
      return of(undefined);
    }
    if (digits.length !== 10 && digits.length !== 11) {
      this.phoneExists = false;
      return of(undefined);
    }
    const originalDigits = this.digitsOnly((this.contactFormSnapshot['phone'] ?? '').toString());
    if (digits === originalDigits) {
      this.phoneExists = false;
      return of(undefined);
    }
    return this.manageProfileService.checkPhoneExists(digits).pipe(
      take(1),
      map((response: { data?: { exists?: boolean } }) => {
        this.phoneExists = response?.data?.exists === true;
        if (this.phoneExists) {
          this.showFailureToastMsg(this.toastMessage['T0004'] || 'The phone number entered is already on file.');
        }
        return undefined;
      }),
      catchError(() => {
        this.phoneExists = false;
        return of(undefined);
      })
    );
  }

  private verifyAlternatePhoneDuplicateIfChanged$(): Observable<void> {
    const raw = (this.contactForm.get('alternatePhone')?.value ?? '').toString();
    const digits = toAlternatePhoneDb(raw);
    const phoneCtrl = this.contactForm.get('alternatePhone');
    if (!digits) {
      this.alternatePhoneExists = false;
      return of(undefined);
    }
    if (phoneCtrl?.invalid) {
      this.alternatePhoneExists = false;
      return of(undefined);
    }
    const originalDigits = toAlternatePhoneDb((this.contactFormSnapshot['alternatePhone'] ?? '').toString());
    if (digits === originalDigits) {
      this.alternatePhoneExists = false;
      return of(undefined);
    }
    return this.manageProfileService.checkPhoneExists(digits).pipe(
      take(1),
      map((response: { data?: { exists?: boolean } }) => {
        this.alternatePhoneExists = response?.data?.exists === true;
        if (this.alternatePhoneExists) {
          this.showFailureToastMsg(this.toastMessage['T0004'] || 'The phone number entered is already on file.');
        }
        return undefined;
      }),
      catchError(() => {
        this.alternatePhoneExists = false;
        return of(undefined);
      })
    );
  }

  private runContactEmailDuplicateCheck(): void {
    this.verifyContactEmailDuplicateIfChanged$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  private runContactRecoveryEmailDuplicateCheck(): void {
    this.verifyContactRecoveryEmailDuplicateIfChanged$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  private runPrimaryPhoneDuplicateCheck(): void {
    this.verifyPrimaryPhoneDuplicateIfChanged$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  private runAlternatePhoneDuplicateCheck(): void {
    this.verifyAlternatePhoneDuplicateIfChanged$().pipe(take(1), takeUntil(this.destroy$)).subscribe();
  }

  onContactUpdate(): void {
    if (this.contactRecoveryMatchesPrimary()) {
      this.contactForm.markAllAsTouched();
      this.alertService.error(
        this.errorMessage['M0088'] ||
          'Recovery email must be different from the primary email address.'
      );
      return;
    }
    this.contactForm.markAllAsTouched();
    ['email', 'recoveryEmail', 'phone', 'alternatePhone'].forEach((key) => {
      this.contactForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
    this.contactForm.updateValueAndValidity({ emitEvent: false });

    if (this.contactForm.invalid) {
      const msg = this.toastMessage['T0002'] || this.errorMessage['M0038'] || 'Please fix the errors before updating.';
      this.alertService.error(msg);
      return;
    }

    forkJoin([
      this.verifyContactEmailDuplicateIfChanged$(),
      this.verifyContactRecoveryEmailDuplicateIfChanged$(),
      this.verifyPrimaryPhoneDuplicateIfChanged$(),
      this.verifyAlternatePhoneDuplicateIfChanged$()
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.emailExists || this.recoveryEmailExists || this.phoneExists || this.alternatePhoneExists) {
          const msg =
            this.toastMessage['T0002'] || this.errorMessage['M0038'] || 'Please fix the errors before updating.';
          this.alertService.error(msg);
          return;
        }
        const personId = this.personId;
        if (!personId) {
          this.alertService.error('Person ID is required.');
          return;
        }
        const contact = this.contactForm.getRawValue();
        const phoneDb = toAlternatePhoneDb((contact.phone ?? '').toString());
        const altPhoneDb = toAlternatePhoneDb((contact.alternatePhone ?? '').toString());
        const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        const updatedBy = sessionUser?.id ? Number(sessionUser.id) : null;
        const body = {
          personId: Number(personId),
          primaryEmail: contact.email || undefined,
          recoveryEmail: contact.recoveryEmail || undefined,
          primaryPhone: phoneDb,
          alternatePhone: altPhoneDb,
          updatedBy: updatedBy ?? undefined
        };
        const url = this.buildSearchUrl('/search/educator-update-identity');
        this.http
          .post<{ status?: string; data?: { success?: boolean; message?: string }; message?: string }>(url, body, {
            headers: this.buildJsonHeaders()
          })
          .pipe(take(1), takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              const success = res?.data?.success === true;
              if (success) {
                this.alertService.success(this.toastMessage['T0005'] || 'Contact updated successfully.');
                const emailChanged =
                  (contact.email || '').trim().toLowerCase() !==
                  ((this.contactFormSnapshot['email'] as string) || '').trim().toLowerCase();
                if (emailChanged) {
                  this.alertService.info('The educator has been logged out because their primary email was changed.');
                }
                this.contactFormSnapshot = this.contactForm.getRawValue();
                this.contactForm.markAsPristine();
                this.requestDashboardRefresh.emit();
              } else {
                this.alertService.error(res?.data?.message || res?.message || this.toastMessage['T0002'] || 'Update failed.');
              }
            },
            error: (err) => {
              const msg = err?.error?.message || err?.message || this.toastMessage['T0002'] || 'Failed to update contact.';
              this.alertService.error(msg);
            }
          });
      });
  }

  onAddressReset(): void {
    const same = !!this.addressFormSnapshot['mailingSameAsAbove'];
    const mailingKeys = [
      'mailingCountry',
      'mailingStreet',
      'mailingLine2',
      'mailingCity',
      'mailingCounty',
      'mailingState',
      'mailingZip'
    ] as const;
    mailingKeys.forEach(k => this.addressForm.get(k)?.enable({ emitEvent: false }));
    this.addressForm.patchValue(this.addressFormSnapshot, { emitEvent: false });
    this.applyMailingSameAsAbove(same);
    this.addressForm.markAsPristine();
  }

  onAddressUpdate(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.alertService.error(this.toastMessage['T0017'] || 'Please fill in all required fields.');
      return;
    }
    const personId = this.personId;
    if (!personId) {
      this.alertService.warn('Person ID is required.');
      return;
    }
    const raw = this.addressForm.getRawValue() as Record<string, string | boolean>;
    const sameAsAbove = !!raw['mailingSameAsAbove'];
    const body: Record<string, unknown> = {
      streetAddress: raw['primaryStreet'] || undefined,
      addressLine2: raw['primaryLine2'] || undefined,
      city: raw['primaryCity'] || undefined,
      county: raw['primaryCounty'] || undefined,
      state: raw['primaryState'] || undefined,
      postalCode: raw['primaryZip'] || undefined,
      country: raw['primaryCountry'] || undefined
    };
    if (sameAsAbove) {
      body.mStreetAddress = raw['primaryStreet'] || undefined;
      body.mAddressLine2 = raw['primaryLine2'] || undefined;
      body.mCity = raw['primaryCity'] || undefined;
      body.mCounty = raw['primaryCounty'] || undefined;
      body.mState = raw['primaryState'] || undefined;
      body.mPostalCode = raw['primaryZip'] || undefined;
      body.mCountry = raw['primaryCountry'] || undefined;
    } else {
      body.mStreetAddress = raw['mailingStreet'] || undefined;
      body.mAddressLine2 = raw['mailingLine2'] || undefined;
      body.mCity = raw['mailingCity'] || undefined;
      body.mCounty = raw['mailingCounty'] || undefined;
      body.mState = raw['mailingState'] || undefined;
      body.mPostalCode = raw['mailingZip'] || undefined;
      body.mCountry = raw['mailingCountry'] || undefined;
    }
    body.personId = Number(personId);
    body.isMailingAddress = sameAsAbove;
    const url = this.buildSearchUrl('/search/educator-update-address');
    this.http
      .post<{
        status?: string;
        success?: boolean;
        profileAddressId?: number;
        data?: { success?: boolean; message?: string; profileAddressId?: number };
        message?: string;
      }>(url, body, { headers: this.buildJsonHeaders() })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const d = (res as any)?.data ?? res;
          const success =
            res?.status === 'success' || d?.success === true || (res as any)?.success === true;
          if (success) {
            const newId = d?.profileAddressId ?? (res as any)?.profileAddressId;
            if (newId != null && Number.isFinite(Number(newId)) && Number(newId) > 0) {
              this.currentProfileAddressId = Number(newId);
            }
            this.alertService.success(this.toastMessage['T0020'] || 'Address updated successfully.');
            this.addressFormSnapshot = this.addressForm.getRawValue();
            this.addressForm.markAsPristine();
            this.requestDashboardRefresh.emit();
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Update failed.');
          }
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Failed to update address.';
          this.alertService.error(msg);
        }
      });
  }

  onPersonalUpdate(): void {
    this.personalForm.markAllAsTouched();
    Object.keys(this.personalForm.controls).forEach((key) => {
      this.personalForm.get(key)?.updateValueAndValidity({ emitEvent: false });
    });
    this.personalForm.updateValueAndValidity({ emitEvent: false });

    if (this.personalForm.invalid) {
      this.alertService.error(
        this.toastMessage['T0017'] ||
          this.toastMessage['T0002'] ||
          this.errorMessage['M0038'] ||
          'Please fill in all required fields.'
      );
      return;
    }

    this.ensureSsnItinDuplicateChecksForSubmit$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.ssnExists || this.itinExists) {
          return;
        }
        const ssnControl = this.personalForm.get('ssn');
        const currentSsnDigits = (ssnControl?.value ?? '').replace(/\D/g, '');
        const ssnChanged = currentSsnDigits.length === 9 && currentSsnDigits !== this.originalSsn;
        const { firstName, lastName } = this.getProfileNameForDuplicateCheck();
        const dobRaw = this.personalForm.get('dateOfBirth')?.value ?? '';
        let birthDate = '';
        if (dobRaw) {
          if (dobRaw.includes('/')) {
            const [m, d, y] = dobRaw.split('/');
            birthDate = `${y}-${m}-${d}`;
          } else {
            birthDate = dobRaw;
          }
        }
        if (ssnChanged && firstName && lastName && birthDate) {
          const duplicatePayload = {
            first_name: firstName,
            last_name: lastName,
            birth_date: birthDate,
            ssn_number: currentSsnDigits
          };
          this.manageProfileService.checkPossibleDuplicate(duplicatePayload).pipe(take(1), takeUntil(this.destroy$)).subscribe({
            next: (duplicateResponse: { data?: { isDuplicate?: boolean } }) => {
              if (duplicateResponse?.data?.isDuplicate) {
                this.openDuplicateAccountModal();
                return;
              }
              this.performPersonalUpdate();
            },
            error: () => {
              this.showFailureToastMsg('Error checking for duplicate SSN.');
            }
          });
        } else {
          this.performPersonalUpdate();
        }
      });
  }

  private performPersonalUpdate(): void {
    const personId = this.personId;
    if (!personId) {
      this.alertService.error('Person ID is required.');
      return;
    }
    if (this.contactRecoveryMatchesPrimary()) {
      this.contactForm.markAllAsTouched();
      this.alertService.error(
        this.errorMessage['M0088'] ||
          'Recovery email must be different from the primary email address.'
      );
      return;
    }
    const raw = this.personalForm.getRawValue();
    const dobRaw = raw.dateOfBirth ?? '';
    let dateOfBirth = '';
    if (dobRaw) {
      if (dobRaw.includes('/')) {
        const [m, d, y] = dobRaw.split('/');
        dateOfBirth = `${y}-${m}-${d}`;
      } else {
        dateOfBirth = dobRaw;
      }
    }
    const contact = this.contactForm.getRawValue();
    const updatedBy = this.getCurrentUserId();
    const body = {
      personId: Number(personId),
      dateOfBirth: dateOfBirth || undefined,
      gender: raw.gender != null && String(raw.gender).trim() !== '' ? String(raw.gender) : undefined,
      race: raw.race != null && String(raw.race).trim() !== '' ? String(raw.race) : undefined,
      ethnicity:
        raw.ethnicity != null && String(raw.ethnicity).trim() !== ''
          ? String(raw.ethnicity)
          : undefined,
      itin: (raw.itin ?? '').replace(/\D/g, '') || undefined,
      ssn: (raw.ssn ?? '').replace(/\D/g, '') || undefined,
      primaryEmail: contact.email || undefined,
      recoveryEmail: contact.recoveryEmail || undefined,
      primaryPhone: toAlternatePhoneDb((contact.phone ?? '').toString()),
      alternatePhone: toAlternatePhoneDb((contact.alternatePhone ?? '').toString()),
      updatedBy: updatedBy ?? undefined
    };
    const url = this.buildSearchUrl('/search/educator-update-identity');
    this.http
      .post<{ status?: string; data?: { success?: boolean; message?: string }; message?: string }>(url, body, { headers: this.buildJsonHeaders() })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const success = res?.status === 'success' || res?.data?.success;
          if (success) {
            this.alertService.success(
              this.toastMessage['T0020'] || 'Identity and contact updated successfully.'
            );
            this.personalFormSnapshot = this.personalForm.getRawValue();
            this.contactFormSnapshot = this.contactForm.getRawValue();
            this.personalForm.markAsPristine();
            this.contactForm.markAsPristine();
            this.requestDashboardRefresh.emit();
          } else {
            this.alertService.error(res?.data?.message || res?.message || 'Update failed.');
          }
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Failed to update identity.';
          this.alertService.error(msg);
        }
      });
  }

getSelectLabel(
  key: any,
  options: { key: any; value: string }[]
): string {
  if (!key || !options?.length) return '';

  const found = options.find(o => String(o.key) === String(key));
  if (!found?.value) return '';

  const trimmedLabel = found.value.trim();
  const charCount = trimmedLabel.length;

  return charCount > 27 ? found.value : '';
}

 

getMultiSelectLabel(
  keys: any[],
  options: { key: any; label: string }[]
): string {
  if (!keys?.length || !options?.length) return '';

  const combined = keys
    .map(k => {
      const found = options.find(o => String(o.key) === String(k));
      return found?.label?.trim() || '';
    })
    .filter(Boolean)
    .join(', ');

  const trimmedCombined = combined.trim();
  const charCount = trimmedCombined.length;

  return charCount > 21 ? combined : '';
}

  ngOnDestroy(): void {
    if (this.editPriorNameModalRef) {
      try {
        this.editPriorNameModalRef.dismiss();
      } catch {
        /* modal may already be closed */
      }
      this.editPriorNameModalRef = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
